import React, { useCallback, useEffect, useRef, useState } from 'react';
import { setRegionOverride, addCompanyRegions, clearCompanyRegions, todayISO, zonedParts, money, moneyTight, currencyCode, fmtDate, fmtDateTime, localeDate } from '../../utils/region';
import { setItemWithRoom } from '../../services/draftMirror';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { api, setMerchantClient, portalFetchHeaders, setPortalSystem, storePortalPin, restorePortalPin,
         resolveForeignPortalSystem, portalLoginAt, portalSystemBase, PORTAL_SYSTEMS, apiFetch, isApiDownResponse } from '../../utils/api';
import PortalClients from '../Admin/PortalClients';
import { getApiHealth } from '../../services/apiHealth';   // is the "updating" card up? (docFetch)
import { brandOr } from '../../utils/brand';   // the firm's side of the calendar: the profile's / public brand (set on load, re-translates when it lands)
import { usePublicBrand, clientPortalTitle } from '../../utils/publicBrand';   // sign-in card title before any login (GET /api/public/brand)
import RichTextEditor from '../RichTextEditor';
import { offeredUiLanguages, setCountryFeaturesOverride } from '../../utils/countryFeatures';
import { setCurrencyWords, isThaiCountry, currencyArgs } from '../../utils/currencyWord';   // {{cur}} / $t(cur_word): the merchant's own currency (owner Q18)
import { thaiModeOf, docPlainOf, plainLineTotals, plainRateOk, plainCurrencies, merchantToday, COMMON_CURRENCIES } from './plainMode';   // a merchant outside Thailand (server merchantMode.ts)
// The signed-in merchant company (set when the profile loads): amounts on screens that have no document of their own
// (the spreadsheet import preview, the products list) print in its region.
let PORTAL_MERCHANT_ID = '';
const mco = (): string | null => PORTAL_MERCHANT_ID || null;

// Client invoicing portal (/portal/*) — role='client' logins only. Self-manages auth (login →
// forced password change → numbering disclaimer → app). English first, Thai second, THB only.
// Identity fields (company name / tax id / VAT status) are read-only: they come from the firm's
// verified records. All tenant scoping is enforced server-side (requireMerchant).

const ACCENT = '#0E7C66';
// Which of the login's companies this browser is working in. Beside the token and cleared with it.
const MERCHANT_CLIENT_KEY = 'mf_merchantClient';
const card: React.CSSProperties = { background: '#fff', border: '1px solid #e3e7ea', borderRadius: 12, padding: 20 };

// Which establishment an all-zero branch number means. Mirrors branchLabel() in
// server/src/services/thaiTaxDocs.ts — the firm's convention throughout the system.
//
// The words go in the HINT and the digits stay in the field. Putting both in the field gave
// "00002 — Branch 00002", which repeats itself and then truncates; the client is checking the
// digits against their own registration, and only needs the words to know which kind it is.
const branchNote = (branchNo: any, t: TFn): string =>
  /^0*$/.test(String(branchNo ?? '').trim()) ? t('p_prof_branch_head') : t('p_prof_branch_n');
const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 14, border: '1px solid #d3dbe2', borderRadius: 8, boxSizing: 'border-box', outline: 'none' };
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#5b6b79', display: 'block', margin: '10px 0 4px' };
const btn: React.CSSProperties = { padding: '9px 18px', borderRadius: 8, border: 'none', background: ACCENT, color: '#fff', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { ...btn, background: '#eef2f5', color: '#333' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#5b6b79', borderBottom: '2px solid #e3e7ea', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: 13.5, borderBottom: '1px solid #eef1f4' };
// Customer code as shown everywhere in the UI. Mirrors customerCode() on the server.
const custCode = (n: any): string => (Number(n) > 0 ? `C${String(Number(n)).padStart(3, '0')}` : '—');
/* ── The portal's type stack ──────────────────────────────────────────
   The portal shell was set in `Arial, sans-serif`, which has no Thai and no CJK. Two of the three
   languages this product supports were therefore being rendered in whatever the operating system
   happened to substitute, at a different x-height and weight from the Latin beside it. That is
   most of what "unrefined" means on the Thai interface.

   Latin and Thai are the app's default face, Google Sans (report 806823ce), whose sheet has a Thai
   subset, with Helvetica and Arial behind it, neither of which has Thai or CJK. A large share of these
   clients are in China, where fonts.googleapis.com is unreachable, so there the system faces behind
   Google Sans draw the Latin and the Thai names behind those draw the Thai. Those names and the CJK
   ones stay per script rather than a webfont, deliberately: the firm already learned that lesson on the
   marketing site (which self-hosts). Naming the good faces that are already on the user's machine
   fixes the actual defect — unmatched fallback — at zero download, on every network. Self-hosting
   a matched trio is the better answer and a separate piece of work.

   Sarabun is listed first for Thai on purpose, for a machine that cannot load the sheet: the Revenue
   Department's own forms — ภ.พ.30, ภ.ง.ด.1/50/51 — are set in TH Sarabun New, so there a client's
   invoice list reads in the same face as the filings SLF submits on their behalf. */
const PORTAL_FONT = [
  '"Google Sans"', 'Helvetica', 'Arial',
  '"Sarabun"', '"Noto Sans Thai"', '"Leelawadee UI"', '"Thonburi"',
  '"PingFang SC"', '"Hiragino Sans GB"', '"Microsoft YaHei"', '"Noto Sans SC"',
  'sans-serif',
].join(', ');

/* Money arithmetic in the browser. `ir2` rounds the way the server's own ir2 does, so a balance
   shown here matches the one the server computes to the satang. */
const num2 = (n: any): number => Number(n) || 0;
const ir2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;
// What the customer ACTUALLY transferred, which is what a receipt is a receipt for. A Thai company
// buyer withholds tax at source and remits it to the Revenue Department on the seller's behalf, so
// the cash that lands is the invoice total MINUS that withholding; a buyer who withholds nothing
// (an individual, or a foreign company) transfers the full amount and this equals it. receipts.amount
// is the GROSS settled value (cash + withheld) and must stay that way — the output-VAT ledger and the
// receipt's own line pro-ration are both computed from it. Only the displayed figure changes.
const received = (r: any): number =>
  (r?.net_to_pay != null && r.net_to_pay !== '' ? Number(r.net_to_pay) : Number(r?.amount)) || 0;

// ── Line arithmetic — ONE copy, shared by the document modal and the credit/debit note modal ──
// This MUST mirror calcItems() in server/src/routes/portalDocs.ts exactly, including the rounding
// points: the server rounds every line and every running total to 2dp, so a preview that adds raw
// floats drifts a satang off the stored document on ~1 document in 4. Round the same way, here.
const VAT_RATE = 7;
const r2 = (n: number) => Math.round(n * 100) / 100;
// Localised, so it has to be built per render rather than held in a module constant. The rate is
// still interpolated from VAT_RATE — the label must not drift from the arithmetic below.
// i18next's own type. A hand-rolled `(key: string, opts?) => string` does NOT accept the hook's t:
// TFunction can return TFunctionDetailedResult, so assigning it loses at the call site (TS2345).
export type TFn = TFunction<'translation', undefined>;
const vatOptions = (t: TFn): { value: string; label: string; hint: string }[] => [
  { value: 'standard',      label: t('p_vat_opt_standard', { rate: VAT_RATE }),      hint: t('p_vat_hint_standard') },
  { value: 'standard_incl', label: t('p_vat_opt_standard_incl', { rate: VAT_RATE }), hint: t('p_vat_hint_standard_incl') },
  { value: 'zero',          label: t('p_vat_opt_zero'),                              hint: t('p_vat_hint_zero') },
  { value: 'exempt',        label: t('p_vat_opt_exempt'),                            hint: t('p_vat_hint_exempt') },
  { value: 'none',          label: t('p_vat_opt_none'),                              hint: t('p_vat_hint_none') },
];
// Split a VAT-inclusive gross: take the VAT out FIRST, then subtract. Never gross/1.07 then ×7%,
// which loses a satang on ~6.5% of amounts. Mirrors splitInclusive() on the server.
const splitIncl = (gross: number) => { const vat = r2(gross * VAT_RATE / (100 + VAT_RATE)); return { base: r2(gross - vat), vat }; };

/** Document totals from the line editor. `preVat` is always the TAX BASE (s.79), in both modes. */
function lineTotals(items: any[], vatReg: boolean): { preVat: number; vat: number; wht: number; total: number; net: number } {
  let preVat = 0, vat = 0, wht = 0;
  for (const it of items) {
    const gross = r2((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0));
    const cat = it.vatCategory || 'none';
    const rated = vatReg && (cat === 'standard' || cat === 'standard_incl');
    let pv: number, v: number;
    if (rated && cat === 'standard_incl') { const s = splitIncl(gross); pv = s.base; v = s.vat; }
    else { pv = gross; v = rated ? r2(gross * VAT_RATE / 100) : 0; }
    // WHT is withheld by the buyer out of the PRE-VAT amount — it never changes the document total,
    // only the cash that actually arrives. It follows the base in both modes.
    preVat = r2(preVat + pv); vat = r2(vat + v); wht = r2(wht + pv * (Number(it.whtRate) || 0) / 100);
  }
  const total = r2(preVat + vat);
  return { preVat, vat, wht, total, net: r2(total - wht) };
}

// ── Row actions, as one menu ─────────────────────────────────────────────────
// Every list row used to end in a strip of buttons that grew with each feature. One trigger opens
// the same actions as a menu instead.
//
// Two things this must not lose from the buttons it replaces:
//  1. A slow action (a PDF is rendered server-side) shows a spinner ON THAT ITEM the instant it is
//     clicked, and the menu stays open and inert until it finishes — the firm's rule for any button
//     that cannot answer immediately.
//  2. The menu is positioned FIXED from the trigger's own rect, so a table cell, a card with
//     overflow, or the last row at the bottom of the viewport can never clip it.
type RowAction = { label: string; onClick: () => void | Promise<void>; danger?: boolean; warn?: boolean; hidden?: boolean; title?: string };

function RowMenu({ actions }: { actions: RowAction[] }) {
  const { t } = useTranslation();
  const items = actions.filter(a => !a.hidden);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<number>(-1);
  const [pos, setPos] = useState<{ top: number; left: number; up: boolean } | null>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const boxRef = React.useRef<HTMLDivElement>(null);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect(); if (!r) return;
    const h = Math.min(items.length, 8) * 34 + 12;
    const up = r.bottom + h > window.innerHeight - 8;
    setPos({ top: up ? r.top - h - 4 : r.bottom + 4, left: Math.max(8, r.right - 168), up });
  };

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node) || boxRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // A menu pinned to a rect must not linger somewhere the trigger no longer is — so a PAGE
    // scroll closes it. The listener is registered in the capture phase to catch scrolls on any
    // container, which is what makes it reliable; it also meant that once the panel itself grew a
    // scroll area, scrolling the menu closed the menu. Ignore scrolls that came from inside it.
    const bail = (e?: Event) => {
      if (e && boxRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', bail, true);
    window.addEventListener('resize', bail);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', bail, true);
      window.removeEventListener('resize', bail);
    };
  }, [open]);

  if (!items.length) return null;

  const run = async (i: number) => {
    if (busy >= 0) return;                       // one at a time — the menu is inert while working
    const a = items[i];
    let out: any;
    try { out = a.onClick(); } catch { setOpen(false); return; }
    if (out && typeof out.then === 'function') {
      setBusy(i);
      try { await out; } finally { setBusy(-1); setOpen(false); }
    } else setOpen(false);
  };

  return (
    <>
      <button ref={btnRef} title={t('p_menu_actions_tooltip')}
        style={{ ...btnGhost, padding: '5px 11px', fontSize: 16, lineHeight: 1, minWidth: 34,
                 background: open ? '#dde5ec' : '#eef2f5' }}
        onClick={() => { if (!open) place(); setOpen(v => !v); }}>⋯</button>
      {open && pos && (
        <div ref={boxRef} role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 200, minWidth: 168,
                   background: '#fff', border: '1px solid #d3dbe2', borderRadius: 9,
                   boxShadow: '0 8px 24px rgba(20,32,45,.16)', padding: 5, overflow: 'hidden' }}>
          {items.map((a, i) => (
            <button key={i} role="menuitem" title={a.title} disabled={busy >= 0 && busy !== i}
              onClick={() => run(i)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left',
                       padding: '7px 10px', border: 'none', borderRadius: 6, background: 'transparent',
                       fontSize: 13.5, fontWeight: 600, cursor: busy >= 0 ? 'progress' : 'pointer',
                       opacity: busy >= 0 && busy !== i ? .45 : 1,
                       color: a.danger ? '#D0021B' : a.warn ? '#B45309' : '#27303B' }}
              onMouseEnter={e => { if (busy < 0) (e.currentTarget as HTMLElement).style.background = '#f2f5f7'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
              {busy === i && <span style={{ width: 11, height: 11, border: '2px solid #cfd8e0', borderTopColor: ACCENT, borderRadius: '50%', display: 'inline-block', animation: 'mfspin .7s linear infinite', flex: 'none' }} />}
              {busy === i ? t('p_menu_working') : a.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ── Pager ────────────────────────────────────────────────────────────────────
// Shared by every list. Paging happens in the DATABASE (see pageArgs on the server) — this only
// asks for a page and shows where you are. `total` is the real row count, so "1–50 of 412" is
// honest rather than "the first 300 rows, and who knows what else".
const PER_PAGE_CHOICES = [25, 50, 100];

/** A clickable column header. Sorting happens in the database, so it orders ALL rows, not the page. */
function SortTh({ id, label, sort, dir, onSort, align }:
  { id: string; label: string; sort: string; dir: string; onSort: (id: string) => void; align?: 'right' }) {
  const { t } = useTranslation();
  const on = sort === id;
  return (
    <th style={{ ...th, textAlign: align || 'left', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        title={t('p_sort_th_tooltip', { label })} onClick={() => onSort(id)}>
      {label}<span style={{ marginLeft: 4, color: on ? ACCENT : '#c8d2da', fontSize: 10 }}>{on ? (dir === 'asc' ? '▲' : '▼') : '▽'}</span>
    </th>
  );
}

// ── Company Profile: a building in the top-right corner ──────────────────────
// Icon-only on purpose. It is the only header control that is not navigation between documents, and
// the corner is where every product people already use keeps "your own account", so it is found
// without a label. The label still ships as the tooltip and the accessible name.
// The Owner Console's own button, to the LEFT of the building icon. It carries a LABEL as well as
// an icon, unlike ProfileIcon: the building is a convention people already look for in a corner,
// whereas nobody is hunting for a console they have never seen. The label is what makes it findable
// on the first visit, and it is the only thing in the header that is about the business rather than
// about a document.
function OwnerButton({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} title={label} aria-current={on ? 'page' : undefined}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 7, height: 36, padding: '0 11px',
               marginLeft: 6, borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: on ? 800 : 600,
               whiteSpace: 'nowrap',
               border: '1px solid ' + (on ? ACCENT : hov ? '#d3dbe2' : '#e3e7ea'),
               background: on ? '#E8F3F0' : hov ? '#f2f5f7' : '#fff',
               color: on ? ACCENT : '#5b6b79' }}>
      {/* A dial at three-quarter sweep with a needle — the one shape that reads as "how is it going"
          rather than "here is a list". */}
      <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor"
           strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3.2 14.6a8 8 0 1 1 13.6 0" />
        <path d="M10 14.2 13.3 8.9" />
        <circle cx="10" cy="14.6" r="1.25" />
      </svg>
      {label}
    </button>
  );
}

function ProfileIcon({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} title={label} aria-label={label} aria-current={on ? 'page' : undefined}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
               width: 36, height: 36, marginLeft: 6, borderRadius: 9, cursor: 'pointer',
               border: '1px solid ' + (on ? ACCENT : hov ? '#d3dbe2' : 'transparent'),
               background: on ? '#E8F3F0' : hov ? '#f2f5f7' : 'transparent',
               color: on ? ACCENT : '#5b6b79' }}>
      {/* Office block: base, roof line, three floors of windows, a door. Drawn rather than an image
          so it takes the colour of its own state. */}
      <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor"
           strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 17.5h14" />
        <path d="M4.5 17.5V4.2a1 1 0 0 1 1-1h6.3a1 1 0 0 1 1 1v13.3" />
        <path d="M12.8 8.2h2.7a1 1 0 0 1 1 1v8.3" />
        <path d="M7 6.4h1.2M10 6.4h1.2M7 9.4h1.2M10 9.4h1.2M7 12.4h1.2M10 12.4h1.2" />
        <path d="M8 17.5v-2.3h2.3v2.3" />
      </svg>
    </button>
  );
}

// ── The company name doubles as the menu for everything that is not daily work ─
// Kept out of the top row so the documents a merchant touches every day stay one click away. The
// panel is FIXED-positioned off the button's rect, like RowMenu, so the header's own overflow can
// never clip it.
// ── Quotation → invoice ───────────────────────────────────────────────
// A dialog rather than a confirm, because there are three things the client has to see first and a
// yes/no box can only carry one: which kind of document this becomes (it fixes the tax point and
// cannot be changed afterwards), which invoices have ALREADY been raised from this quotation, and
// whether money is already sitting on it as receipts — those receipts stay where they are, so the
// new invoice opens unpaid for its full value.
//
// It is also the reason a double-click cannot bill twice: the second click lands on this.
function ConvertQuotationModal({ doc, onClose, onDone }:
  { doc: any; onClose: () => void; onDone: (docNo: string) => void }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState<any>(null);
  const [docType, setDocType] = useState('invoice');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.portalQuotationImpact(doc.id)
      .then((d: any) => { setInfo(d); setDocType((d.docTypes || ['invoice'])[0]); })
      .catch((e: any) => setErr(e?.message || String(e)));
  }, [doc.id]);
  const types: Record<string, string> = {
    invoice: t('p_conv_type_invoice'), tax_invoice: t('p_conv_type_tax_invoice'), proforma: t('p_conv_type_proforma'),
  };
  const hints: Record<string, string> = {
    // A merchant outside Thailand (info.plain): its invoice is not the Thai s.78/1 bill.
    invoice: info?.plain ? t('pi_conv_invoice_hint') : t('p_conv_type_invoice_hint'), tax_invoice: t('p_conv_type_tax_invoice_hint'), proforma: t('p_conv_type_proforma_hint'),
  };
  const go = async () => {
    setBusy(true); setErr('');
    try { const r = await api.portalQuotationToInvoice(doc.id, docType); onDone(r.doc_no); }
    catch (e: any) { setErr(e?.message || String(e)); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex',
                  alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: '4vh 12px' }}
         onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...card, width: 560, maxWidth: '96vw' }}>
        <div style={{ fontSize: 17, fontWeight: 800 }}>{t('p_conv_title', { docNo: doc.doc_no })}</div>
        <div style={{ fontSize: 12.5, color: '#7c8894', marginTop: 2, marginBottom: 14 }}>{t('p_conv_subtitle')}</div>

        {!info && !err && <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_conv_loading')}</div>}

        {info && (
          <>
            {/* Already billed. Allowed on purpose — a quote can be billed in stages — but it must
                never be a surprise, so it is stated before the button, not after. */}
            {!!info.existing?.length && (
              <div style={{ background: '#FEF6E7', border: '1px solid #F0C879', borderRadius: 9,
                            padding: '11px 13px', fontSize: 13, marginBottom: 12 }}>
                {t('p_conv_already', { list: info.existing.join(', ') })}
              </div>
            )}
            {/* Receipts stay on the quotation. Moving them would rewrite documents the customer
                already holds; copying the figure without them would make the invoice claim a
                payment it cannot evidence. So the new invoice opens unpaid, and we say why. */}
            {!!info.receipts?.length && (
              <div style={{ background: '#FEF6E7', border: '1px solid #F0C879', borderRadius: 9,
                            padding: '11px 13px', fontSize: 13, marginBottom: 12 }}>
                {t('p_conv_receipts', { list: info.receipts.map((r: any) => r.doc_no).join(', ') })}
              </div>
            )}

            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#5b6b79', marginBottom: 6 }}>{t('p_conv_type_label')}</div>
            <div style={{ display: 'grid', gap: 7, marginBottom: 6 }}>
              {(info.docTypes || []).map((k: string) => (
                <label key={k} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer',
                                        border: '1px solid ' + (docType === k ? ACCENT : '#e3e7ea'), borderRadius: 9,
                                        padding: '10px 12px', background: docType === k ? '#F3FAF6' : '#fff' }}>
                  <input type="radio" name="convtype" checked={docType === k} onChange={() => setDocType(k)}
                         style={{ marginTop: 2 }} />
                  <span>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700 }}>{types[k] || k}</span>
                    <span style={{ display: 'block', fontSize: 11.5, color: '#7c8894', marginTop: 1 }}>{hints[k] || ''}</span>
                  </span>
                </label>
              ))}
            </div>
            {/* The one thing about this choice that is genuinely irreversible. */}
            <div style={{ fontSize: 11.5, color: '#98a4b0', marginBottom: 14 }}>{t('p_conv_type_locked')}</div>
          </>
        )}

        {err && <div style={{ background: '#FBEAE8', border: '1px solid #E8B4AE', borderRadius: 9,
                              padding: '11px 13px', fontSize: 13, color: '#8a1f16', marginBottom: 12 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={btnGhost} onClick={onClose} disabled={busy}>{t('p_conv_cancel')}</button>
          <button style={{ ...btn, opacity: busy || !info ? .6 : 1 }} onClick={go} disabled={busy || !info}>
            {busy ? t('p_conv_working') : t('p_conv_go')}
          </button>
        </div>
      </div>
    </div>
  );
}

function BrandMenu({ name, groups, tab, setTab, active, companies, activeClientId, onPickCompany, onSignOut }:
  { name: string; groups: { title: string; items: { k: any; label: string; hint?: string; locked?: string }[] }[];
    tab: string; setTab: (k: any) => void; active?: string;
    companies?: { clientId: string; name: string }[]; activeClientId?: string;
    onPickCompany?: (clientId: string) => void; onSignOut?: () => void }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  // A long company list must not push the navigation off the panel. The list BOUNDS ITSELF and
  // scrolls in its own section, so Settings and Sign out sit at the same height whether the login
  // holds two companies or twenty — rather than every group competing for one shared scroll, where
  // the thing that varies in size is also the thing that squeezes everything else.
  const [q, setQ] = useState('');
  const coListRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const boxRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) { setQ(''); return; }
    // Open on the company you are IN. Past a few entries the active one sits below the fold, and a
    // switcher that opens showing none of your current state reads as if it had lost it.
    const reveal = window.setTimeout(() => {
      const box = coListRef.current;
      const el = box?.querySelector('[data-on="1"]') as HTMLElement | null;
      if (box && el && box.scrollHeight > box.clientHeight) box.scrollTop = Math.max(0, el.offsetTop - 8);
    }, 0);
    const away = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node) || boxRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // A PAGE scroll closes the panel, because it is pinned to a rect the trigger may have left.
    // The listener captures, so it also sees scrolls inside the panel — and once the panel grew
    // scroll areas of its own, scrolling the menu closed the menu. Ignore those.
    const bail = (e?: Event) => { if (e && boxRef.current?.contains(e.target as Node)) return; setOpen(false); };
    document.addEventListener('mousedown', away); document.addEventListener('keydown', esc);
    window.addEventListener('scroll', bail, true); window.addEventListener('resize', bail);
    return () => {
      window.clearTimeout(reveal);
      document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', bail, true); window.removeEventListener('resize', bail);
    };
  }, [open]);
  // Section headings sit on a recessed band closed by a hairline, so the groups read as separate
  // shelves inside the panel instead of one continuous list of text. Declared once — three places
  // used to repeat the same six properties and had already drifted apart in padding.
  const secHead = (first: boolean): React.CSSProperties => ({
    fontSize: 10.5, fontWeight: 800, color: '#8d9aa6', letterSpacing: '.07em', textTransform: 'uppercase',
    padding: first ? '7px 10px 6px' : '9px 10px 6px', margin: first ? '-6px -6px 4px' : '4px -6px 4px',
    background: 'linear-gradient(#f5f7f9, #fafbfc)', borderTop: first ? 'none' : '1px solid #eef1f4',
    borderBottom: '1px solid #eef1f4', borderTopLeftRadius: first ? 11 : 0, borderTopRightRadius: first ? 11 : 0,
  });
  // A raised item: the current company, the open page. A ring plus a soft drop makes it sit ON the
  // panel rather than being a differently-coloured patch of it.
  const raised = '0 1px 2px rgba(20,32,45,.10), inset 0 0 0 1px rgba(20,90,72,.13)';
  return (
    <>
      <button ref={btnRef} title={t('p_menu_tooltip')}
        onClick={() => { const r = btnRef.current?.getBoundingClientRect(); if (r) setPos({ top: r.bottom + 2, left: r.left }); setOpen(v => !v); }}
        style={{ display: 'flex', alignItems: 'center', gap: 9, border: 'none', cursor: 'pointer',
                 background: open ? '#f2f5f7' : 'transparent', padding: '10px 12px 10px 8px', borderRadius: 9 }}>
        <img src="/logo.png" alt={t('p_logo_alt')} style={{ width: 30, height: 30, objectFit: 'contain' }} />
        <span style={{ textAlign: 'left' }}>
          <span style={{ display: 'block', fontWeight: 800, color: '#27303B', fontSize: 15, whiteSpace: 'nowrap' }}>{name}</span>
          {/* When the open page lives in here, say so — otherwise nothing in the top row is
              highlighted and the merchant cannot tell where they are. */}
          {active && <span style={{ display: 'block', fontSize: 11.5, color: ACCENT, fontWeight: 700 }}>{active}</span>}
        </span>
        <span style={{ color: '#98a4b0', fontSize: 10, marginTop: 2 }}>▼</span>
      </button>
      {open && pos && (
        <div ref={boxRef} role="menu" className="mf-menu-pop"
          // Depth, not decoration (owner 2026-09-22: the panel read as flat). Three things do it:
          // a shadow in three layers — a tight contact shadow, a mid lift and a wide ambient one,
          // which is how a real object sits above a surface; a hairline of white along the inside
          // of the top edge, the highlight a lit edge catches; and a faint top-to-bottom gradient
          // so the panel is not one uniform fill. The caret below anchors it to the button.
          style={{ position: 'fixed', top: pos.top + 5, left: pos.left, zIndex: 200, minWidth: 258,
                   background: 'linear-gradient(#ffffff, #fbfcfd)',
                   border: '1px solid #ccd6de', borderRadius: 12, padding: 6,
                   // The panel had no height limit at all: it simply grew downwards and ran off the
                   // bottom of the window, taking Settings and Sign out with it. Nobody noticed
                   // while it was short; adding the "My books" group on 2026-10-02 pushed it past
                   // the edge on a laptop. Cap it to whatever room is left below the button and let
                   // the CONTENT scroll — the scroll is on the inner div, not here, so the caret
                   // and the rounded border stay put instead of sliding up out of the panel.
                   maxHeight: `calc(100vh - ${Math.round(pos.top) + 18}px)`,
                   display: 'flex', flexDirection: 'column',
                   boxShadow: '0 1px 2px rgba(20,32,45,.10), 0 8px 18px -6px rgba(20,32,45,.16),'
                            + ' 0 28px 52px -14px rgba(20,32,45,.26), inset 0 1px 0 rgba(255,255,255,.9)' }}>
          {/* The little tongue joining panel to button. Only its top two borders are drawn, so the
              square's lower half stays invisible against the panel it overlaps. */}
          <span aria-hidden="true"
            style={{ position: 'absolute', top: -6, left: 22, width: 11, height: 11, background: '#fff',
                     borderLeft: '1px solid #ccd6de', borderTop: '1px solid #ccd6de',
                     transform: 'rotate(45deg)', borderTopLeftRadius: 3 }} />
          {/* Everything below scrolls as one. The negative margin and matching padding keep the
              section headings' full-bleed recessed bands reaching the panel edge, which they do by
              being wider than the content; without it the scrollbar would cut them short. */}
          <div style={{ overflowY: 'auto', overflowX: 'hidden', minHeight: 0, margin: '-6px', padding: '6px' }}>
          {/* One owner, several companies (owner 2026-09-09). Shown only when there IS more than
              one, so nothing changes for the single-company client. Switching reloads the whole
              portal for that company — documents, numbering, customers and books are separate and
              must never be mixed in one view. */}
          {(companies?.length || 0) > 1 && (() => {
            const all = companies!;
            // Thresholds, not a single rule. Up to six the list is faster to read than to filter,
            // and capping it would add a scrollbar nobody needs. Past six it scrolls in its own
            // box — six rows is what the panel can spend without the navigation below it moving.
            // Past ten, reading becomes slower than typing, so a filter appears.
            const CAP = 6, FILTER_FROM = 10;
            const needle = q.trim().toLowerCase();
            const shown = needle ? all.filter(c => c.name.toLowerCase().includes(needle)) : all;
            return (
            <div>
              <div style={secHead(true)}>
                {t('p_menu_group_company')}
                {all.length > CAP && <span style={{ float: 'right', fontWeight: 600, letterSpacing: 0, textTransform: 'none' }}>
                  {needle ? t('p_menu_company_n_of', { n: shown.length, total: all.length }) : all.length}
                </span>}
              </div>
              {all.length >= FILTER_FROM && (
                <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('p_menu_company_find')}
                  autoComplete="off" aria-label={t('p_menu_company_find')}
                  onKeyDown={e => { if (e.key === 'Escape' && q) { e.stopPropagation(); setQ(''); } }}
                  style={{ width: '100%', boxSizing: 'border-box', margin: '2px 0 5px', padding: '6px 9px',
                           border: '1px solid #dde3e8', borderRadius: 7, fontSize: 13, outline: 'none',
                           background: '#fbfcfd', color: '#27303B' }} />
              )}
              <div ref={coListRef} style={all.length > CAP
                ? { maxHeight: CAP * 37, overflowY: 'auto', overflowX: 'hidden', margin: '0 -2px', padding: '0 2px' }
                : undefined}>
              {!shown.length && (
                <div style={{ padding: '8px 10px', fontSize: 12.5, color: '#98a4b0' }}>{t('p_menu_company_none')}</div>
              )}
              {shown.map(c => {
                const on = c.clientId === activeClientId;
                return (
                  <button key={c.clientId} role="menuitem" data-on={on ? '1' : '0'}
                    onClick={() => { if (!on) onPickCompany?.(c.clientId); setOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left',
                             border: 'none', borderRadius: 7, padding: '8px 10px', cursor: on ? 'default' : 'pointer',
                             background: on ? '#E8F3F0' : 'transparent', color: on ? ACCENT : '#27303B',
                             boxShadow: on ? raised : 'none',
                             fontSize: 13.5, fontWeight: on ? 800 : 500 }}
                    onMouseEnter={e => { if (!on) (e.currentTarget as HTMLElement).style.background = '#f2f5f7'; }}
                    onMouseLeave={e => { if (!on) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                    <span style={{ width: 12, flex: 'none' }}>{on ? '✓' : ''}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                  </button>
                );
              })}
              </div>
            </div>
            );
          })()}
          {groups.map((g, gi) => (
            <div key={g.title}>
              {/* "First" is about the panel, not the array: with one company the switcher above is
                  not rendered at all, and this heading is the one that meets the rounded top edge. */}
              <div style={secHead(gi === 0 && (companies?.length || 0) <= 1)}>{g.title}</div>
              {/* `locked` shows an entry that EXISTS but has nothing behind it yet — greyed, inert,
                  and carrying the reason where its hint would be. Hiding it instead (what this menu
                  did until 2026-10-02) was worse in both directions: a client cannot tell the page
                  is coming, and the firm's own owner read the absence as "never built". A disabled
                  row with a stated reason says neither of those things. */}
              {g.items.map(it => (
                <button key={it.k} role="menuitem" disabled={!!it.locked} aria-disabled={!!it.locked}
                  title={it.locked || undefined}
                  onClick={() => { if (it.locked) return; setTab(it.k); setOpen(false); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 7,
                           padding: '8px 10px', cursor: it.locked ? 'default' : 'pointer',
                           boxShadow: tab === it.k ? raised : 'none',
                           background: tab === it.k ? '#EAF6EF' : 'transparent' }}
                  onMouseEnter={e => { if (!it.locked && tab !== it.k) (e.currentTarget as HTMLElement).style.background = '#f2f5f7'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = tab === it.k ? '#EAF6EF' : 'transparent'; }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 700,
                                 color: it.locked ? '#aab4bd' : tab === it.k ? ACCENT : '#27303B' }}>
                    {it.label}
                    {it.locked && (
                      <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                           strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" style={{ flex: 'none', opacity: .8 }}>
                        <rect x="3" y="7" width="10" height="7" rx="1.6" />
                        <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
                      </svg>
                    )}
                  </span>
                  {(it.locked || it.hint) && (
                    <span style={{ display: 'block', fontSize: 11.5, color: it.locked ? '#b6c0c8' : '#7c8894', marginTop: 1 }}>
                      {it.locked || it.hint}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}

          {/* Language and sign-out live here rather than in the top bar (owner 2026-09-21). The bar
              had grown to eight tabs and was scrolling sideways to fit them; these two are settings,
              not navigation, and moving them gives the tabs the width they needed. */}
          <div style={secHead(false)}>{t('p_menu_group_settings')}</div>
          <div style={{ display: 'flex', gap: 4, padding: '0 10px 6px' }}>
            {portalLangs(t, String(i18n.language || '').slice(0, 2)).map(l => {
              const on = String(i18n.language || '').slice(0, 2) === l.k;
              return (
                <button key={l.k} role="menuitem"
                  onClick={() => { i18n.changeLanguage(l.k); try { localStorage.setItem('mf_uiLang', l.k); } catch { /* private mode */ } }}
                  style={{ flex: 1, border: '1px solid ' + (on ? ACCENT : '#dde3e8'), borderRadius: 7,
                           padding: '6px 4px', cursor: 'pointer', fontSize: 12.5, fontWeight: on ? 800 : 500,
                           background: on ? ACCENT : 'linear-gradient(#fff, #f7f9fa)', color: on ? '#fff' : '#5b6b79',
                           boxShadow: on ? '0 1px 3px rgba(20,90,72,.35)' : '0 1px 1px rgba(20,32,45,.05)' }}>
                  {l.short}
                </button>
              );
            })}
          </div>
          <button role="menuitem" onClick={() => { setOpen(false); onSignOut?.(); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 7,
                     padding: '8px 10px', cursor: 'pointer', background: 'transparent', fontSize: 13.5,
                     color: '#a32018', fontWeight: 600 }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#fbeae8'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
            {t('p_nav_sign_out')}
          </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Language ─────────────────────────────────────────────────────────────────
// The portal shares the staff app's i18n, so resolveInitialLang() already picks the opening
// language: a remembered choice first, else the browser's, else English. This only lets the client
// change it, and remembers that choice under the same key the staff app uses.
// Only the languages the workspace offers (utils/countryFeatures.ts), plus the one in use.
const portalLangs = (t: TFn, current?: string): { k: string; label: string; short: string }[] => [
  { k: 'en', label: t('p_lang_en'), short: t('p_lang_en_short') },
  { k: 'zh', label: '中文', short: t('p_lang_zh_short') },
  { k: 'th', label: t('p_lang_th_short'), short: t('p_lang_th_short') },
].filter(l => offeredUiLanguages(current).includes(l.k));

// ── Which office holds this account ─────────────────────────────────────────
// The label this installation reports on the profile (OFFICE_LABEL, server services/thisOffice.ts),
// so the client sees whose books these are rather than having to infer it.
function OfficeBadge({ office }: { office?: { key?: string; label?: string } }) {
  const { t } = useTranslation();
  if (!office?.label) return null;   // an older API that does not report one — show nothing, never a guess
  return (
    <span title={t('p_office_hint')}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none', whiteSpace: 'nowrap',
               padding: '4px 10px', borderRadius: 999, background: '#E8F3F0', color: '#0E7C66',
               border: '1px solid #C5E3DA', fontSize: 12, fontWeight: 800 }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
      </svg>
      {office.label}
    </span>
  );
}

function LangPicker() {
  const { t, i18n } = useTranslation();
  const cur = String(i18n.language || 'en').slice(0, 2);
  const pick = (k: string) => {
    try { localStorage.setItem('mf_uiLang', k); } catch { /* private window — session only */ }
    i18n.changeLanguage(k);
  };
  return (
    <div style={{ display: 'flex', border: '1px solid #e3e7ea', borderRadius: 8, overflow: 'hidden', flex: 'none' }}>
      {portalLangs(t, cur).map(l => (
        <button key={l.k} onClick={() => pick(l.k)} title={l.label}
          style={{ border: 'none', cursor: 'pointer', padding: '6px 10px', fontSize: 12.5,
                   fontWeight: cur === l.k ? 800 : 500,
                   background: cur === l.k ? ACCENT : 'transparent',
                   color: cur === l.k ? '#fff' : '#5b6b79' }}>
          {l.short}
        </button>
      ))}
    </div>
  );
}

function Pager({ page, per, total, onPage, onPer }:
  { page: number; per: number; total: number; onPage: (p: number) => void; onPer: (n: number) => void }) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / per));
  const from = total === 0 ? 0 : (page - 1) * per + 1;
  const to = Math.min(total, page * per);
  const nav: React.CSSProperties = { ...btnGhost, padding: '5px 10px', fontSize: 13, minWidth: 34 };
  const dim = (off: boolean): React.CSSProperties => ({ ...nav, opacity: off ? .4 : 1, cursor: off ? 'default' : 'pointer' });
  const first = page <= 1, last = page >= pages;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 12,
                  paddingTop: 10, borderTop: '1px solid #eef1f4' }}>
      <span style={{ fontSize: 13, color: '#5b6b79' }}>
        {total === 0 ? t('p_pager_no_records') : t('p_pager_showing', { from, to, total })}
      </span>
      <div style={{ flex: 1 }} />
      <label style={{ fontSize: 13, color: '#5b6b79', display: 'flex', alignItems: 'center', gap: 6 }}>
        {t('p_pager_per_page')}
        <select style={{ ...inp, width: 78, padding: '5px 8px', fontSize: 13 }} value={per}
                onChange={e => onPer(Number(e.target.value))}>
          {PER_PAGE_CHOICES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <button style={dim(first)} disabled={first} title={t('p_pager_first')} onClick={() => onPage(1)}>«</button>
        <button style={dim(first)} disabled={first} title={t('p_pager_prev')} onClick={() => onPage(page - 1)}>‹</button>
        <span style={{ fontSize: 13, color: '#5b6b79', padding: '0 4px', whiteSpace: 'nowrap' }}>
          {t('p_pager_page_of', { page, pages })}
        </span>
        <button style={dim(last)} disabled={last} title={t('p_pager_next')} onClick={() => onPage(page + 1)}>›</button>
        <button style={dim(last)} disabled={last} title={t('p_pager_last')} onClick={() => onPage(pages)}>»</button>
      </div>
    </div>
  );
}

/** Page state + the guard every list needs: never sit on a page that no longer exists. */
function usePaging(reload: (page: number, per: number) => void) {
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(50);
  const [total, setTotal] = useState(0);
  // Deleting the last row of the last page (or shrinking the page size) can leave `page` past the
  // end, which would show an empty table with rows still in the database. Step back instead.
  useEffect(() => {
    const pages = Math.max(1, Math.ceil(total / per));
    if (page > pages) setPage(pages);
  }, [total, per, page]);
  return { page, per, total, setTotal, setPer: (n: number) => { setPer(n); setPage(1); }, setPage, reload };
}

// ── Search box ───────────────────────────────────────────────────────────────
// Debounced so a request does not fly on every keystroke, and the caller is handed the settled
// term. Searching happens in the database (see searchClause on the server) — filtering the fetched
// page here would only ever search the rows already on screen.
// ── Searchable picker (owner 2026-09-08) ────────────────────────────────────────────────────────
// Every "pick a customer / an invoice" control in this portal was a plain <select> filled from ONE
// page of the API — 50 rows. Samui Host has 69 customers, so a customer added today (C069) simply
// was not in the list, and neither was any invoice older than the last 50. This asks the SERVER as
// the operator types, so the whole book is reachable however long it gets, and it says how many of
// the total it is showing rather than pretending the list is complete.
type PickOpt = { id: string; label: string; sub?: string };
function PickerSelect({ value, options, total, onSearch, onPick, placeholder, footer }: {
  value?: PickOpt | null;
  options: PickOpt[];
  total?: number;
  onSearch: (term: string) => void;
  onPick: (o: PickOpt) => void;
  placeholder: string;
  /** Rendered under the list — used for "+ Add customer". */
  footer?: (close: () => void) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = React.useRef<HTMLDivElement | null>(null);
  const first = React.useRef(true);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  // Debounced so a fast typist does not fire a query per keystroke; the mount is skipped because
  // the caller has already loaded the opening page.
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const h = setTimeout(() => onSearch(q), 300);
    return () => clearTimeout(h);
  }, [q]); // eslint-disable-line
  return (
    <div ref={box} style={{ position: 'relative' }}>
      <button type="button" onClick={() => { setOpen(o => !o); setQ(''); }}
        style={{ ...inp, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: value ? '#0f172a' : '#94a3b8' }}>
          {value ? value.label : placeholder}
        </span>
        <span style={{ color: '#94a3b8', flexShrink: 0 }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 60, top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 12px 28px rgba(15,23,42,0.14)', maxHeight: 340, overflowY: 'auto' }}>
          <div style={{ position: 'sticky', top: 0, background: '#fff', padding: 8, borderBottom: '1px solid #eef2f6' }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('p_pick_search')}
              style={{ ...inp, padding: '7px 9px', fontSize: 13 }} />
          </div>
          {options.map(o => (
            <div key={o.id} onClick={() => { onPick(o); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f5f7f9' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f3f7f6')}
              onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>{o.label}</div>
              {o.sub && <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{o.sub}</div>}
            </div>
          ))}
          {!options.length && <div style={{ padding: '12px', fontSize: 12.5, color: '#94a3b8' }}>{t('p_pick_none')}</div>}
          {typeof total === 'number' && total > options.length && (
            <div style={{ padding: '6px 12px', fontSize: 11.5, color: '#94a3b8', background: '#fafbfc' }}>
              {t('p_pick_more', { shown: options.length, total })}
            </div>
          )}
          {footer?.(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

// The customer control for the invoice / quotation form: search the whole register, and when the
// buyer is genuinely new, add them right here instead of leaving the half-typed document behind.
function CustomerPicker({ valueId, onChange, plain }: { valueId: string; onChange: (id: string, row?: any) => void; plain?: boolean }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<any[]>([]); const [total, setTotal] = useState(0);
  const [adding, setAdding] = useState(false); const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  // The all-zero branch = Thai head office; a merchant outside Thailand has no such convention.
  const blankBranch = plain ? '' : '00000';
  const [f, setF] = useState({ name: '', taxId: '', branchNo: blankBranch, address: '', email: '', phone: '' });
  // 200, not the API default of 50: a register of this size then arrives whole, and the search
  // (plus the 'showing n of m' line) is what carries a merchant whose book outgrows it.
  const search = (term: string) => api.portalCustomers(1, 200, term)
    .then((d: any) => { setRows(d.rows || []); setTotal(Number(d.total) || 0); }).catch(() => {});
  useEffect(() => { search(''); }, []); // eslint-disable-line
  const sel = rows.find(r => r.id === valueId);
  const add = async (close: () => void) => {
    if (!f.name.trim()) { setErr(t('p_cust_name_req')); return; }
    setBusy(true); setErr('');
    try {
      const r: any = await api.portalSaveCustomer(null, f);
      await search('');
      onChange(r.id, { ...f, id: r.id });
      setAdding(false); setF({ name: '', taxId: '', branchNo: blankBranch, address: '', email: '', phone: '' });
      close();
    } catch (e: any) { setErr(e?.message || t('p_cust_add_failed')); }
    finally { setBusy(false); }
  };
  return (
    <PickerSelect
      value={sel ? { id: sel.id, label: sel.name, sub: sel.tax_id || '' } : null}
      options={rows.map(r => ({ id: r.id, label: r.name, sub: [r.tax_id, r.email].filter(Boolean).join(' · ') }))}
      total={total}
      onSearch={search}
      onPick={o => onChange(o.id, rows.find(r => r.id === o.id))}
      placeholder={t('p_select_placeholder')}
      footer={close => (adding ? (
        <div style={{ padding: 10, borderTop: '1px solid #eef2f6', background: '#fafbfc' }} onClick={e => e.stopPropagation()}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>{t('p_cust_add_title')}</div>
          {([['name', t('p_cust_f_name')], ['taxId', t('p_cust_f_taxid')], ['branchNo', t('p_cust_f_branch')],
             ['address', t('p_cust_f_address')], ['email', t('p_cust_f_email')], ['phone', t('p_cust_f_phone')]] as [string, string][])
            .filter(([k]) => !(plain && k === 'branchNo'))
            .map(([k, label]) => (
              <input key={k} value={(f as any)[k]} onChange={e => setF({ ...f, [k]: e.target.value })} placeholder={label}
                style={{ ...inp, padding: '6px 9px', fontSize: 12.5, marginBottom: 5 }} />
            ))}
          {err && <div style={{ fontSize: 11.5, color: '#b91c1c', marginBottom: 5 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button type="button" style={{ ...btnGhost, padding: '5px 12px', fontSize: 12 }} onClick={() => setAdding(false)}>{t('p_btn_cancel')}</button>
            <button type="button" style={{ ...btn, padding: '5px 12px', fontSize: 12, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => add(close)}>
              {busy ? '…' : t('p_cust_add_save')}
            </button>
          </div>
        </div>
      ) : (
        <div onClick={() => setAdding(true)}
          style={{ padding: '9px 12px', borderTop: '1px solid #eef2f6', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#0E7C66', background: '#fafbfc' }}>
          + {t('p_cust_add_title')}
        </div>
      ))}
    />
  );
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  const { t } = useTranslation();
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);          // reset when the caller clears it
  useEffect(() => {
    if (text === value) return;
    const t = setTimeout(() => onChange(text), 280);
    return () => clearTimeout(t);
  }, [text]); // eslint-disable-line
  return (
    <div style={{ position: 'relative', flex: '0 1 300px', minWidth: 190 }}>
      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                     color: '#98a4b0', fontSize: 13, pointerEvents: 'none' }}>⌕</span>
      <input style={{ ...inp, padding: '8px 30px 8px 26px', fontSize: 13.5 }} value={text} placeholder={placeholder}
             onChange={e => setText(e.target.value)}
             onKeyDown={e => { if (e.key === 'Enter') onChange(text); if (e.key === 'Escape') { setText(''); onChange(''); } }} />
      {!!text && (
        <button title={t('p_search_clear')} onClick={() => { setText(''); onChange(''); }}
          style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none',
                   background: 'transparent', cursor: 'pointer', color: '#7c8894', fontSize: 15, lineHeight: 1, padding: 4 }}>×</button>
      )}
    </div>
  );
}

function Login({ onDone }: { onDone: (mustChange: boolean, isAdmin?: boolean) => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(''); const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const pubBrand = usePublicBrand();
  // A sign-in the own host refused but another installation claims: held here, shown to the
  // person, and sent nowhere until they say yes (review 2026-10-15 — a resolve answer is an
  // unauthenticated claim, so it may propose the hop but never route the password by itself).
  const [hop, setHop] = useState<{ system: string; em: string; pw: string } | null>(null);
  const finish = (r: any, system: string) => {
    // Clients get their invoicing portal; the OWN installation's admins get the portal-accounts
    // backoffice right here. A foreign installation's admin does not: the backoffice talks to
    // /portal-accounts on this build's own host, which their token cannot open — they sign in at
    // their own installation instead.
    const ok = system ? r.role === 'client' : (r.role === 'client' || r.role === 'admin');
    if (!ok) { setPortalSystem(null); setErr(t('p_login_err_staff')); setBusy(false); return; }
    setItemWithRoom('mf_token', r.token); // a full origin must never eat the login (incident 2026-09-23)
    // The pin is stored BESIDE the token it belongs to (an atom, utils/api.ts), after the token:
    // a refresh re-pins in the lazy initialiser below, and any later sign-in unpins it.
    setPortalSystem(system || null);
    storePortalPin(system);
    if (r.role === 'admin') localStorage.setItem('mf_portal_admin', '1'); else localStorage.removeItem('mf_portal_admin');
    onDone(!!r.mustChangePassword, r.role === 'admin');
  };
  const go = async () => {
    const em = email.trim();
    if (!em || !pw) { setErr(t('p_login_err_blank')); return; }   // never resolve on a blank (alert noise)
    setBusy(true); setErr(''); setHop(null);
    try {
      // The own office first, password and all — the single-host behaviour this page always had.
      finish(await api.login(em, pw), '');
    } catch (e: any) {
      // Only the own host's plain refusal opens the question of other offices. A disabled account,
      // a rate limit or an outage is answered where it happened. The string is our own server's
      // (routes/auth.ts answers unknown email and wrong password identically).
      if (String(e?.message) !== 'Invalid credentials' || !Object.keys(PORTAL_SYSTEMS).length) {
        setErr(e?.message || t('p_login_err_failed')); setBusy(false); return;
      }
      try {
        const system = await resolveForeignPortalSystem(em);
        if (system) { setHop({ system, em, pw }); setBusy(false); return; }
      } catch { /* nobody reachable claims it — fall through to the own host's answer */ }
      setErr(e?.message || t('p_login_err_failed')); setBusy(false);
    }
  };
  const goHop = async () => {
    if (!hop) return;
    setBusy(true); setErr('');
    try { const r: any = await portalLoginAt(hop.system, hop.em, hop.pw); setHop(null); finish(r, hop.system); }
    catch (e: any) { setErr(e?.message || t('p_login_err_failed')); setBusy(false); }
  };
  const hopHost = (() => { try { return hop ? new URL(portalSystemBase(hop.system)).host : ''; } catch { return hop?.system || ''; } })();
  return (
    <div style={{ maxWidth: 400, margin: '10vh auto', ...card }}>
      {/* The picker belongs here too, not only inside the app: a client whose browser language is
          neither Thai nor Chinese lands on this page in English and needs to be able to switch
          before they can read the sign-in form. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <img src="/logo.png" alt={pubBrand || t('ctrl_logo_alt')} style={{ width: 38, height: 38, objectFit: 'contain' }} />
        <div style={{ fontSize: 18, fontWeight: 800, color: '#27303B', flex: 1 }}>{clientPortalTitle(t, pubBrand)}</div>
        <LangPicker />
      </div>
      <div style={{ fontSize: 12.5, color: '#7c8894' }}>{t('p_login_subtitle')}</div>
      <label style={lbl}>{t('p_login_email')}</label>
      <input style={inp} value={email} onChange={e => setEmail(e.target.value)} autoFocus />
      <label style={lbl}>{t('p_login_password')}</label>
      <input style={inp} type="password" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === 'Enter' && go()} />
      {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
      {hop ? (
        // The hop, stated before it happens: which host is about to receive this sign-in. The
        // password has NOT left the page yet — "Yes" is what sends it, and only there.
        <div style={{ marginTop: 14, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 9, padding: '11px 13px' }}>
          <div style={{ fontSize: 12.5, color: '#92400E' }}>{t('p_login_hop_text', { host: hopHost })}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button style={{ ...btn, flex: 1, opacity: busy ? .6 : 1 }} disabled={busy} onClick={goHop}>{busy ? t('p_login_signing_in') : t('p_login_hop_go')}</button>
            <button style={{ ...btn, flex: 1, background: '#fff', color: '#27303B', border: '1px solid #d4dae0' }} disabled={busy} onClick={() => setHop(null)}>{t('p_login_hop_cancel')}</button>
          </div>
        </div>
      ) : (
        <button style={{ ...btn, width: '100%', marginTop: 16, opacity: busy ? .6 : 1 }} disabled={busy} onClick={go}>{busy ? t('p_login_signing_in') : t('p_login_signin')}</button>
      )}
    </div>
  );
}

// `first` = the forced first-login rotation, where the temporary password already proved identity.
// Any later change must present the current password, so a stolen 30-day token cannot lock the
// real owner out of their own portal.
function ChangePassword({ onDone, first = true, onCancel }: { onDone: () => void; first?: boolean; onCancel?: () => void }) {
  const { t } = useTranslation();
  const [cur, setCur] = useState('');
  const [pw, setPw] = useState(''); const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const go = async () => {
    if (!first && !cur) return setErr(t('p_pw_err_current_required'));
    if (pw.length < 8) return setErr(t('p_pw_err_min_length'));
    if (pw !== pw2) return setErr(t('p_pw_err_mismatch'));
    setBusy(true); setErr('');
    try { await api.portalChangePassword(pw, first ? undefined : cur); onDone(); } catch (e: any) { setErr(e?.message || t('p_pw_err_failed')); setBusy(false); }
  };
  return (
    <div style={{ maxWidth: 400, margin: first ? '10vh auto' : '0 0 12px', ...card }}>
      <div style={{ fontSize: 17, fontWeight: 800, color: '#27303B' }}>{first ? t('p_pw_title_first') : t('p_pw_title_change')}</div>
      <div style={{ fontSize: 12.5, color: '#7c8894', marginTop: 4 }}>{first ? t('p_pw_hint_first') : t('p_pw_hint_current')}</div>
      {!first && <><label style={lbl}>{t('p_pw_current')}</label><input style={inp} type="password" value={cur} onChange={e => setCur(e.target.value)} /></>}
      <label style={lbl}>{t('p_pw_new')}</label>
      <input style={inp} type="password" value={pw} onChange={e => setPw(e.target.value)} />
      <label style={lbl}>{t('p_pw_confirm')}</label>
      <input style={inp} type="password" value={pw2} onChange={e => setPw2(e.target.value)} />
      {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
      <button style={{ ...btn, width: '100%', marginTop: 16, opacity: busy ? .6 : 1 }} disabled={busy} onClick={go}>{t('p_btn_save_bilingual')}</button>
      {!first && onCancel && <button style={{ ...btnGhost, width: '100%', marginTop: 8 }} onClick={onCancel}>{t('p_btn_cancel')}</button>}
    </div>
  );
}

// This is a recorded liability acknowledgement — accepting it stamps disclaimer_accepted_at. The
// statement itself therefore stays EXACTLY as it has always been, English and Thai together, in
// every locale: swapping it for whichever single language the client happens to have selected would
// change what they consented to. Chinese is added underneath as a courtesy translation, which adds
// comprehension without taking anything out of the record. Only the heading, the acceptance line's
// extra sentence and the button follow the UI language.
function Disclaimer({ onDone }: { onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const [ok, setOk] = useState(false); const [busy, setBusy] = useState(false);
  const zh = String(i18n.language || '').slice(0, 2) === 'zh';
  return (
    <div style={{ maxWidth: 560, margin: '8vh auto', ...card }}>
      <div style={{ fontSize: 17, fontWeight: 800, color: '#27303B' }}>{t('p_disc_title')}</div>
      <div style={{ fontSize: 13.5, color: '#3c4656', lineHeight: 1.8, marginTop: 10 }}>
        All of your sales documents (quotations, invoices, receipts) must be issued through this system so their
        running numbers stay continuous. Numbers are never reused; cancelling a document keeps its number as VOID.
        <b> You are solely responsible for the correctness and continuity of your document numbers</b>, including any
        documents you issue outside this system.
        <br /><br />
        เอกสารขายทั้งหมดของคุณต้องออกผ่านระบบนี้เพื่อให้เลขที่เอกสารต่อเนื่อง เลขที่จะไม่ถูกนำกลับมาใช้ซ้ำ
        การยกเลิกเอกสารจะคงเลขไว้เป็น VOID <b>คุณเป็นผู้รับผิดชอบความถูกต้องและความต่อเนื่องของเลขที่เอกสารแต่เพียงผู้เดียว</b>
        {zh && <>
          <br /><br />
          {t('p_disc_body_p1')}<b> {t('p_disc_body_bold')}</b>{t('p_disc_body_p2')}
        </>}
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 14, fontSize: 13.5, color: '#27303B', cursor: 'pointer' }}>
        <input type="checkbox" checked={ok} onChange={e => setOk(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          I have read and accept the above. / ข้าพเจ้าได้อ่านและยอมรับข้อความข้างต้น
          {zh && <><br />{t('p_disc_accept')}</>}
        </span>
      </label>
      <button style={{ ...btn, marginTop: 16, opacity: ok && !busy ? 1 : .5 }} disabled={!ok || busy}
        onClick={async () => { setBusy(true); try { await api.portalAcceptDisclaimer(); onDone(); } catch { setBusy(false); } }}>
        {t('p_disc_continue')}
      </button>
    </div>
  );
}

// ── My Customers ─────────────────────────────────────────────────────────────
function Customers({ plain }: { plain?: boolean }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<any[]>([]); const [edit, setEdit] = useState<any>(null);
  const [page, setPage] = useState(1); const [per, setPerRaw] = useState(50); const [total, setTotal] = useState(0);
  const setPer = (n: number) => { setPerRaw(n); setPage(1); };
  const [q, setQRaw] = useState('');
  const setQ = (v: string) => { setQRaw(v); setPage(1); };
  const load = (p = page, n = per, term = q) => api.portalCustomers(p, n, term)
    .then((d: any) => { setRows(d.rows || []); setTotal(Number(d.total) || 0); }).catch(() => {});
  useEffect(() => { load(page, per, q); }, [page, per, q]); // eslint-disable-line
  useEffect(() => { const pages = Math.max(1, Math.ceil(total / per)); if (page > pages) setPage(pages); }, [total, per, page]);
  const save = async () => {
    try { await api.portalSaveCustomer(edit.id || null, edit); setEdit(null); load(); }
    catch (e: any) { alert(e?.message || t('p_err_save_failed')); }
  };
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 800 }}>{t('p_cust_title')}</div>
        <div style={{ flex: 1 }} />
        <SearchBox value={q} onChange={setQ} placeholder={t('p_cust_search_ph')} />
        <button style={btn} onClick={() => setEdit({ name: '', taxId: '', branchNo: plain ? '' : '00000', address: '', email: '', phone: '', notes: '' })}>{t('p_cust_add')}</button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={{ ...th, width: 76 }}>{t('p_cust_th_no')}</th><th style={th}>{t('p_cust_th_name')}</th><th style={th}>{t('p_cust_th_taxid')}</th>{!plain && <th style={th}>{t('p_cust_th_branch')}</th>}<th style={th}>{t('p_cust_th_email')}</th><th style={th}></th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id}>
            <td style={{ ...td, fontFamily: 'ui-monospace, monospace', color: '#5b6b79', whiteSpace: 'nowrap' }}>{custCode(r.code_no)}</td>
            <td style={td}>{r.name}</td><td style={td}>{r.tax_id}</td>{!plain && <td style={td}>{r.branch_no}</td>}<td style={td}>{r.email}</td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>
              <RowMenu actions={[
                { label: t('p_row_edit'), onClick: () => setEdit({ id: r.id, name: r.name, nameTh: r.name_th, taxId: r.tax_id, branchNo: r.branch_no, address: r.address, email: r.email, phone: r.phone, notes: r.notes || '' }) },
                { label: t('p_row_delete'), danger: true, onClick: async () => { if (window.confirm(t('p_cust_confirm_remove', { name: r.name }))) { await api.portalDelCustomer(r.id); load(); } } },
              ]} />
            </td>
          </tr>))}
          {!rows.length && <tr><td style={{ ...td, color: '#98a4b0' }} colSpan={6}>{q ? t('p_empty_no_match', { query: q }) : t('p_cust_empty')}</td></tr>}
        </tbody>
      </table>
      <Pager page={page} per={per} total={total} onPage={setPage} onPer={setPer} />
      {edit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && setEdit(null)}>
          <div style={{ ...card, width: 460, maxWidth: '92vw' }}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>{edit.id ? t('p_cust_modal_edit') : t('p_cust_modal_new')}</div>
            <label style={lbl}>{t('p_cust_f_name')}</label><input style={inp} value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} />
            {/* The Thai name and the branch serve a Thai tax invoice (s.86/4) — not shown outside Thailand. */}
            {!plain && <><label style={lbl}>{t('p_cust_f_name_th')}</label><input style={inp} value={edit.nameTh || ''} onChange={e => setEdit({ ...edit, nameTh: e.target.value })} /></>}
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 2 }}><label style={lbl}>{t('p_cust_f_taxid')}</label><input style={inp} value={edit.taxId || ''} onChange={e => setEdit({ ...edit, taxId: e.target.value })} /></div>
              {!plain && <div style={{ flex: 1 }}><label style={lbl}>{t('p_cust_f_branch')}</label><input style={inp} value={edit.branchNo || ''} onChange={e => setEdit({ ...edit, branchNo: e.target.value })} /></div>}
            </div>
            <label style={lbl}>{t('p_cust_f_address')}</label><input style={inp} value={edit.address || ''} onChange={e => setEdit({ ...edit, address: e.target.value })} />
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={lbl}>{t('p_cust_f_email')}</label><input style={inp} value={edit.email || ''} onChange={e => setEdit({ ...edit, email: e.target.value })} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>{t('p_cust_f_phone')}</label><input style={inp} value={edit.phone || ''} onChange={e => setEdit({ ...edit, phone: e.target.value })} /></div>
            </div>
            {/* Internal notes — your own reminders about this buyer; never printed on documents. */}
            <label style={lbl}>{t('p_cust_f_notes')} <span style={{ color: '#98a4b0', fontWeight: 400 }}>{t('p_cust_f_notes_hint')}</span></label>
            <textarea style={{ ...inp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} value={edit.notes || ''} onChange={e => setEdit({ ...edit, notes: e.target.value })} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btnGhost} onClick={() => setEdit(null)}>{t('p_btn_cancel')}</button>
              <button style={btn} onClick={save}>{t('p_btn_save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── My Products ──────────────────────────────────────────────────────────────
function Products({ vatRegistered, plain }: { vatRegistered: boolean; plain?: boolean }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<any[]>([]); const [edit, setEdit] = useState<any>(null);
  const [page, setPage] = useState(1); const [per, setPerRaw] = useState(50); const [total, setTotal] = useState(0);
  const setPer = (n: number) => { setPerRaw(n); setPage(1); };
  const [q, setQRaw] = useState('');
  const setQ = (v: string) => { setQRaw(v); setPage(1); };
  const load = (p = page, n = per, term = q) => api.portalProducts(p, n, term)
    .then((d: any) => { setRows(d.rows || []); setTotal(Number(d.total) || 0); }).catch(() => {});
  useEffect(() => { load(page, per, q); }, [page, per, q]); // eslint-disable-line
  useEffect(() => { const pages = Math.max(1, Math.ceil(total / per)); if (page > pages) setPage(pages); }, [total, per, page]);
  const save = async () => {
    try { await api.portalSaveProduct(edit.id || null, edit); setEdit(null); load(); }
    catch (e: any) { alert(e?.message || t('p_err_save_failed')); }
  };
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 800 }}>{t('p_prod_title')}</div>
        <div style={{ flex: 1 }} />
        <SearchBox value={q} onChange={setQ} placeholder={t('p_prod_search_ph')} />
        <button style={btn} onClick={() => setEdit({ name: '', unit: '', unitPrice: 0, vatApplicable: true, whtRate: 0, image: '' })}>{t('p_prod_add')}</button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={{ ...th, width: 46 }}></th><th style={th}>{t('p_prod_th_name')}</th><th style={th}>{t('p_prod_th_unit')}</th><th style={{ ...th, textAlign: 'right' }}>{t('p_prod_th_price')}</th>{vatRegistered && <th style={th}>{t('p_prod_th_vat')}</th>}<th style={th}></th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id}>
            <td style={{ ...td, padding: '4px 8px' }}>{r.image ? <img src={r.image} alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 6, border: '1px solid #e3e7ea', display: 'block' }} /> : <span style={{ display: 'block', width: 34, height: 34, borderRadius: 6, background: '#f2f5f7' }} />}</td>
            <td style={td}>{r.name}</td><td style={td}>{r.unit}</td><td style={{ ...td, textAlign: 'right' }}>{money(r.unit_price, 2, mco())}</td>
            {vatRegistered && <td style={td}>{r.vat_applicable ? t('p_prod_vat_yes', { rate: VAT_RATE }) : '—'}</td>}
            <td style={{ ...td, whiteSpace: 'nowrap' }}>
              <RowMenu actions={[
                { label: t('p_row_edit'), onClick: () => setEdit({ id: r.id, name: r.name, nameTh: r.name_th, unit: r.unit, unitPrice: r.unit_price, vatApplicable: !!r.vat_applicable, whtRate: Number(r.wht_rate) || 0, image: r.image || '' }) },
                { label: t('p_row_delete'), danger: true, onClick: async () => { if (window.confirm(t('p_prod_confirm_remove', { name: r.name }))) { await api.portalDelProduct(r.id); load(); } } },
              ]} />
            </td>
          </tr>))}
          {!rows.length && <tr><td style={{ ...td, color: '#98a4b0' }} colSpan={6}>{q ? t('p_empty_no_match', { query: q }) : t('p_prod_empty')}</td></tr>}
        </tbody>
      </table>
      <Pager page={page} per={per} total={total} onPage={setPage} onPer={setPer} />
      {edit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && setEdit(null)}>
          <div style={{ ...card, width: 420, maxWidth: '92vw' }}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>{edit.id ? t('p_prod_modal_edit') : t('p_prod_modal_new')}</div>
            <label style={lbl}>{t('p_prod_f_name')}</label><input style={inp} value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} />
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={lbl}>{t('p_prod_f_unit')}</label><input style={inp} value={edit.unit || ''} onChange={e => setEdit({ ...edit, unit: e.target.value })} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>{t('p_prod_f_price')}</label><input style={inp} type="number" value={edit.unitPrice} onChange={e => setEdit({ ...edit, unitPrice: e.target.value })} /></div>
            </div>
            {vatRegistered && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 13.5, cursor: 'pointer' }}>
                <input type="checkbox" checked={edit.vatApplicable} onChange={e => setEdit({ ...edit, vatApplicable: e.target.checked })} />
                {t('p_prod_f_vat_checkbox')}
              </label>
            )}
            {/* Goods are 0%; services are usually withheld at 3% (rent 5%, ads/professional fees vary).
                Whatever is set here pre-fills the line when the item is picked on a document. */}
            {!plain && <>
              <label style={lbl}>{t('p_prod_f_wht')}</label>
              {whtSelect(edit.whtRate, v => setEdit({ ...edit, whtRate: v }), inp, t)}
              <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 3 }}>{t('p_prod_f_wht_hint')}</div>
            </>}
            <div style={{ marginTop: 12 }}>
              <ImagePick label={t('p_prod_photo_label')} hint={t('p_prod_photo_hint')} boxW={72} boxH={72} value={edit.image || ''} onChange={v => setEdit({ ...edit, image: v })} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btnGhost} onClick={() => setEdit(null)}>{t('p_btn_cancel')}</button>
              <button style={btn} onClick={save}>{t('p_btn_save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Read an image file into a downscaled data URL. Logos and payment QRs are stored inline on the
// company / bank row (that is what the PDF renderer embeds), so a raw 5 MB phone photo has to be
// resized here or the profile save would be rejected for size.
async function imageToDataUrl(file: File, maxPx: number, t: TFn): Promise<string> {
  const raw = await new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => no(new Error(t('p_img_err_read'))); r.readAsDataURL(file); });
  const img = await new Promise<HTMLImageElement>((ok, no) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => no(new Error(t('p_img_err_not_image'))); i.src = raw; });
  const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
  if (scale >= 1 && raw.length < 300000) return raw;                       // already small enough
  const c = document.createElement('canvas');
  c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
  const ctx = c.getContext('2d'); if (!ctx) return raw;
  ctx.drawImage(img, 0, 0, c.width, c.height);
  const png = c.toDataURL('image/png');                                    // PNG keeps logo/QR edges crisp
  return png.length < 480000 ? png : c.toDataURL('image/jpeg', 0.85);
}
function ImagePick({ label, hint, value, onChange, boxW, boxH }: { label: string; hint?: string; value: string; onChange: (v: string) => void; boxW: number; boxH: number }) {
  const { t } = useTranslation();
  const [err, setErr] = useState('');
  const pick = async (file?: File | null) => {
    if (!file) return;
    setErr('');
    try { onChange(await imageToDataUrl(file, Math.max(boxW, boxH) * 3, t)); }
    catch (e: any) { setErr(e?.message || t('p_img_err_generic')); }
  };
  return (
    <div>
      <label style={lbl}>{label}{hint && <span style={{ color: '#98a4b0', fontWeight: 400 }}> — {hint}</span>}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: boxW, height: boxH, border: '1px dashed #cfd8e0', borderRadius: 8, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
          {value ? <img src={value} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: 11, color: '#98a4b0' }}>{t('p_img_none')}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ ...btnGhost, padding: '6px 12px', cursor: 'pointer' }}>
            {value ? t('p_img_replace') : t('p_img_upload')}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { pick(e.target.files?.[0]); e.currentTarget.value = ''; }} />
          </label>
          {value && <button style={{ ...btnGhost, padding: '6px 12px', color: '#D0021B' }} onClick={() => onChange('')}>{t('p_img_remove')}</button>}
          {err && <span style={{ fontSize: 12, color: '#D0021B' }}>{err}</span>}
        </div>
      </div>
    </div>
  );
}

// ── Company profile ──────────────────────────────────────────────────────────
// The disclaimer stamp is unix SECONDS. Anything else — a datetime string from a hand-edited row,
// a null that survived a migration — makes `v * 1000` NaN, and `new Date(NaN).toISOString()` throws
// a RangeError mid-render. There is no error boundary above this, so that one bad value blanks the
// entire portal for that client. Seen for real on 2026-09-05. Return an empty date instead: the
// numbering section still renders, just without the "accepted on" note.
const acceptedOn = (v: any): string => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '';
  const d = new Date(n * 1000);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

// ── Numbering scheme helpers (mirror services/docNumberScheme.ts) ───────────────────────────────
// A reset needs a date segment fine enough to tell one period from the next: reset monthly with
// only a year in the number and INV-2026-0001 comes round again every month. The server refuses
// those combinations; this shows the operator why before they press Save.
const SCHEME_GRAIN: Record<string, number> = { none: 0, YYYY: 1, YYYYMM: 2, YYYYMMDD: 3 };
const SCHEME_NEEDS: Record<string, number> = { none: 0, year: 1, month: 2, day: 3 };
type UiScheme = { date: string; reset: string; pad: number };
const schemeOf = (f: any, k: string): UiScheme => {
  const s = (f.doc_no_scheme || {})[k];
  // No scheme saved yet ⇒ show what this merchant's numbers look like today, not a new default.
  const legacy: UiScheme = { date: Number(f.doc_no_month_segment ?? 1) ? 'YYYYMM' : 'none', reset: 'none', pad: 3 };
  return s ? { date: s.date || legacy.date, reset: s.reset || 'none', pad: Number(s.pad) || legacy.pad } : legacy;
};
const schemeError = (s: UiScheme, t: TFn): string =>
  (SCHEME_NEEDS[s.reset] || 0) > (SCHEME_GRAIN[s.date] || 0)
    ? t('p_prof_scheme_bad', { need: s.reset === 'year' ? 'YYYY' : s.reset === 'month' ? 'YYYYMM' : 'YYYYMMDD' })
    : '';
const previewNo = (prefix: string, s: UiScheme, run: number): string => {
  const d = new Date(); const p2 = (n: number) => String(n).padStart(2, '0');
  const ymd = `${d.getFullYear()}${p2(d.getMonth() + 1)}${p2(d.getDate())}`;
  const seg = s.date === 'YYYY' ? ymd.slice(0, 4) : s.date === 'YYYYMM' ? ymd.slice(0, 6) : s.date === 'YYYYMMDD' ? ymd : '';
  return `${prefix || '?'}${seg ? `-${seg}` : ''}-${String(run).padStart(s.pad, '0')}`;
};

function Profile({ profile, reload }: { profile: any; reload: () => void }) {
  const { t } = useTranslation();
  const [pwOpen, setPwOpen] = useState(false);
  const [f, setF] = useState<any>({ address: profile.address || '', email: profile.email || '', contact_person: profile.contact_person || '', contact_phone: profile.contact_phone || '', website: profile.website || '', biz_type: profile.biz_type || 'service', doc_prefix_inv: profile.doc_prefix_inv, doc_prefix_quo: profile.doc_prefix_quo, doc_prefix_rec: profile.doc_prefix_rec, doc_prefix_tax: profile.doc_prefix_tax, doc_prefix_pro: profile.doc_prefix_pro, doc_prefix_cn: profile.doc_prefix_cn, doc_prefix_dn: profile.doc_prefix_dn, doc_start_inv: profile.doc_start_inv, doc_start_quo: profile.doc_start_quo, doc_start_rec: profile.doc_start_rec, doc_start_tax: profile.doc_start_tax, doc_start_pro: profile.doc_start_pro, doc_start_cn: profile.doc_start_cn, doc_start_dn: profile.doc_start_dn, doc_no_month_segment: profile.doc_no_month_segment, doc_no_scheme: (() => { try { return JSON.parse(String(profile.doc_no_scheme || '{}')) || {}; } catch { return {}; } })(), logo: profile.logo || '', vat_registered: profile.vat_registered ? 1 : 0, default_wht_rate: Number(profile.default_wht_rate) || 0 });
  const [bank, setBank] = useState<any>(null); const [msg, setMsg] = useState('');
  // Q8: the parts of the email field that are not an address, as the server named them on the last
  // save. A warning only — the value is saved as typed and sending uses the valid addresses.
  const [emailBad, setEmailBad] = useState<string[]>([]);
  const save = async () => {
    try {
      const r: any = await api.portalSaveProfile(f);
      setEmailBad(Array.isArray(r?.emailInvalid) ? r.emailInvalid.map(String) : []);
      setMsg(t('p_prof_saved')); reload(); setTimeout(() => setMsg(''), 1500);
    } catch (e: any) { setMsg(e?.message || t('p_prof_failed')); }
  };
  const ro: React.CSSProperties = { ...inp, background: '#f2f5f7', color: '#5b6b79' };
  // Outside Thailand (merchantMode.ts): no establishment, VAT registration, withholding default or
  // Thai tax-document series — the merchant issues plain documents only.
  const plain = !thaiModeOf(profile);
  const co: string | null = profile?.id || null;   // the merchant company — its own currency is the default (Q11)
  const bankCurrencies = plain ? plainCurrencies(currencyCode(co), COMMON_CURRENCIES, profile.packCurrencies) : COMMON_CURRENCIES;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>{t('p_prof_title')}</div>
        <div style={{ fontSize: 12, color: '#98a4b0' }}>{t('p_prof_identity_note')}</div>
        {plain && <div style={{ fontSize: 12.5, color: '#1d5ba6', background: '#EEF5FD', border: '1px solid #CFE0F5', borderRadius: 8, padding: '8px 11px', marginTop: 8 }}>{t('pi_plain_note')}</div>}
        {/* Read-only like the rest of this block: which office holds the account is the firm's
            record, not a client setting. Stated in full here; the header carries the short badge. */}
        {profile.office?.label && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <span style={{ ...lbl, margin: 0 }}>{t('p_office_label')}</span>
            <OfficeBadge office={profile.office} />
          </div>
        )}
        {/* Wraps since 2026-09-23: the establishment joined this row and five fields do not sit on
            one line on a laptop, let alone a phone. */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 220 }}><label style={lbl}>{t('p_prof_company_name')}</label><input style={ro} readOnly value={profile.name || ''} /></div>
          <div style={{ flex: 1, minWidth: 150 }}><label style={lbl}>{t('p_prof_tax_id')}</label><input style={ro} readOnly value={profile.tax_id || ''} /></div>
          {/* The establishment (s.86/4(2)(3)). It was already stored, already synced from the firm's
              client record alongside the name and tax id, and already printed beside the tax number
              on tax invoices and credit/debit notes — it was simply never shown here, so a client
              had no way to notice it was wrong on documents they are the ones signing. Read-only for
              the same reason the tax id is: it was verified from their company documents. */}
          {!plain && <>
          <div style={{ flex: 1, minWidth: 150 }}><label style={lbl}>{t('p_prof_branch')}</label>
            <input style={ro} readOnly value={profile.branch_no || ''} />
            <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 3 }}>
              <b style={{ fontWeight: 700, color: '#5b6b79' }}>{branchNote(profile.branch_no, t)}</b>
              {' · '}{t('p_prof_branch_hint')}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 150 }}><label style={lbl}>{t('p_prof_vat_reg')}</label>
            <select style={inp} value={String(f.vat_registered)} onChange={e => setF({ ...f, vat_registered: Number(e.target.value) })}>
              <option value="0">{t('p_prof_vat_not_registered')}</option>
              <option value="1">{t('p_prof_vat_registered')}</option>
            </select>
            <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 3 }}>{t('p_prof_vat_hint')}</div>
          </div>
          <div style={{ flex: 1, minWidth: 150 }}><label style={lbl}>{t('p_prof_default_wht')}</label>
            {whtSelect(f.default_wht_rate, v => setF({ ...f, default_wht_rate: v }), inp, t)}
            <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 3 }}>{t('p_prof_default_wht_hint')}</div>
          </div>
          </>}
        </div>
        <div style={{ marginTop: 10 }}>
          <ImagePick label={t('p_prof_logo_label')} hint={t('p_prof_logo_hint')} boxW={150} boxH={56}
            value={f.logo} onChange={v => setF({ ...f, logo: v })} />
        </div>
        <label style={lbl}>{t('p_prof_address')}</label><input style={inp} value={f.address} onChange={e => setF({ ...f, address: e.target.value })} />
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><label style={lbl}>{t('p_prof_email')}</label><input style={inp} value={f.email} onChange={e => { setF({ ...f, email: e.target.value }); setEmailBad([]); }} />
            {emailBad.length > 0 && <div style={{ fontSize: 11.5, color: '#B45309', marginTop: 3 }}>{t('pi_prof_email_invalid', { parts: emailBad.join(', ') })}</div>}
            <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 3 }}>{t('pi_prof_email_hint')}</div></div>
          <div style={{ flex: 1 }}><label style={lbl}>{t('p_prof_phone')}</label><input style={inp} value={f.contact_phone} onChange={e => setF({ ...f, contact_phone: e.target.value })} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>{t('p_prof_website')}</label><input style={inp} value={f.website} onChange={e => setF({ ...f, website: e.target.value })} /></div>
        </div>
        {/* Business type only chooses between Thai tax points (s.78 / s.78/1) — meaningless outside Thailand. */}
        {!plain && <>
        <div style={{ fontSize: 13.5, fontWeight: 800, margin: '16px 0 2px' }}>{t('p_prof_biz_type')}</div>
        <select style={{ ...inp, maxWidth: 460 }} value={f.biz_type} onChange={e => setF({ ...f, biz_type: e.target.value })}>
          <option value="service">{t('p_prof_biz_service')}</option>
          <option value="goods">{t('p_prof_biz_goods')}</option>
          <option value="mixed">{t('p_prof_biz_mixed')}</option>
        </select>
        <div style={{ fontSize: 11.5, color: '#7c8894', marginTop: 4 }}>{t('p_prof_biz_hint')}</div>
        </>}
        <div style={{ fontSize: 13.5, fontWeight: 800, margin: '16px 0 2px' }}>{t('p_prof_numbering')} <span style={{ fontWeight: 400, fontSize: 11.5, color: '#B45309' }}>{t('p_prof_numbering_note', { date: acceptedOn(profile.disclaimer_accepted_at) })}</span></div>
        {/* Per series: prefix, next number, the DATE that goes in the number, how often the run
            RESETS, and how wide it is padded (owner 2026-09-08). The live preview is the whole
            point — a numbering rule is impossible to picture from four dropdowns. */}
        <div style={{ display: 'grid', gap: 8 }}>
          {(plain ? (['inv', 'quo', 'rec'] as const) : (['inv', 'quo', 'rec', 'tax', 'pro', 'cn', 'dn'] as const)).map(k => {
            const label = k === 'inv' ? t('p_prof_doc_inv') : k === 'quo' ? t('p_prof_doc_quo') : k === 'rec' ? t('p_prof_doc_rec')
              : k === 'tax' ? t('p_prof_doc_tax') : k === 'pro' ? t('p_prof_doc_pro') : k === 'cn' ? t('p_prof_doc_cn') : t('p_prof_doc_dn');
            const sc = schemeOf(f, k);
            const setSc = (patch: any) => setF({ ...f, doc_no_scheme: { ...(f.doc_no_scheme || {}), [k]: { ...sc, ...patch } } });
            const start = Math.max(1, Math.floor(Number(f[`doc_start_${k}`]) || 1));
            const bad = schemeError(sc, t);
            return (
              <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap',
                padding: '8px 10px', border: '1px solid #eef2f6', borderRadius: 9, background: '#fbfcfd' }}>
                <div style={{ width: 108, fontSize: 12.5, fontWeight: 700, color: '#5b6b79', paddingBottom: 9 }}>{label}</div>
                <div><label style={{ ...lbl, margin: '0 0 3px' }}>{t('p_prof_f_prefix')}</label>
                  <input style={{ ...inp, width: 92, padding: '6px 9px', fontSize: 13 }} value={f[`doc_prefix_${k}`] || ''}
                    onChange={e => setF({ ...f, [`doc_prefix_${k}`]: e.target.value })} /></div>
                <div><label style={{ ...lbl, margin: '0 0 3px' }}>{t('p_prof_f_next')}</label>
                  <input style={{ ...inp, width: 104, padding: '6px 9px', fontSize: 13 }} type="number" value={f[`doc_start_${k}`] ?? 1}
                    onChange={e => setF({ ...f, [`doc_start_${k}`]: e.target.value })} /></div>
                <div><label style={{ ...lbl, margin: '0 0 3px' }}>{t('p_prof_f_date')}</label>
                  <select style={{ ...inp, width: 138, padding: '6px 9px', fontSize: 13 }} value={sc.date}
                    onChange={e => setSc({ date: e.target.value })}>
                    <option value="none">{t('p_prof_date_none')}</option>
                    <option value="YYYY">YYYY · 2026</option>
                    <option value="YYYYMM">YYYYMM · 202609</option>
                    <option value="YYYYMMDD">YYYYMMDD · 20260908</option>
                  </select></div>
                <div><label style={{ ...lbl, margin: '0 0 3px' }}>{t('p_prof_f_reset')}</label>
                  <select style={{ ...inp, width: 132, padding: '6px 9px', fontSize: 13 }} value={sc.reset}
                    onChange={e => setSc({ reset: e.target.value })}>
                    <option value="none">{t('p_prof_reset_none')}</option>
                    <option value="year">{t('p_prof_reset_year')}</option>
                    <option value="month">{t('p_prof_reset_month')}</option>
                    <option value="day">{t('p_prof_reset_day')}</option>
                  </select></div>
                <div><label style={{ ...lbl, margin: '0 0 3px' }}>{t('p_prof_f_pad')}</label>
                  <select style={{ ...inp, width: 78, padding: '6px 9px', fontSize: 13 }} value={sc.pad}
                    onChange={e => setSc({ pad: Number(e.target.value) })}>
                    {[3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                  </select></div>
                <div style={{ flex: 1, minWidth: 170, paddingBottom: 8 }}>
                  <div style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', color: bad ? '#b91c1c' : '#0E7C66', fontWeight: 700 }}>
                    {previewNo(String(f[`doc_prefix_${k}`] || '').toUpperCase(), sc, start)}
                  </div>
                  {bad && <div style={{ fontSize: 11, color: '#b91c1c', lineHeight: 1.35 }}>{bad}</div>}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 11.5, color: '#7c8894', marginTop: 8, lineHeight: 1.5 }}>{t('p_prof_numbering_hint')}</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}>
          <button style={btn} onClick={save}>{t('p_btn_save_bilingual')}</button>
          <button style={btnGhost} onClick={() => setPwOpen(v => !v)}>{t('p_pw_title_change')}</button>
          {msg && <span style={{ fontSize: 13, color: msg.includes('✓') ? '#15803D' : '#D0021B' }}>{msg}</span>}
        </div>
        {pwOpen && <div style={{ marginTop: 12 }}><ChangePassword first={false} onCancel={() => setPwOpen(false)} onDone={() => { setPwOpen(false); setMsg(t('p_prof_pw_changed')); }} /></div>}
      </div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{t('p_bank_title')}</div>
          <button style={btn} onClick={() => setBank({ currency: currencyCode(co), bankName: '', bankFullAddress: '', swiftCode: '', accountName: '', accountNumber: '', remark: '', qrCode: '' })}>{t('p_bank_add')}</button>
        </div>
        {(profile.banks || []).map((b: any) => (
          <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '9px 0', borderBottom: '1px solid #eef1f4', fontSize: 13.5 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: 5, padding: '1px 6px' }}>{b.currency || currencyCode(co)}</span>
                <b>{b.account_number}</b><span style={{ color: '#5b6b79' }}>{b.account_name}</span>
              </div>
              <div style={{ color: '#5b6b79', marginTop: 3 }}>{b.bank_address}{b.swift_code ? ` · SWIFT ${b.swift_code}` : ''}</div>
              {b.bank_full_address && <div style={{ color: '#98a4b0', fontSize: 12.5, marginTop: 2 }}>{b.bank_full_address}</div>}
              {!b.swift_code && <div style={{ color: '#B54708', fontSize: 12, marginTop: 3 }}>{t('p_bank_no_swift')}</div>}
            </div>
            <div style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap', alignItems: 'center' }}>
              {b.qr_code && <img src={b.qr_code} alt={t('p_bank_qr_alt')} title={t('p_bank_qr_title')} style={{ width: 34, height: 34, objectFit: 'contain', border: '1px solid #e3e7ea', borderRadius: 5 }} />}
              <button style={{ ...btnGhost, padding: '4px 10px' }} onClick={() => setBank({ id: b.id, currency: b.currency || currencyCode(co), bankName: b.bank_address || '', bankFullAddress: b.bank_full_address || '', swiftCode: b.swift_code || '', accountName: b.account_name || '', accountNumber: b.account_number || '', remark: b.remark || '', qrCode: b.qr_code || '' })}>{t('p_row_edit')}</button>
              <button style={{ ...btnGhost, padding: '4px 10px', color: '#D0021B' }} onClick={async () => { if (window.confirm(t('p_bank_confirm_remove'))) { await api.portalDelBank(b.id); reload(); } }}>{t('p_row_delete')}</button>
            </div>
          </div>
        ))}
        {!(profile.banks || []).length && <div style={{ fontSize: 13, color: '#98a4b0' }}>{t('p_bank_empty')}</div>}
        {bank && (
          <div style={{ marginTop: 12, padding: 12, background: '#f6f9fb', borderRadius: 8 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ width: 110 }}><label style={lbl}>{t('p_bank_currency')}</label>
                <select style={inp} value={bank.currency} onChange={e => setBank({ ...bank, currency: e.target.value })}>
                  {bankCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div style={{ flex: 1, minWidth: 180 }}><label style={lbl}>{t('p_bank_name')}</label><input style={inp} value={bank.bankName} onChange={e => setBank({ ...bank, bankName: e.target.value })} placeholder={plain ? '' : t('p_bank_name_ph')} /></div>
              <div style={{ width: 160 }}><label style={lbl}>{t('p_bank_swift')}</label><input style={inp} value={bank.swiftCode} onChange={e => setBank({ ...bank, swiftCode: e.target.value.toUpperCase() })} placeholder={plain ? '' : t('p_bank_swift_ph')} /></div>
            </div>
            <div style={{ marginTop: 10 }}><label style={lbl}>{t('p_bank_address')} <span style={{ color: '#98a4b0', fontWeight: 400 }}>{t('p_bank_address_hint')}</span></label>
              <input style={inp} value={bank.bankFullAddress} onChange={e => setBank({ ...bank, bankFullAddress: e.target.value })} placeholder={plain ? '' : t('p_bank_address_ph')} /></div>
            <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 180 }}><label style={lbl}>{t('p_bank_acct_name')}</label><input style={inp} value={bank.accountName} onChange={e => setBank({ ...bank, accountName: e.target.value })} /></div>
              <div style={{ flex: 1, minWidth: 150 }}><label style={lbl}>{t('p_bank_acct_no')}</label><input style={inp} value={bank.accountNumber} onChange={e => setBank({ ...bank, accountNumber: e.target.value })} /></div>
              <div style={{ flex: 1, minWidth: 150 }}><label style={lbl}>{t('p_bank_note')}</label><input style={inp} value={bank.remark} onChange={e => setBank({ ...bank, remark: e.target.value })} placeholder={t('p_bank_note_ph')} /></div>
            </div>
            <div style={{ marginTop: 12 }}>
              {/* The hint and placeholders above are Thai examples (a Bangkok branch, PromptPay) — not shown outside Thailand. */}
              <ImagePick label={t('p_bank_qr_label')} hint={plain ? undefined : t('p_bank_qr_hint')} boxW={96} boxH={96}
                value={bank.qrCode || ''} onChange={v => setBank({ ...bank, qrCode: v })} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
              <button style={btnGhost} onClick={() => setBank(null)}>{t('p_btn_cancel')}</button>
              <button style={btn} onClick={async () => { if (bank.id) await api.portalSaveBank(bank.id, bank); else await api.portalAddBank(bank); setBank(null); reload(); }}>{t('p_btn_save')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shell ────────────────────────────────────────────────────────────────────
// Two families of tabs: the client's OWN sales documents, and — after the divider — what SLF holds
// and did for them. Keeping them visually apart is the whole point: "my invoice to my customer" and
// "SLF's invoice to me" are different documents that would otherwise read as the same list.
// ── Jobs from SLF (read-only) ────────────────────────────────────────────────
// What the firm is doing for this client. Every field here is on the allow-list the owner signed
// off on 2026-09-21; the server decides what exists, this only lays it out.
//
// Fifteen fields across a flat table would scroll off any screen and read as a spreadsheet, so the
// table carries the seven a client scans for — number, what it is, whose move, when, and the three
// money figures — and the rest hang off the job name where they answer a follow-up question
// instead of competing with the scan.
//
// `status` arrives as one of seven bucket KEYS, never a raw staff status. The mapping lives on the
// server (services/jobStatusPublic.ts) so "Awaiting Boom" cannot reach a browser even by mistake.
const JOB_STATUSES = ['not_started', 'in_progress', 'with_you', 'with_authority', 'with_third_party', 'done', 'cancelled'] as const;
// with_you is the one bucket that asks the client to act, so it is the one that gets a colour that
// carries urgency. Everything else stays quiet — a wall of coloured pills communicates nothing.
const JOB_STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  not_started:      { bg: '#eef2f5', fg: '#5b6b79' },
  in_progress:      { bg: '#e6f0fb', fg: '#1d5ba6' },
  with_you:         { bg: '#fdf0d9', fg: '#8a5a06' },
  with_authority:   { bg: '#ede9fb', fg: '#5b4ba6' },
  with_third_party: { bg: '#eef2f5', fg: '#5b6b79' },
  done:             { bg: '#e6f4ee', fg: '#0E7C66' },
  cancelled:        { bg: '#f4f4f5', fg: '#8a8f94' },
};

/** Unix seconds → `YYYY-MM-DD` in the tenant's zone (utils/region.ts). The portal had no epoch formatter; `money()` and
 *  `today()` were the only shared ones. Returns '' for 0/null so callers render an em dash. */
const bkkDay = (secs: any): string => {
  const n = Number(secs);
  if (!Number.isFinite(n) || n <= 0) return '';
  return todayISO(new Date(n * 1000));
};

function JobsFromSlf() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<any[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(50);
  const [only, setOnly] = useState<string>('');      // '' = every bucket
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    setBusy(true);
    api.portalJobs(page, per)
      .then((d: any) => { setRows(d.rows || []); setTotal(d.total || 0); setCounts(d.counts || {}); })
      .catch(() => { setRows([]); setTotal(0); setCounts({}); })
      .finally(() => setBusy(false));
  }, [page, per]);

  // Filtering is client-side on the loaded page, but the chip COUNTS come from the server and cover
  // every job — so a chip never claims a number this page happens to hold.
  const shown = only ? rows.filter(r => r.status === only) : rows;
  const chip = (k: string, label: string, n: number) => (
    <button key={k} onClick={() => setOnly(only === k ? '' : k)}
      style={{ ...btnGhost, padding: '6px 14px',
        background: only === k ? ACCENT : '#eef2f5', color: only === k ? '#fff' : '#333' }}>
      {label} ({n})
    </button>
  );

  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_jobs_title')}</div>
      <div style={{ fontSize: 12.5, color: '#7c8894', margin: '4px 0 14px' }}>{t('p_jobs_subtitle')}</div>

      {!busy && total > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {chip('', t('p_jobs_all'), total)}
          {JOB_STATUSES.filter(k => (counts[k] || 0) > 0).map(k => chip(k, t(`p_jobs_st_${k}`), counts[k] || 0))}
        </div>
      )}

      {busy ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_jobs_loading')}</div>
        : !shown.length ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_doc_empty')}</div> : (
        <>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
            <thead><tr>
              <th style={th}>{t('p_jobs_no')}</th>
              <th style={th}>{t('p_jobs_what')}</th>
              <th style={th}>{t('p_jobs_status')}</th>
              <th style={th}>{t('p_jobs_due_date')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_jobs_amount')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_jobs_paid')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_jobs_outstanding')}</th>
            </tr></thead>
            <tbody>
              {shown.map(r => {
                const st = JOB_STATUS_STYLE[r.status] || JOB_STATUS_STYLE.in_progress;
                // A finished job's own completion date is more informative than the estimate it beat
                // or missed, so it replaces the target once there is one.
                const when = r.completedAt ? bkkDay(r.completedAt)
                  : r.finishUnknown ? t('p_jobs_date_tbc')
                  : (r.finishDate || '');
                return (
                  <tr key={r.jobNo}>
                    <td style={{ ...td, fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>#{r.jobNo}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.job || '—'}</div>
                      {/* The secondary fields: present when they say something, absent when they don't. */}
                      <div style={{ fontSize: 12, color: '#7c8894', marginTop: 2 }}>
                        {[r.service, r.firm, r.contact].filter(Boolean).join(' · ') || ' '}
                      </div>
                      {!!(r.prereqs || []).length && (
                        <div style={{ fontSize: 12, color: '#8a5a06', marginTop: 3 }}>
                          {t('p_jobs_waiting_on')} {r.prereqs.map((p: any) => `#${p.jobNo} ${p.job}`).join('; ')}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: '#a8b2ba', marginTop: 3 }}>
                        {t('p_jobs_opened')} {bkkDay(r.createdAt) || '—'}
                        {r.updatedAt ? ` · ${t('p_jobs_updated')} ${bkkDay(r.updatedAt)}` : ''}
                      </div>
                    </td>
                    <td style={td}>
                      <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 20,
                        fontSize: 12, fontWeight: 700, background: st.bg, color: st.fg, whiteSpace: 'nowrap' }}>
                        {t(`p_jobs_st_${r.status}`)}
                      </span>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{when || '—'}</td>
                    {/* The firm's amounts, in the firm's region (#58). */}
                    <td style={{ ...td, textAlign: 'right' }}>{r.amount ? money(r.amount, 2, r.firmId) : '—'}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#5b6b79' }}>{r.paid ? money(r.paid, 2, r.firmId) : '—'}</td>
                    {/* A cancelled job still carries whatever balance it had when it was cancelled, and
                        the server reports it honestly. Emphasising it would read as a demand for money
                        on work that is not happening, so only a LIVE balance is highlighted; on a
                        cancelled row the same figure stays muted and unbolded. */}
                    <td style={{ ...td, textAlign: 'right',
                                 fontWeight: r.due > 0 && r.status !== 'cancelled' ? 700 : 400,
                                 color: r.due > 0 && r.status !== 'cancelled' ? '#8a5a06' : '#98a4b0' }}>
                      {r.due ? money(r.due, 2, r.firmId) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pager page={page} per={per} total={total} onPage={setPage} onPer={setPer} />
        </>
      )}
    </div>
  );
}

// ── What SLF does (read-only) ────────────────────────────────────────────────
// The catalogue is 142 entries, which is a list nobody reads top to bottom — so the page is built
// around finding rather than browsing: the client's own services first, a search box that filters
// as you type, and everything else grouped by which office provides it.
//
// Each service opens to reveal its workflow. That is the part worth having: the firm already
// records, per service, the steps a job goes through and WHICH SIDE owns each one. "What happens
// after I say yes" is the question a services page is actually asked, and this one can answer it.
function ServicesFromSlf() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<any[]>([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string>('');
  const [steps, setSteps] = useState<Record<string, any[]>>({});
  useEffect(() => {
    api.portalServices()
      .then((d: any) => setGroups(d.groups || []))
      .catch(() => setGroups([]))
      .finally(() => setBusy(false));
  }, []);

  const toggle = (id: string) => {
    if (open === id) { setOpen(''); return; }
    setOpen(id);
    if (steps[id]) return;                       // already loaded — the flow does not change under them
    api.portalServiceSteps(id)
      .then((d: any) => setSteps(s => ({ ...s, [id]: d.steps || [] })))
      .catch(() => setSteps(s => ({ ...s, [id]: [] })));
  };

  const needle = q.trim().toLowerCase();
  const match = (s: any) => !needle
    || String(s.name || '').toLowerCase().includes(needle)
    || String(s.description || '').toLowerCase().includes(needle);
  // The client's own services lead each group; the rest keep the server's alphabetical order.
  const sorted = (list: any[]) => [...list].sort((a, b) => (b.yours ? 1 : 0) - (a.yours ? 1 : 0));
  const visible = groups
    .map(g => ({ ...g, services: sorted(g.services.filter(match)) }))
    .filter(g => g.services.length);
  const hits = visible.reduce((n, g) => n + g.services.length, 0);

  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_svc_title')}</div>
      <div style={{ fontSize: 12.5, color: '#7c8894', margin: '4px 0 14px' }}>{t('p_svc_subtitle')}</div>

      {!busy && (
        <input style={{ ...inp, maxWidth: 380, marginBottom: 14 }} value={q}
          onChange={e => setQ(e.target.value)} placeholder={t('p_svc_search')} />
      )}

      {busy ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_svc_loading')}</div>
        : !hits ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_svc_none')}</div> : (
        visible.map(g => (
          <div key={g.firm} style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: ACCENT, margin: '0 0 8px',
                          borderBottom: '2px solid #e3e7ea', paddingBottom: 6 }}>
              {g.firm} <span style={{ fontWeight: 500, color: '#98a4b0' }}>({g.services.length})</span>
            </div>
            {g.services.map((s: any) => {
              const isOpen = open === s.id;
              const st = steps[s.id];
              return (
                <div key={s.id} style={{ borderBottom: '1px solid #eef1f4' }}>
                  <button onClick={() => toggle(s.id)}
                    style={{ display: 'flex', width: '100%', gap: 12, alignItems: 'baseline',
                             background: 'none', border: 'none', padding: '11px 2px', cursor: 'pointer',
                             textAlign: 'left', font: 'inherit' }}>
                    <span style={{ color: '#98a4b0', fontSize: 11, width: 12, flex: 'none' }}>{isOpen ? '▾' : '▸'}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#27303B' }}>{s.name}</span>
                      {s.yours && (
                        <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 20, fontSize: 11,
                                       fontWeight: 700, background: '#e6f4ee', color: ACCENT }}>
                          {t('p_svc_yours')}
                        </span>
                      )}
                      {!!s.description && (
                        <span style={{ display: 'block', fontSize: 12.5, color: '#7c8894', marginTop: 3 }}>
                          {s.description}
                        </span>
                      )}
                    </span>
                    <span style={{ flex: 'none', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: '#27303B' }}>
                        {s.price ? `${money(s.price, 2, s.firmId)} ${s.currency}` : '—'}
                      </span>
                      {/* A negotiated price and a list price are different claims; say which this is. */}
                      <span style={{ display: 'block', fontSize: 11, color: s.priceIsYours ? ACCENT : '#a8b2ba' }}>
                        {s.price ? (s.priceIsYours ? t('p_svc_your_price') : t('p_svc_list_price')) : ' '}
                        {s.unit ? ` / ${s.unit}` : ''}
                      </span>
                    </span>
                  </button>

                  {isOpen && (
                    <div style={{ padding: '2px 0 16px 26px' }}>
                      {!st ? <div style={{ color: '#98a4b0', fontSize: 12.5 }}>{t('p_svc_steps_loading')}</div>
                        : !st.length ? <div style={{ color: '#98a4b0', fontSize: 12.5 }}>{t('p_svc_no_steps')}</div> : (
                        <>
                          <div style={{ fontSize: 12, color: '#7c8894', marginBottom: 8 }}>{t('p_svc_how')}</div>
                          {st.map((x: any) => (
                            <div key={x.no} style={{ display: 'flex', gap: 10, marginBottom: 9 }}>
                              {/* The step's number, trimmed to its cap height so the flex box centres the digits
                                  themselves (Bugify #836d4443, 2nd opening) */}
                              <span style={{ flex: 'none', width: 20, height: 20, borderRadius: 10, fontSize: 11,
                                             fontWeight: 700, display: 'inline-flex', alignItems: 'center',
                                             justifyContent: 'center',
                                             background: x.who === 'you' ? '#fdf0d9' : '#eef2f5',
                                             color: x.who === 'you' ? '#8a5a06' : '#5b6b79' }}>
                                <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{x.no}</span>
                              </span>
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: 13, fontWeight: 600 }}>{x.title}</span>
                                <span style={{ marginLeft: 7, fontSize: 11, fontWeight: 700,
                                               color: x.who === 'you' ? '#8a5a06' : '#98a4b0' }}>
                                  {x.who === 'you' ? t('p_svc_step_you') : t('p_svc_step_slf')}
                                </span>
                                {!!x.detail && (
                                  <span style={{ display: 'block', fontSize: 12.5, color: '#7c8894', marginTop: 2 }}>
                                    {x.detail}
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}

// ── PandaAI Tax (read-only) ──────────────────────────────────────────────────
// Nine filings down the left, the selected one on the right. A form the client has nothing under is
// GREYED, never hidden: "we file nothing for you under PND.50" is information, an absent row is not.
//
// The two payment legs are rendered as two separate boxes and never combined. That is the firm's own
// rule (services/clientTaxPaid.ts): one says the money reached SLF, the other says the tax reached
// the Revenue Department. A client who has paid us has NOT thereby had their tax filed, and one
// merged "paid" tick would tell them something untrue about their own liability.
const TAX_MONTHS = [1,2,3,4,5,6,7,8,9,10,11,12];

function TaxFromSlf() {
  const { t } = useTranslation();
  const [forms, setForms] = useState<any[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [sel, setSel] = useState<string>('');
  const [year, setYear] = useState<number>(0);
  const [month, setMonth] = useState<number>(0);
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.portalTaxForms().then((d: any) => {
      const fs = d.forms || [];
      setForms(fs); setYears(d.years || []);
      setYear((d.years || [])[0] || new Date().getFullYear());
      // Open on the first form that actually has something, rather than on a grey row.
      setSel((fs.find((f: any) => f.available) || fs[0] || {}).key || '');
    }).catch(() => setForms([])).finally(() => setBusy(false));
  }, []);

  const form = forms.find(f => f.key === sel);
  useEffect(() => {
    if (!sel || !year || !form?.available) { setData(null); return; }
    setLoading(true);
    api.portalTax(sel, year, form.period === 'month' ? month : 0)
      .then((d: any) => { setData(d); if (form.period === 'month' && !month) setMonth(d.month || 0); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [sel, year, month, form?.available, form?.period]); // eslint-disable-line

  const pick = (k: string) => { setSel(k); setMonth(0); setData(null); };
  const line = (label: string, node: React.ReactNode, strong = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '6px 0',
                  borderTop: strong ? '1px solid #d3dbe2' : '1px solid #f2f5f7' }}>
      <span style={{ fontSize: 13, color: strong ? '#27303B' : '#5b6b79', fontWeight: strong ? 700 : 400 }}>{label}</span>
      <span style={{ fontSize: 13.5, fontWeight: strong ? 800 : 500, fontVariantNumeric: 'tabular-nums' }}>{node}</span>
    </div>
  );

  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_tax_title')}</div>
      <div style={{ fontSize: 12.5, color: '#7c8894', margin: '4px 0 14px' }}>{t('p_tax_subtitle')}</div>

      {busy ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_tax_loading')}</div> : (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* Left: the nine filings */}
          <div style={{ flex: '0 0 210px', minWidth: 190 }}>
            {forms.map(f => {
              const on = f.key === sel;
              return (
                <button key={f.key} onClick={() => f.available && pick(f.key)}
                  disabled={!f.available}
                  title={f.available ? '' : t(f.note === 'not_configured' ? 'p_tax_not_configured' : 'p_tax_nothing_filed')}
                  style={{ display: 'block', width: '100%', textAlign: 'left', font: 'inherit',
                           padding: '9px 11px', marginBottom: 3, borderRadius: 8, border: 'none',
                           cursor: f.available ? 'pointer' : 'not-allowed',
                           background: on ? '#e6f4ee' : 'transparent',
                           color: !f.available ? '#c2cbd2' : on ? ACCENT : '#27303B',
                           fontWeight: on ? 800 : 500, fontSize: 13.5 }}>
                  {f.label}
                  {!f.available && (
                    <span style={{ display: 'block', fontSize: 11, color: '#c2cbd2', fontWeight: 400, marginTop: 1 }}>
                      {t(f.note === 'not_configured' ? 'p_tax_not_configured' : 'p_tax_nothing_filed')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right: the selected filing */}
          <div style={{ flex: 1, minWidth: 300 }}>
            {!form?.available ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_tax_pick')}</div> : (
            <>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                {years.length > 1 && (
                  <select value={year} onChange={e => { setYear(Number(e.target.value)); setMonth(0); }}
                    style={{ ...inp, width: 'auto', padding: '6px 10px' }}>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                )}
                {/* Monthly forms only. PND.51 is filed once per half-year and PND.50 / PND.90-91 once
                    per year, so a month picker on them would be inviting a choice that does not exist. */}
                {form.period === 'month' && TAX_MONTHS.map(m => (
                  <button key={m} onClick={() => setMonth(m)}
                    style={{ ...btnGhost, padding: '5px 10px', fontSize: 12.5,
                             background: (month || data?.month) === m ? ACCENT : '#eef2f5',
                             color: (month || data?.month) === m ? '#fff' : '#333' }}>{m}</button>
                ))}
                {form.period === 'half' && <span style={{ fontSize: 12.5, color: '#7c8894' }}>{t('p_tax_half_year')}</span>}
                {form.period === 'year' && <span style={{ fontSize: 12.5, color: '#7c8894' }}>{t('p_tax_annual')}</span>}
              </div>

              {loading ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_tax_loading')}</div>
                : !data ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_doc_empty')}</div> : (
                <>
                  {/* Calculation */}
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#5b6b79', marginBottom: 4 }}>{t('p_tax_calc')}</div>
                  {!data.calc.length ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_tax_no_calc')}</div> : (
                    <div style={{ marginBottom: 14 }}>
                      {data.calc.map((l: any, i: number) => l.label === 'payee'
                        ? line(`${l.name} · ${l.kind} · ${money(l.gross)} × ${l.rate}%`, money(l.value))
                        : line(t(`p_tax_l_${l.label}`), l.plain ? l.value : money(l.value), !!l.strong))}
                      {line(t('p_tax_amount'), `${money(data.amount)} ${currencyCode()}`, true)}
                    </div>
                  )}

                  {/* The two payment legs — deliberately two boxes, never one tick */}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                    <div style={{ flex: '1 1 210px', border: '1px solid #e3e7ea', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#5b6b79' }}>{t('p_tax_you_paid_slf')}</div>
                      {!data.clientPaid || !data.clientPaid.expected ? (
                        <div style={{ fontSize: 13, color: '#98a4b0', marginTop: 5 }}>{t('p_tax_nothing_billed')}</div>
                      ) : data.clientPaid.complete ? (
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: ACCENT, marginTop: 5 }}>
                          {t('p_tax_settled')} · {data.clientPaid.on}
                        </div>
                      ) : (
                        // clientTaxPaid deliberately withholds a DATE on a part payment: a date on a
                        // half-paid month reads as "settled". Show the shortfall instead.
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#8a5a06', marginTop: 5 }}>
                          {t('p_tax_outstanding')} {money(data.clientPaid.outstanding)}
                        </div>
                      )}
                    </div>
                    <div style={{ flex: '1 1 210px', border: '1px solid #e3e7ea', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#5b6b79' }}>{t('p_tax_slf_paid_rd')}</div>
                      <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 5,
                                    color: data.remitted?.remitted ? ACCENT : '#98a4b0' }}>
                        {data.remitted?.remitted ? `${t('p_tax_remitted')} · ${data.remitted.on}` : t('p_tax_not_remitted')}
                      </div>
                      {data.filed && (
                        <div style={{ fontSize: 11.5, color: '#7c8894', marginTop: 3 }}>{t('p_tax_filed')}</div>
                      )}
                    </div>
                  </div>

                  {/* Payment method is absent from the schema; the page says so rather than guessing. */}
                  {line(t('p_tax_method'), <span style={{ color: '#98a4b0', fontWeight: 400 }}>{t('p_tax_method_na')}</span>)}

                  {/* Filed paperwork */}
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#5b6b79', margin: '16px 0 4px' }}>{t('p_tax_docs')}</div>
                  {!data.docs.length ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_tax_no_docs')}</div> : (
                    // A plain href cannot work here: the endpoint wants the Bearer token and a
                    // browser navigation sends no headers, so the file came back Unauthorized.
                    // openAuthed fetches it with the token and opens the blob — the same path the
                    // portal's own PDFs already take.
                    data.docs.map((d: any) => (
                      <button key={d.id} onClick={() => openAuthed(api.portalFilingDocUrl(d.id), d.filename, t)}
                        style={{ display: 'block', width: '100%', textAlign: 'left', font: 'inherit',
                                 fontSize: 13.5, color: ACCENT, padding: '5px 0', cursor: 'pointer',
                                 borderTop: '1px solid #f2f5f7', border: 'none', background: 'none',
                                 borderTopStyle: 'solid', borderTopWidth: 1, borderTopColor: '#f2f5f7' }}>
                        {d.receipt ? '🧾' : '📄'} {d.label} · {d.filename}
                      </button>
                    ))
                  )}
                </>
              )}
            </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── PandaAI HR ───────────────────────────────────────────────────────────────
// The client's own staff, what each costs in social security, and a way to ASK for a change.
// Nothing here edits the roster: headcount and salary decide the PND.1 and SSO returns SLF files in
// its own name, and a filed month is frozen, so the portal records an intention and a person at SLF
// applies it. The submit button says "Request", not "Save", because that is what it does.
const HR_KINDS = ['salary_change', 'employee_add', 'employee_leave'] as const;

function HrPage() {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [reqs, setReqs] = useState<any[]>([]);
  const [busy, setBusy] = useState(true);
  const [year, setYear] = useState(0);
  const [month, setMonth] = useState(0);
  const [form, setForm] = useState<any>(null);      // null = closed
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = (y = year, m = month) => {
    setBusy(true);
    Promise.all([api.portalHr(y, m), api.portalHrRequests()])
      .then(([hr, rs]: any[]) => {
        setD(hr); setReqs(rs.rows || []);
        if (!y) { setYear(hr.year); setMonth(hr.month); }
      })
      .catch(() => setD(null))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(0, 0); }, []); // eslint-disable-line

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      await api.portalHrRequest(form.kind, form.empId || '', form.payload);
      setForm(null);
      load();
    } catch (e: any) {
      setErr(String(e?.message || '').replace(/^\w+:\s*/, '') || t('p_hr_err'));
    } finally { setSaving(false); }
  };

  const emp = (id: string) => (d?.employees || []).find((e: any) => e.id === id);
  const moverOf = (id: string) => (d?.movers || []).find((m: any) => m.empId === id);

  if (busy && !d) return <div style={card}><div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_hr_loading')}</div></div>;
  if (!d) return <div style={card}><div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_doc_empty')}</div></div>;

  const cfg = d.config || { rate: 5, cap: 875, floor: 1650 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Roster + request */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_hr_title')}</div>
            <div style={{ fontSize: 12.5, color: '#7c8894', marginTop: 4 }}>{t('p_hr_subtitle')}</div>
          </div>
          <button style={btn} onClick={() => setForm({ kind: 'employee_add', empId: '', payload: {} })}>
            {t('p_hr_new_request')}
          </button>
        </div>

        {!!d.pending && (
          <div style={{ marginTop: 12, padding: '9px 12px', borderRadius: 8, background: '#fdf0d9',
                        color: '#8a5a06', fontSize: 13, fontWeight: 600 }}>
            {t('p_hr_pending_note', { count: d.pending })}
          </div>
        )}

        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead><tr>
              <th style={th}>{t('p_hr_name')}</th>
              <th style={th}>{t('p_hr_position')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_hr_salary')}</th>
              <th style={th}>{t('p_hr_joined')}</th>
              <th style={th}>{t('p_hr_status')}</th>
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {!d.employees.length ? (
                <tr><td style={{ ...td, color: '#98a4b0' }} colSpan={6}>{t('p_hr_no_staff')}</td></tr>
              ) : d.employees.map((e: any) => {
                const mv = moverOf(e.id);
                return (
                  <tr key={e.id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{e.name || '—'}</div>
                      <div style={{ fontSize: 11.5, color: '#a8b2ba' }}>
                        {e.nationality}{e.idTail ? ` · ····${e.idTail}` : ''}
                      </div>
                    </td>
                    <td style={td}>{e.position || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{e.salary ? money(e.salary) : '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{e.joined || '—'}</td>
                    <td style={td}>
                      {e.left ? <span style={{ color: '#8a5a06', fontWeight: 600 }}>{t('p_hr_left')} {e.left}</span>
                        : !e.inSso ? <span style={{ color: '#7c8894' }}>{t('p_hr_not_in_sso')}</span>
                        : <span style={{ color: ACCENT }}>{t('p_hr_active')}</span>}
                      {mv && (
                        <div style={{ fontSize: 11.5, color: '#8a5a06', marginTop: 2 }}>
                          {mv.joined ? t('p_hr_joined_this_month') : t('p_hr_left_this_month')}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {!e.left && (
                        <>
                          <button style={{ ...btnGhost, padding: '4px 9px', fontSize: 12 }}
                            onClick={() => setForm({ kind: 'salary_change', empId: e.id, payload: { salary: e.salary } })}>
                            {t('p_hr_change_salary')}
                          </button>
                          <button style={{ ...btnGhost, padding: '4px 9px', fontSize: 12, marginLeft: 5 }}
                            onClick={() => setForm({ kind: 'employee_leave', empId: e.id, payload: {} })}>
                            {t('p_hr_leaving')}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* SSO — the explanation comes FIRST, because a number nobody can check is not information */}
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_hr_sso_title')}</div>
        <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, background: '#f0f5f3',
                      fontSize: 12.5, color: '#3d5c50', lineHeight: 1.7 }}>
          {t('p_hr_sso_how', { rate: cfg.rate, floor: money(cfg.floor), cap: money(cfg.cap) })}
        </div>

        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', margin: '12px 0' }}>
          {TAX_MONTHS.map(m => (
            <button key={m} onClick={() => { setMonth(m); load(year, m); }}
              style={{ ...btnGhost, padding: '5px 10px', fontSize: 12.5,
                       background: month === m ? ACCENT : '#eef2f5', color: month === m ? '#fff' : '#333' }}>{m}</button>
          ))}
        </div>

        {!!(d.movers || []).length && (
          <div style={{ padding: '9px 12px', borderRadius: 8, background: '#fdf0d9', color: '#8a5a06',
                        fontSize: 12.5, marginBottom: 10, lineHeight: 1.6 }}>
            <b>{t('p_hr_check_these')}</b>{' '}
            {d.movers.map((m: any) => `${m.name} (${m.joined ? `${t('p_hr_joined')} ${m.joined}` : `${t('p_hr_left')} ${m.left}`})`).join(' · ')}
            <div style={{ marginTop: 3 }}>{t('p_hr_mover_note')}</div>
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
            <thead><tr>
              <th style={th}>{t('p_hr_name')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_hr_salary')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_hr_employee_side')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_hr_employer_side')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_hr_total')}</th>
            </tr></thead>
            <tbody>
              {!d.current.lines.length ? (
                <tr><td style={{ ...td, color: '#98a4b0' }} colSpan={5}>{t('p_hr_no_sso')}</td></tr>
              ) : <>
                {d.current.lines.map((l: any) => (
                  <tr key={l.empId || l.name}>
                    <td style={td}>{l.name}{l.manual && <span style={{ fontSize: 11, color: '#8a5a06', marginLeft: 6 }}>{t('p_hr_manual')}</span>}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(l.wage)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(l.employee)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(l.employer)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(l.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...td, fontWeight: 800, borderTop: '2px solid #e3e7ea' }}>
                    {t('p_hr_month_total', { count: d.current.totals.count })}
                  </td>
                  <td style={{ ...td, textAlign: 'right', borderTop: '2px solid #e3e7ea' }}>{money(d.current.totals.wage)}</td>
                  <td style={{ ...td, textAlign: 'right', borderTop: '2px solid #e3e7ea' }}>{money(d.current.totals.employee)}</td>
                  <td style={{ ...td, textAlign: 'right', borderTop: '2px solid #e3e7ea' }}>{money(d.current.totals.employer)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800, borderTop: '2px solid #e3e7ea' }}>{money(d.current.totals.total)}</td>
                </tr>
              </>}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11.5, color: '#a8b2ba', marginTop: 6 }}>
          {d.current.snapshot ? t('p_hr_snapshot') : t('p_hr_preview')}
        </div>
      </div>

      {/* January to December */}
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_hr_year_title', { year })}</div>
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead><tr>
              <th style={th}>{t('p_hr_month')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_hr_headcount')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_hr_wages')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_hr_total')}</th>
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {d.months.map((m: any) => (
                <tr key={m.month} style={{ background: m.month === month ? '#f0f5f3' : undefined }}>
                  <td style={td}>{m.month}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{m.count || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{m.wage ? money(m.wage) : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{m.total ? money(m.total) : '—'}</td>
                  <td style={{ ...td, fontSize: 11.5, color: '#a8b2ba' }}>
                    {m.count ? (m.snapshot ? t('p_hr_filed_snap') : t('p_hr_preview_short')) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Requests already made */}
      {!!reqs.length && (
        <div style={card}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_hr_requests')}</div>
          <div style={{ marginTop: 10 }}>
            {reqs.map(r => (
              <div key={r.id} style={{ display: 'flex', gap: 12, padding: '8px 0', borderTop: '1px solid #eef1f4',
                                       alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, flex: 1, minWidth: 180 }}>
                  {t(`p_hr_k_${r.kind}`)}
                  {r.empId && emp(r.empId) ? ` · ${emp(r.empId).name}` : r.payload?.name ? ` · ${r.payload.name}` : ''}
                  {r.payload?.salary ? ` · ${money(r.payload.salary)}` : ''}
                </span>
                <span style={{ fontSize: 12, padding: '2px 9px', borderRadius: 20, fontWeight: 700,
                               background: r.status === 'applied' ? '#e6f4ee' : r.status === 'rejected' ? '#fbeae8' : '#fdf0d9',
                               color: r.status === 'applied' ? ACCENT : r.status === 'rejected' ? '#a32018' : '#8a5a06' }}>
                  {t(`p_hr_s_${r.status}`)}
                </span>
                {!!r.rejectReason && <span style={{ fontSize: 12, color: '#a32018', flexBasis: '100%' }}>{r.rejectReason}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* The request form */}
      {form && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,28,24,.45)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 }}
             onClick={e => { if (e.target === e.currentTarget) setForm(null); }}>
          <div style={{ ...card, maxWidth: 440, width: '100%', maxHeight: '86vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_hr_request_title')}</div>
            <div style={{ fontSize: 12.5, color: '#7c8894', margin: '4px 0 12px' }}>{t('p_hr_request_note')}</div>

            <label style={lbl}>{t('p_hr_kind')}</label>
            <select style={inp} value={form.kind}
              onChange={e => setForm({ kind: e.target.value, empId: '', payload: {} })}>
              {HR_KINDS.map(k => <option key={k} value={k}>{t(`p_hr_k_${k}`)}</option>)}
            </select>

            {form.kind !== 'employee_add' && (
              <>
                <label style={lbl}>{t('p_hr_employee')}</label>
                <select style={inp} value={form.empId}
                  onChange={e => setForm({ ...form, empId: e.target.value })}>
                  <option value="">{t('p_hr_pick_employee')}</option>
                  {d.employees.filter((e: any) => !e.left).map((e: any) =>
                    <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </>
            )}

            {form.kind === 'employee_add' && <>
              <label style={lbl}>{t('p_hr_name')}</label>
              <input style={inp} value={form.payload.name || ''}
                onChange={e => setForm({ ...form, payload: { ...form.payload, name: e.target.value } })} />
              <label style={lbl}>{t('p_hr_position')}</label>
              <input style={inp} value={form.payload.position || ''}
                onChange={e => setForm({ ...form, payload: { ...form.payload, position: e.target.value } })} />
              <label style={lbl}>{t('p_hr_nationality')}</label>
              <input style={inp} value={form.payload.nationality || ''}
                onChange={e => setForm({ ...form, payload: { ...form.payload, nationality: e.target.value } })} />
              <label style={lbl}>{t('p_hr_start_date')}</label>
              <input type="date" style={inp} value={form.payload.startDate || ''}
                onChange={e => setForm({ ...form, payload: { ...form.payload, startDate: e.target.value } })} />
            </>}

            {form.kind === 'employee_leave' && <>
              <label style={lbl}>{t('p_hr_last_day')}</label>
              <input type="date" style={inp} value={form.payload.lastDay || ''}
                onChange={e => setForm({ ...form, payload: { ...form.payload, lastDay: e.target.value } })} />
            </>}

            {form.kind !== 'employee_leave' && <>
              <label style={lbl}>{t('p_hr_new_salary')}</label>
              <input type="number" style={inp} value={form.payload.salary ?? ''}
                onChange={e => setForm({ ...form, payload: { ...form.payload, salary: Number(e.target.value) } })} />
              {/* What this change does to social security, before they send it. */}
              {form.payload.salary > 0 && (
                <div style={{ fontSize: 12.5, color: '#3d5c50', background: '#f0f5f3', borderRadius: 8,
                              padding: '8px 11px', marginTop: 6, lineHeight: 1.6 }}>
                  {t('p_hr_sso_preview', {
                    employee: money(Math.min(Math.round(Math.max(form.payload.salary, cfg.floor) * cfg.rate / 100), cfg.cap)),
                    total: money(Math.min(Math.round(Math.max(form.payload.salary, cfg.floor) * cfg.rate / 100), cfg.cap) * 2),
                  })}
                </div>
              )}
            </>}

            {form.kind === 'salary_change' && <>
              <label style={lbl}>{t('p_hr_effective_from')}</label>
              <input type="date" style={inp} value={form.payload.effectiveFrom || ''}
                onChange={e => setForm({ ...form, payload: { ...form.payload, effectiveFrom: e.target.value } })} />
            </>}

            <label style={lbl}>{t('p_hr_note')}</label>
            <textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} value={form.payload.note || ''}
              onChange={e => setForm({ ...form, payload: { ...form.payload, note: e.target.value } })} />

            {!!err && <div style={{ color: '#a32018', fontSize: 12.5, marginTop: 8 }}>{err}</div>}

            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button style={btnGhost} onClick={() => setForm(null)} disabled={saving}>{t('p_hr_cancel')}</button>
              <button style={btn} onClick={submit} disabled={saving}>
                {saving ? t('p_hr_sending') : t('p_hr_send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Calendar (read-only) ─────────────────────────────────────────────────────
// What is coming up, and whose move it is. The page leads with the client's OWN open items,
// because that is the only part they can act on; everything SLF owes sits underneath as context.
// Built from filing deadlines and tasks, both keyed to the client — not the team CalDAV calendars,
// whose events carry no client at all.
function CalendarPage() {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState(true);
  const [year, setYear] = useState(0);
  // Opens on the CURRENT month, not the whole year: a year of filings is a wall, and the question
  // a client came to answer is what is happening now. "Whole year" is one click away.
  const [month, setMonth] = useState(() => zonedParts().m);
  const [mine, setMine] = useState(false);

  useEffect(() => {
    setBusy(true);
    api.portalCalendar(year)
      .then((r: any) => { setD(r); if (!year) setYear(r.year); })
      .catch(() => setD(null))
      .finally(() => setBusy(false));
  }, [year]);

  if (busy && !d) return <div style={card}><div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_cal_loading')}</div></div>;
  if (!d) return <div style={card}><div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_doc_empty')}</div></div>;

  // A filing deadline and the firm's own task for the same filing land on the same day with the
  // same name — two true facts that read as one thing printed twice. Keep the DEADLINE (it is the
  // date that actually binds) and drop the task that duplicates it.
  const seen = new Set<string>();
  const all = [...(d.items || [])]
    .sort((a: any, b: any) => (a.source === 'filing' ? -1 : 1) - (b.source === 'filing' ? -1 : 1))
    .filter((i: any) => {
      const k = `${i.date}|${i.kind || i.rawKind}|${i.period}|${i.who}`;
      if (seen.has(k)) return false;
      seen.add(k); return true;
    })
    .sort((a: any, b: any) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const items = all.filter((i: any) =>
    (!month || Number(i.date.slice(5, 7)) === month) && (!mine || (i.who === 'you' && !i.done)));
  const label = (i: any) => i.kind ? t(`p_cal_k_${i.kind}`) : i.rawKind;
  // Days are grouped so a date with three things on it reads as one day, not three rows.
  const days: { date: string; items: any[] }[] = [];
  for (const i of items) {
    const last = days[days.length - 1];
    if (last && last.date === i.date) last.items.push(i); else days.push({ date: i.date, items: [i] });
  }
  const yoursOpen = all.filter((i: any) => i.who === 'you' && !i.done).length;

  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_cal_title')}</div>
      <div style={{ fontSize: 12.5, color: '#7c8894', margin: '4px 0 14px' }}>{t('p_cal_subtitle')}</div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {(d.years || []).length > 1 && (
          <select value={year} onChange={e => { setYear(Number(e.target.value)); setMonth(0); }}
            style={{ ...inp, width: 'auto', padding: '6px 10px' }}>
            {d.years.map((y: number) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        <button onClick={() => setMonth(0)}
          style={{ ...btnGhost, padding: '5px 11px', fontSize: 12.5,
                   background: !month ? ACCENT : '#eef2f5', color: !month ? '#fff' : '#333' }}>
          {t('p_cal_all_year')}
        </button>
        {TAX_MONTHS.map(m => {
          // Counted from the SAME deduped set the list renders, or the strip would promise six
          // items in a month that shows five.
          const inM = all.filter((x: any) => Number(x.date.slice(5, 7)) === m);
          const mm = { total: inM.length, yours: inM.filter((x: any) => x.who === 'you' && !x.done).length };
          return (
            <button key={m} onClick={() => setMonth(m)} disabled={!mm.total}
              title={mm.yours ? t('p_cal_yours_count', { count: mm.yours }) : ''}
              style={{ ...btnGhost, padding: '5px 10px', fontSize: 12.5, position: 'relative',
                       cursor: mm.total ? 'pointer' : 'default',
                       background: month === m ? ACCENT : '#eef2f5',
                       color: month === m ? '#fff' : mm.total ? '#333' : '#c2cbd2' }}>
              {m}
              {/* A month that still needs the CLIENT gets a dot — the one thing worth seeing
                  before you open it. */}
              {!!mm.yours && <span style={{ position: 'absolute', top: 2, right: 3, width: 6, height: 6,
                borderRadius: 3, background: month === m ? '#fff' : '#d9822b' }} />}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <button onClick={() => setMine(!mine)}
          style={{ ...btnGhost, padding: '5px 12px', fontSize: 12.5,
                   background: mine ? '#8a5a06' : '#eef2f5', color: mine ? '#fff' : '#333' }}>
          {t('p_cal_only_mine', { count: yoursOpen })}
        </button>
      </div>

      {!days.length ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_cal_nothing')}</div> : (
        <div>
          {days.map(day => {
            const past = day.date < String(d.today || '');
            return (
              <div key={day.date} style={{ display: 'flex', gap: 14, padding: '10px 0',
                                           borderTop: '1px solid #eef1f4', alignItems: 'flex-start' }}>
                <div style={{ flex: 'none', width: 92 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                color: past ? '#a8b2ba' : '#27303B' }}>{day.date}</div>
                  {day.date === d.today && (
                    <div style={{ fontSize: 11, color: ACCENT, fontWeight: 700 }}>{t('p_cal_today')}</div>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {day.items.map((i: any) => (
                    <div key={i.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{ flex: 'none', padding: '2px 8px', borderRadius: 20, fontSize: 11,
                                     fontWeight: 700,
                                     background: i.who === 'you' ? '#fdf0d9' : '#eef2f5',
                                     color: i.who === 'you' ? '#8a5a06' : '#5b6b79' }}>
                        {i.who === 'you' ? t('p_cal_you') : brandOr(t('ct_office'))}
                      </span>
                      <span style={{ fontSize: 13.5, fontWeight: 500,
                                     color: i.done ? '#98a4b0' : '#27303B',
                                     textDecoration: i.done ? 'line-through' : 'none' }}>
                        {label(i)}
                      </span>
                      {!!i.period && <span style={{ fontSize: 12, color: '#a8b2ba' }}>{i.period}</span>}
                      {i.source === 'filing' && (
                        <span style={{ fontSize: 11, color: '#a8b2ba' }}>{t('p_cal_is_filing')}</span>
                      )}
                      {i.done && <span style={{ fontSize: 11, color: ACCENT, fontWeight: 700 }}>{t('p_cal_done')}</span>}
                      {i.blocked && <span style={{ fontSize: 11, color: '#a32018', fontWeight: 700 }}>{t('p_cal_blocked')}</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Profit & loss (read-only) ────────────────────────────────────────────────
// Actual recorded figures only — no estimate, no projection (owner 2026-09-21). The statement for
// the year reads first, because that is the question; the month table underneath is where someone
// goes when a figure surprises them.
//
// A month with business activity but no frozen payroll reports zero salary. That is a gap in
// preparation, not a month without staff, and the page says so rather than letting a flattering net
// stand unexplained.
function FinancePage() {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState(true);
  const [year, setYear] = useState(0);

  useEffect(() => {
    setBusy(true);
    api.portalFinance(year)
      .then((r: any) => { setD(r); if (!year) setYear(r.year); })
      .catch(() => setD(null))
      .finally(() => setBusy(false));
  }, [year]);

  if (busy && !d) return <div style={card}><div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_fin_loading')}</div></div>;
  if (!d || !d.totals) return <div style={card}><div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_doc_empty')}</div></div>;

  const T = d.totals;
  // Expense lines are only worth a row when they carry something; an all-zero row of "rent paid to
  // company landlords" is noise on most clients' statements.
  const expenseLines: [string, number][] = ([
    ['p_fin_purchases', T.purchases], ['p_fin_salaries', T.salaries], ['p_fin_sso', T.sso],
    ['p_fin_pnd3', T.pnd3], ['p_fin_rent53', T.rent53],
  ] as [string, number][]).filter(([, v]) => v !== 0);

  const row = (label: string, value: number, opts: { strong?: boolean; top?: boolean; muted?: boolean } = {}) => (
    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '7px 0',
      borderTop: opts.top ? '2px solid #d3dbe2' : '1px solid #f2f5f7' }}>
      <span style={{ fontSize: 13.5, fontWeight: opts.strong ? 700 : 400,
                     color: opts.muted ? '#7c8894' : '#27303B' }}>{label}</span>
      <span style={{ fontSize: 13.5, fontWeight: opts.strong ? 800 : 500, fontVariantNumeric: 'tabular-nums',
                     color: value < 0 ? '#a32018' : '#27303B' }}>{money(value)}</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_fin_title', { year: d.year })}</div>
            <div style={{ fontSize: 12.5, color: '#7c8894', marginTop: 4 }}>{t('p_fin_subtitle')}</div>
          </div>
          {(d.years || []).length > 1 && (
            <select value={year} onChange={e => setYear(Number(e.target.value))}
              style={{ ...inp, width: 'auto', padding: '6px 10px' }}>
              {d.years.map((y: number) => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
        </div>

        {d.empty ? <div style={{ color: '#98a4b0', fontSize: 13, marginTop: 12 }}>{t('p_fin_no_data')}</div> : (
          <>
            {!!(d.incomplete || []).length && (
              <div style={{ margin: '12px 0 0', padding: '10px 12px', borderRadius: 8, background: '#fdf0d9',
                            color: '#8a5a06', fontSize: 12.5, lineHeight: 1.6 }}>
                {t('p_fin_incomplete', { months: d.incomplete.join(', ') })}
              </div>
            )}
            <div style={{ marginTop: 14, maxWidth: 520 }}>
              {row(t('p_fin_revenue'), T.revenue, { strong: true })}
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#5b6b79', margin: '14px 0 2px',
                            textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('p_fin_expenses')}</div>
              {expenseLines.map(([k, v]) => row(t(k), v))}
              {row(t('p_fin_expenses_total'), T.expenses, { strong: true, top: true })}
              {row(t('p_fin_net'), T.net, { strong: true, top: true })}
            </div>
          </>
        )}
      </div>

      {!d.empty && (
        <div style={card}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_fin_by_month')}</div>
          <div style={{ overflowX: 'auto', marginTop: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 660 }}>
              <thead><tr>
                <th style={th}>{t('p_fin_month')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('p_fin_revenue')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('p_fin_expenses_total')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('p_fin_net')}</th>
                <th style={th}></th>
              </tr></thead>
              <tbody>
                {d.months.filter((m: any) => m.hasData).map((m: any) => (
                  <tr key={m.month}>
                    <td style={td}>{m.month}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(m.revenue)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(m.expenses)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700,
                                 color: m.net < 0 ? '#a32018' : '#27303B' }}>{money(m.net)}</td>
                    <td style={{ ...td, fontSize: 11.5, color: '#8a5a06' }}>
                      {m.payrollMissing ? t('p_fin_no_payroll') : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11.5, color: '#a8b2ba', marginTop: 8, lineHeight: 1.6 }}>{t('p_fin_note')}</div>
        </div>
      )}
    </div>
  );
}

// ── File Storage (read-only) ─────────────────────────────────────────────────
// An index, not a folder. Every row points at a file already held somewhere in the system and the
// download streams from there — nothing is copied, so nothing can go stale against its original.
const FILE_FOLDERS = ['tax', 'company', 'uploads', 'deliverables', 'workflow', 'library'] as const;
const fileSize = (n: number) => n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB`
  : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`;

function FilesPage() {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState(true);
  const [folder, setFolder] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    api.portalFiles().then(setD).catch(() => setD(null)).finally(() => setBusy(false));
  }, []);

  if (busy && !d) return <div style={card}><div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_files_loading')}</div></div>;
  if (!d) return <div style={card}><div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_doc_empty')}</div></div>;

  const needle = q.trim().toLowerCase();
  const shown = (d.entries || []).filter((e: any) =>
    (!folder || e.folder === folder) && (!needle || String(e.name || '').toLowerCase().includes(needle)));

  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_files_title')}</div>
      <div style={{ fontSize: 12.5, color: '#7c8894', margin: '4px 0 14px' }}>{t('p_files_subtitle')}</div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <button onClick={() => setFolder('')}
          style={{ ...btnGhost, padding: '6px 13px', fontSize: 12.5,
                   background: !folder ? ACCENT : '#eef2f5', color: !folder ? '#fff' : '#333' }}>
          {t('p_files_all')} ({(d.entries || []).length})
        </button>
        {FILE_FOLDERS.filter(f => (d.folders || []).some((x: any) => x.key === f)).map(f => {
          const n = ((d.folders || []).find((x: any) => x.key === f) || { count: 0 }).count;
          return (
            <button key={f} onClick={() => setFolder(folder === f ? '' : f)}
              style={{ ...btnGhost, padding: '6px 13px', fontSize: 12.5,
                       background: folder === f ? ACCENT : '#eef2f5', color: folder === f ? '#fff' : '#333' }}>
              {t(`p_files_f_${f}`)} ({n})
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <input style={{ ...inp, maxWidth: 240, padding: '7px 11px' }} value={q}
          onChange={e => setQ(e.target.value)} placeholder={t('p_files_search')} />
      </div>

      {!shown.length ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_files_none')}</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
            <thead><tr>
              <th style={th}>{t('p_files_name')}</th>
              <th style={th}>{t('p_files_folder')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_files_size')}</th>
              <th style={th}>{t('p_files_added')}</th>
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {shown.map((e: any) => {
                const [src, id] = String(e.id).split(/:(.+)/);
                const url = api.portalFileUrl(src, id);
                return (
                  <tr key={e.id}>
                    <td style={{ ...td, fontWeight: 500 }}>{e.name || '—'}</td>
                    <td style={{ ...td, color: '#7c8894' }}>{t(`p_files_f_${e.folder}`)}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#7c8894' }}>{e.size ? fileSize(e.size) : '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#7c8894' }}>{bkkDay(e.at) || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {/* Not links: the endpoint needs the Bearer token, and a browser navigation
                          carries no headers — an href here returns Unauthorized. Both go through
                          openAuthed, which fetches with the token and hands over the blob. */}
                      <button onClick={() => openAuthed(`${url}?inline=1`, e.name || 'document', t)}
                        style={{ border: 'none', background: 'none', font: 'inherit', cursor: 'pointer',
                                 color: ACCENT, fontWeight: 600, padding: 0, marginRight: 14 }}>
                        {t('p_files_view')}
                      </button>
                      <button onClick={() => openAuthed(url, e.name || 'document', t)}
                        style={{ border: 'none', background: 'none', font: 'inherit', cursor: 'pointer',
                                 color: ACCENT, fontWeight: 600, padding: 0 }}>
                        {t('p_files_download')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* The firm's bills and the client's own documents live in their own tabs with their own
          signed links; naming them here keeps the library from looking incomplete without
          re-minting a link this page has no business creating. */}
      {!!(d.elsewhere?.bills || d.elsewhere?.filings) && (
        <div style={{ fontSize: 12, color: '#a8b2ba', marginTop: 12, lineHeight: 1.7 }}>
          {t('p_files_elsewhere', { bills: d.elsewhere.bills, filings: d.elsewhere.filings })}
        </div>
      )}
    </div>
  );
}

type Tab = 'owner' | 'bookpolicy' | 'chart' | 'ledgers' | 'statements' | 'invoices' | 'quotations' | 'receipts' | 'notes' | 'expenses' | 'bankstmt' | 'customers' | 'products' | 'profile' | 'filings' | 'bills' | 'jobs' | 'services' | 'tax' | 'hr' | 'calendar' | 'finance' | 'files';
export default function ClientPortal() {
  const { t } = useTranslation();
  // Restore the pinned installation and the active company BEFORE anything can issue a request. A
  // lazy initialiser runs during the first render, ahead of every useEffect — so loadProfile()
  // below already talks to the installation that holds this token, instead of firing once at the
  // wrong one on every refresh.
  const [stage, setStage] = useState<'login' | 'pw' | 'app'>(() => {
    restorePortalPin();   // honoured only while the stored atom still belongs to the current token
    setMerchantClient(localStorage.getItem(MERCHANT_CLIENT_KEY) || '');
    return localStorage.getItem('mf_token') ? 'app' : 'login';
  });
  const [isAdmin, setIsAdmin] = useState<boolean>(!!localStorage.getItem('mf_portal_admin'));
  const [profile, setProfile] = useState<any>(null);
  const [needDisclaimer, setNeedDisclaimer] = useState(false);
  // The open tab lives in the URL hash, so a refresh (or a bookmarked/shared link) lands back on
  // the same tab instead of bouncing to Invoices (owner 2026-08-08).
  // MUST list every tab. A key missing here is not "unrecognised" — it round-trips through the hash
  // and comes back as 'invoices', so the tab visibly bounces back on the first click and only sticks
  // on the second (when the hash already matches and no hashchange fires). Derived from `tabs` below
  // would be better still, but that array is built after this runs.
  const TAB_KEYS: Tab[] = ['owner', 'bookpolicy', 'chart', 'ledgers', 'statements', 'invoices', 'quotations', 'receipts', 'notes', 'expenses', 'bankstmt',
    'customers', 'products', 'profile', 'filings', 'bills', 'jobs', 'services', 'tax', 'hr', 'calendar', 'finance', 'files'];
  const tabFromUrl = (): Tab => {
    const h = String(window.location.hash || '').replace(/^#\/?/, '') as Tab;
    return TAB_KEYS.includes(h) ? h : 'invoices';
  };
  const [tab, setTabState] = useState<Tab>(tabFromUrl);
  const setTab = (k: Tab) => { setTabState(k); if (tabFromUrl() !== k) window.location.hash = `#${k}`; };
  useEffect(() => {
    if (!window.location.hash) window.location.replace(`#${tab}`);   // stamp the default so refresh is stable
    const onHash = () => setTabState(tabFromUrl());                  // browser back/forward between tabs
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []); // eslint-disable-line

  const signOut = () => {
    localStorage.removeItem('mf_token'); localStorage.removeItem('mf_portal_admin');
    storePortalPin(''); setPortalSystem(null);   // the pin atom lives beside the token (utils/api.ts)
    localStorage.removeItem(MERCHANT_CLIENT_KEY); setMerchantClient('');
    setProfile(null); setIsAdmin(false); setStage('login');
  };
  // Switching company re-pins the client and reloads the profile, which re-renders every tab
  // against the new tenant. Everything downstream reads req.merchant, so nothing else has to know.
  const pickCompany = (clientId: string) => {
    setMerchantClient(clientId);
    try { localStorage.setItem(MERCHANT_CLIENT_KEY, clientId); } catch { /* private window */ }
    setProfile(null); setNeedDisclaimer(false);
    setTab('invoices');
    loadProfile();
  };
  const profileRetry = useRef<number | undefined>();   // the 5 s retry while the API is unreachable (below)
  // The gate is "has accepted the CURRENT terms", not "has ever accepted anything". Both numbers
  // come from the server — `disclaimerVersion` is what it requires, `disclaimer_version` is what
  // this company holds — so raising the terms re-prompts every client with no frontend release.
  // A server that does not send a required version yet (older office) reads as 1, which is what
  // every existing acceptance was backfilled to, so nobody is re-prompted by a version skew.
  const loadProfile = () => api.portalProfile()
    .then((d: any) => {
      import('../../utils/brand').then(m => m.loadPublicBrand()).catch(() => { /* {{brand}} stays '' */ });   // {{brand}} in every portal string: the profile's, else GET /api/public/brand
      // The portal-wide zone / currency / languages: the firm's — or, for a merchant outside Thailand
      // (#58, server merchantMode.ts), the merchant's own region and its country's languages.
      setRegionOverride(d?.region || null);
      setCountryFeaturesOverride(d?.countryFeatures);   // the languages offered (cpackdocs)
      // Per company: the merchant's own region for ITS documents (passed its id below) and — for a
      // merchant outside Thailand — each firm's region for what that firm billed (`firmRegions`).
      // Rebuilt on every profile load so switching company never keeps the previous one's map.
      clearCompanyRegions();
      addCompanyRegions({ ...(d?.firmRegions || {}), ...(d?.id && d?.docRegion ? { [d.id]: d.docRegion } : {}) });
      setCurrencyWords(d?.docRegion || d?.region || null);   // the currency every portal sentence names = the merchant's own (owner Q18)
      PORTAL_MERCHANT_ID = String(d?.id || '');   // the signed-in merchant — amounts in screens without a document of their own
      setProfile(d);
      const need = Number(d.disclaimerVersion ?? 1);
      setNeedDisclaimer((Number(d.disclaimer_version) || 0) < need);
    })
    .catch((e: any) => {
      // The office's server restarting under a deploy (Bugify deploy-now, 2026-11-03) is no reason to sign the client
      // out: try again in 5 s, and at once when the API answers again (slf:api-back below).
      if (e?.apiDown) {
        window.clearTimeout(profileRetry.current);
        profileRetry.current = window.setTimeout(() => { if (localStorage.getItem('mf_token')) loadProfile(); }, 5000);
        return;
      }
      if (String(e?.message || '').includes('disclaimer')) setNeedDisclaimer(true);
      else signOut();
    });
  useEffect(() => () => window.clearTimeout(profileRetry.current), []);
  useEffect(() => { if (stage === 'app' && !isAdmin) loadProfile(); }, [stage, isAdmin]); // eslint-disable-line
  // Still on "Loading…" because the profile could not be fetched: fetch it the moment the API is back.
  useEffect(() => {
    if (stage !== 'app' || isAdmin || profile) return;
    const onBack = () => { window.clearTimeout(profileRetry.current); loadProfile(); };
    window.addEventListener('slf:api-back', onBack);
    return () => window.removeEventListener('slf:api-back', onBack);
  }, [stage, isAdmin, profile]); // eslint-disable-line
  // A tab left open across a change made on OUR side (books opened, a feature switched on) keeps the
  // profile it fetched at mount, so the client goes on seeing padlocks that stopped being true hours
  // ago and has no way to know a reload would clear them. Re-read the profile when the tab comes back
  // to the foreground. Errors are swallowed on purpose: this only refreshes something we already hold,
  // so a blip while the laptop wakes up must never sign anybody out the way loadProfile() would.
  useEffect(() => {
    if (stage !== 'app' || isAdmin) return;
    const onShow = () => {
      if (document.visibilityState !== 'visible') return;
      api.portalProfile().then((d: any) => {
        setCurrencyWords(d?.docRegion || d?.region || null);   // as loadProfile: the merchant's currency may have changed on our side
        setProfile(d);
      }).catch(() => { /* keep what we have */ });
    };
    document.addEventListener('visibilitychange', onShow);
    return () => document.removeEventListener('visibilitychange', onShow);
  }, [stage, isAdmin]);

  if (stage === 'login') return <div style={{ minHeight: '100vh', background: '#f4f6f8' }}><Login onDone={(mc, adm) => { setIsAdmin(!!adm); setStage(mc && !adm ? 'pw' : 'app'); }} /></div>;
  if (stage === 'pw') return <div style={{ minHeight: '100vh', background: '#f4f6f8' }}><ChangePassword onDone={() => setStage('app')} /></div>;
  if (isAdmin) return (
    <div style={{ minHeight: '100vh', background: '#f4f6f8', fontFamily: PORTAL_FONT }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e3e7ea', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/logo.png" alt={t('p_logo_alt')} style={{ width: 30, height: 30, objectFit: 'contain' }} />
        <div style={{ fontWeight: 800, color: '#27303B', fontSize: 15 }}>{t('p_admin_title')}</div>
        <div style={{ flex: 1 }} />
        <button style={{ ...btnGhost, padding: '6px 12px' }} onClick={signOut}>{t('p_nav_sign_out')}</button>
      </div>
      <div style={{ padding: '22px 16px' }}><PortalClients /></div>
    </div>
  );
  if (needDisclaimer) return <div style={{ minHeight: '100vh', background: '#f4f6f8' }}><Disclaimer onDone={() => { setNeedDisclaimer(false); loadProfile(); }} /></div>;
  if (!profile) return <div style={{ minHeight: '100vh', background: '#f4f6f8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#98a4b0' }}>{t('p_loading')}</div>;

  // A merchant outside Thailand (server merchantMode.ts): the firm's Thai services — purchase bills
  // for Thai input tax, the Thai tax / filings / payroll / P&L views, credit & debit notes and the
  // statutory books — do not exist for it. Hidden here; the server refuses them too.
  const thai = thaiModeOf(profile);
  const THAI_ONLY_TABS = new Set<Tab>(['expenses', 'tax', 'hr', 'finance', 'notes', 'chart', 'ledgers', 'statements', 'bookpolicy', 'filings']);
  const hiddenTab = !thai && THAI_ONLY_TABS.has(tab);
  // The top bar carries the DAILY work — the documents a merchant issues and the bills they send in.
  // Everything consulted now and then (their own reference data, their profile, what SLF filed and
  // billed) moves under the company name on the left, so the row stays readable.
  const tabsAll: { k: Tab; label: string }[] = [
    { k: 'invoices', label: t('p_nav_invoices') }, { k: 'quotations', label: t('p_nav_quotations') }, { k: 'receipts', label: t('p_nav_receipts') },
    // Credit / debit notes left the top bar on 2026-09-22. They are the remedy for a document that
    // went out wrong — reached for when something is already broken, not daily — and the row had
    // grown to ten tabs, which is where a bar stops being navigation and becomes a wall.
    { k: 'expenses', label: t('p_nav_expenses') },
    // Bank statements (owner 2026-10-13) sit beside the purchase bills: both are things the client
    // SENDS IN for the firm to work, i.e. daily work, which is what the bar is for. Not Thai-only —
    // every company has a bank. Never a locked entry (owner: 不要有灰色的菜单): the server sends
    // features.bankStatements = true for every merchant today, so the tab is simply there; only an
    // explicit `false` from a future switch hides it — hides, never greys.
    ...(profile.features?.bankStatements === false ? [] : [{ k: 'bankstmt' as Tab, label: t('p_nav_bankstmt') }]),
    // Jobs sits in the TOP BAR, not the brand menu, even though it is a "From SLF" read-only view
    // like filings and bills. Those two are consulted; this one answers "where is my thing" — the
    // question clients actually call about — and a tab nobody can find answers nothing.
    { k: 'jobs', label: t('p_nav_jobs') },
    { k: 'tax', label: t('p_nav_tax') },
    { k: 'hr', label: t('p_nav_hr') },
    { k: 'calendar', label: t('p_nav_calendar') },
    { k: 'finance', label: t('p_nav_finance') },
  ];
  const tabs = tabsAll.filter(x => thai || !THAI_ONLY_TABS.has(x.k));
  // Files goes in the brand menu, not the top bar: it is where you go when you need a specific
  // document, not something you check. The top row is already seven tabs wide.
  const menuGroupsAll: { title: string; items: { k: Tab; label: string; hint?: string }[] }[] = [
    { title: t('p_menu_group_mydata'), items: [
      { k: 'notes', label: t('p_nav_notes'), hint: t('p_menu_notes_hint') },
      { k: 'customers', label: t('p_menu_customers'), hint: t('p_menu_customers_hint') },
      { k: 'products', label: t('p_menu_products'), hint: t('p_menu_products_hint') },
      // Company Profile left this menu on 2026-09-22 for the building icon at the far right of the
      // header: it is the one entry here that is about WHO YOU ARE rather than what you sell or
      // owe, and every other product puts that in the corner, so people look there first.
    ] },
    // ── My books ────────────────────────────────────────────────────────────────────────────────
    // A THIRD group rather than three more tabs. The bar already wraps at 1280px with the nine it
    // has, and the portal's own rule puts daily work in the bar and what is "consulted now and
    // then" in this menu — nobody opens a trial balance to issue an invoice.
    //
    // "How we keep your books" is first and is ALWAYS present: it describes the treatment we apply,
    // so it has content from the day a client signs. The three that read actual books appear only
    // once there is something in them (features.books, which requires a POSTED entry, not merely an
    // open period) — three correct, empty pages read as a broken feature, not as "not yet written
    // up". Today that gate is closed for every client: production holds no posted entry at all.
    { title: t('p_nav_books'), items: [
      // "How we keep your books" left this menu on 2026-10-02 (owner). The page, its route and its
      // trilingual copy all stay — /portal/#bookpolicy still opens it and it is still served by
      // GET /portal/books/policy — it is simply not offered here any more.
      // Always listed, locked until there is a posted entry behind them (owner 2026-10-02, choosing
      // this over hiding them). features.books is the gate; `locked` carries the reason on screen.
      ...(['chart', 'ledgers', 'statements'] as const).map(k => ({
        k: k as Tab,
        label: t(`p_menu_${k}`),
        hint: t(`p_menu_${k}_hint`),
        locked: profile.features?.books ? undefined : t('p_menu_books_locked'),
      })),
    ] },
    { title: t('p_menu_group_fromslf'), items: [
      { k: 'filings', label: t('p_menu_filings'), hint: t('p_menu_filings_hint') },
      { k: 'bills', label: t('p_menu_bills'), hint: t('p_menu_bills_hint') },
      // "Our services" removed from the client menu on 2026-10-02 (owner: 客户不需要看这个). The
      // page and its route stay — the catalogue is still reachable for staff and by direct link —
      // but a client does not open their portal to read what we sell and what it costs.
      { k: 'files', label: t('p_menu_files'), hint: t('p_menu_files_hint') },
    ] },
  ];
  const menuGroups = menuGroupsAll
    .map(g => ({ ...g, items: g.items.filter(i => thai || !THAI_ONLY_TABS.has(i.k)) }))
    .filter(g => g.items.length);
  // Tabs whose main content is a wide data table rather than a form or a document.
  const LIST_TABS = new Set(['invoices', 'quotations', 'receipts', 'notes', 'expenses', 'bankstmt', 'jobs', 'filings', 'bills']);
  const inMenu = menuGroups.flatMap(g => g.items).find(i => i.k === tab);
  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f8', fontFamily: PORTAL_FONT }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e3e7ea', padding: '0 20px', display: 'flex', alignItems: 'center', gap: 18 }}>
        <BrandMenu name={profile.name} groups={menuGroups} tab={tab} setTab={setTab} active={inMenu?.label}
          companies={profile.companies} activeClientId={profile.activeClientId} onPickCompany={pickCompany}
          onSignOut={signOut} />
        <OfficeBadge office={profile.office} />
        {/* No sideways scroll (owner 2026-09-21): a scrollbar in a menu bar hides the tabs past the
            edge just as surely as clipping them did. With language and sign-out moved into the brand
            menu there is room for all eight; if a narrow window still runs out, the row WRAPS onto a
            second line, where every tab stays visible. */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {tabs.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '16px 12px', fontSize: 13.5, whiteSpace: 'nowrap', fontWeight: tab === t.k ? 800 : 500, color: tab === t.k ? ACCENT : '#5b6b79', borderBottom: tab === t.k ? `3px solid ${ACCENT}` : '3px solid transparent' }}>
              {t.label}
            </button>
          ))}
        </div>
        <OwnerButton on={tab === 'owner'} label={t('p_nav_owner')} onClick={() => setTab('owner')} />
        <ProfileIcon on={tab === 'profile'} label={t('p_menu_profile')} onClick={() => setTab('profile')} />
      </div>
      {/* 1040 suits a form; it does not suit a nine-column ledger. Adding the Due and Outstanding
          columns pushed the invoice table's minimum width past the cap, and a table cannot shrink
          below its own content — so it painted straight out of the card. The list tabs get room;
          the form and reading tabs keep the narrower measure, which is what makes them readable. */}
      <div style={{ maxWidth: LIST_TABS.has(tab) ? 1360 : 1040, margin: '22px auto', padding: '0 16px' }}>
        {hiddenTab && <div style={{ ...card, color: '#5b6b79', fontSize: 13.5 }}>{t('pi_tab_unavailable')}</div>}
        {tab === 'owner' && <OwnerConsole merchantId={profile?.id} />}
        {!hiddenTab && tab === 'bookpolicy' && <BookPolicy />}
        {!hiddenTab && (tab === 'chart' || tab === 'ledgers' || tab === 'statements') && <BooksPage kind={tab} />}
        {!hiddenTab && tab === 'expenses' && <Expenses />}
        {tab === 'bankstmt' && <BankStatementsPage banks={profile.banks || []} />}
        {!hiddenTab && tab === 'filings' && <TaxFilings />}
        {tab === 'bills' && <BillsFromSlf />}
        {tab === 'jobs' && <JobsFromSlf />}
        {tab === 'services' && <ServicesFromSlf />}
        {!hiddenTab && tab === 'tax' && <TaxFromSlf />}
        {!hiddenTab && tab === 'hr' && <HrPage />}
        {tab === 'calendar' && <CalendarPage />}
        {!hiddenTab && tab === 'finance' && <FinancePage />}
        {tab === 'files' && <FilesPage />}
        {tab === 'customers' && <Customers plain={!thai} />}
        {tab === 'products' && <Products vatRegistered={thai && !!profile.vat_registered} plain={!thai} />}
        {tab === 'profile' && <Profile profile={profile} reload={loadProfile} />}
        {!hiddenTab && (tab === 'invoices' || tab === 'quotations' || tab === 'receipts' || tab === 'notes') && (
          <DocsPage kind={tab} profile={profile} toExpenses={() => setTab('expenses')} />
        )}
      </div>
    </div>
  );
}

// ── Documents: invoices / quotations / receipts ──────────────────────────────
// openPdf / whtSelect / openAuthed / openExpenseFile sit outside any component, so they cannot call
// useTranslation and take `t` as an argument. It goes LAST in all four: a call that forgets it then
// fails to compile, where a leading `t` would let an id or a URL bind to it and fetch the wrong
// thing at runtime. (vatOptions/monthName/monthAbbrs take it first — they have no such collision.)
// During a deploy's restart (apiFetch, 2026-11-04) all four say "being updated — try again in a minute" (sysup_try_again)
// instead of "PDF failed" or "no longer available", and the "updating" notice goes up at once.
// …but not with an alert() straight away (review round 2): a native alert stops the page before React can draw the card
// or the probe can run, so the client saw a browser dialog first, and the card only after clicking OK. The notice says
// it: the sentence is shown only if, a moment later, no card is up (a lost network: the banner comes after two probes).
const SAY_DOWN_AFTER_MS = 1500;
const sayApiDown = (t: TFn): void => {
  window.setTimeout(() => { if (!getApiHealth().overlay) alert(t('sysup_try_again')); }, SAY_DOWN_AFTER_MS);
};
const docFetch = async (url: string, failKey: string, t: TFn): Promise<Response | null> => {
  let res: Response;
  try { res = await apiFetch(url, { headers: portalFetchHeaders() }); }
  catch (e: any) { if (e?.apiDown) sayApiDown(t); else alert(t(failKey)); return null; }
  if (!res.ok) { if (isApiDownResponse(res)) sayApiDown(t); else alert(t(failKey)); return null; }
  return res;
};
const openPdf = async (kind: string, id: string, t: TFn) => {
  const res = await docFetch(api.portalPdfUrl(kind, id), 'p_pdf_failed', t);
  if (!res) return;
  const url = URL.createObjectURL(await res.blob());
  window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000);
};
// Server-side PDF rendering takes a few seconds — the button spins and locks itself so the page
// never looks frozen and can't be double-fired (firm rule: every slow button shows a spinner).
const today = (): string => todayISO();
// Payment terms. An invoice without a deadline is not a demand for payment, so the due date is
// required and defaults to issue + 30 days. Computed in UTC off the date string itself so it can
// never drift a day with the browser's timezone.
const PAYMENT_TERM_DAYS = 30;
const addDays = (iso: string, days: number): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '')); if (!m) return '';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
// Withholding rates a Thai payer may certify on ภ.ง.ด.3 / 53. 0 = the buyer withholds nothing.
const WHT_RATES = [0, 1, 2, 3, 5, 10, 15];
const whtSelect = (value: any, onChange: (n: number) => void, style: React.CSSProperties, t: TFn) => (
  <select style={style} value={String(Number(value) || 0)} onChange={e => onChange(Number(e.target.value))} title={t('p_wht_select_title')}>
    {WHT_RATES.map(r => <option key={r} value={r}>{r === 0 ? t('p_wht_none') : t('p_wht_rate', { rate: r })}</option>)}
  </select>
);

// ── Spreadsheet import ───────────────────────────────────────────────────────
// Two steps on purpose. The preview is where the operator confirms the one thing the file cannot
// settle by itself: whether each fee column is VAT-inclusive. Getting that wrong overcharges every
// buyer by 7%, so it is never assumed silently — and the reconciliation against the sheet's own
// total column is shown right next to it.
function ImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [sheet, setSheet] = useState('');
  const [modes, setModes] = useState<Record<string, string>>({});
  const [docType, setDocType] = useState('tax_invoice_receipt');
  const [prev, setPrev] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<any>(null);
  // The operator's column mapping. Seeded from the server's suggestion (header patterns, or what
  // this merchant confirmed last time for an identically-shaped sheet) and edited here; every
  // preview and the commit carry it, because the server parses from the FILE and the mapping alone.
  const [map, setMap] = useState<any[]>([]);
  // Step 2 is skipped once the operator accepts the mapping, so an ordinary month stays two clicks.
  const [mapOpen, setMapOpen] = useState(false);

  // Each preview supersedes the one before it: a slow response must never overwrite a mapping the
  // operator has edited since, or a corrected column would silently snap back.
  const seq = React.useRef(0);
  const runPreview = async (f: File, sh: string, md: Record<string, string>, mp?: any[]) => {
    const mine = ++seq.current;
    setBusy(true); setErr('');
    try {
      const d: any = await api.portalImportPreview(f, sh, md, mp);
      if (mine !== seq.current) return;                       // a newer preview is already in flight
      setPrev(d); setSheet(d.sheet);
      const m: Record<string, string> = {};
      for (const c of d.feeColumns || []) m[c.key] = md[c.key] || c.mode;
      setModes(m);
      setMap(d.mapping || []);
      // Open the mapping step by itself when the sheet could not be read end-to-end — that is
      // precisely the case the mapping exists for, and the old build just showed a red error.
      if (!d.rows?.length) setMapOpen(true);
    } catch (e: any) { if (mine === seq.current) { setErr(e?.message || t('p_imp_err_read')); setPrev(null); } }
    finally { if (mine === seq.current) setBusy(false); }
  };

  // Re-map one column, then re-read the sheet so the preview below always reflects the mapping.
  // Moving a single-use field (Customer, Invoice no., …) to another column RELEASES its old owner
  // in the same step: making the operator free the old column first would leave the mapping
  // momentarily invalid, and the server — rightly — refuses an incomplete one.
  const setCol = (col: number, patch: any) => {
    const f = patch.field;
    const single = f && f !== 'ignore' && f !== 'fee';
    const next = map.map(s => {
      if (s.col === col) return { ...s, ...patch };
      if (single && s.field === f) return { col: s.col, field: 'ignore' };
      return s;
    });
    setMap(next);
    if (file) runPreview(file, sheet, modes, next);
  };

  const commit = async () => {
    if (!file || !prev) return;
    const n = prev.rows.length;
    const docTypeWord = docType === 'tax_invoice_receipt' || docType === 'tax_invoice'
      ? t('p_imp_word_tax_invoices') : t('p_imp_word_invoices');
    if (!window.confirm(t('p_imp_confirm', { n, docTypeWord, m: prev.newCustomers?.length || 0 }))) return;
    setBusy(true); setErr('');
    try { const d: any = await api.portalImportCommit(file, sheet, modes, docType, map); setDone(d); }
    catch (e: any) { setErr(e?.message || t('p_imp_err_failed')); }
    finally { setBusy(false); }
  };

  const fieldOf = (col: number): string => map.find(s => s.col === col)?.field || 'ignore';
  const specOf = (col: number): any => map.find(s => s.col === col) || {};
  // Every field except a fee line may be used once — offering a taken one only invites a rejection
  // from the server, so it is greyed out on the other rows instead.
  const takenBy = (f: string): number | undefined => f === 'ignore' || f === 'fee'
    ? undefined : map.find(s => s.field === f)?.col;
  const mapped = (f: string) => map.some(s => s.field === f);
  const missing = ['customer', 'docNo', 'issueDate'].filter(f => !mapped(f));
  const noFee = !map.some(s => s.field === 'fee');

  const totals = prev ? prev.rows.reduce((s: number, r: any) => s + r.gross, 0) : 0;
  const mismatched = prev ? prev.rows.filter((r: any) => r.totalsAgree === false) : [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: '4vh 12px' }}
         onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...card, width: 'min(1000px, 96vw)', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>{t('p_imp_title')}</div>
        <div style={{ fontSize: 12.5, color: '#7c8894', marginBottom: 14 }}>
          {t('p_imp_subtitle')}
        </div>

        {done ? (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#15803D', marginBottom: 8 }}>{t('p_imp_done', { created: done.created, customers: done.customersAdded })}</div>
            {!!done.skipped?.length && <>
              <div style={{ fontSize: 13, fontWeight: 700, margin: '12px 0 4px' }}>{t('p_imp_skipped_title')}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>{t('p_imp_th_row')}</th><th style={th}>{t('p_imp_th_docno')}</th><th style={th}>{t('p_imp_th_owner')}</th><th style={th}>{t('p_imp_th_property')}</th><th style={th}>{t('p_imp_th_why')}</th></tr></thead>
                <tbody>{done.skipped.map((s: any) => (
                  <tr key={s.rowNo}><td style={td}>{s.rowNo}</td><td style={td}>{s.docNo || '—'}</td><td style={td}>{s.ownerName || '—'}</td><td style={td}>{s.property || '—'}</td><td style={{ ...td, color: '#B45309' }}>{s.why}</td></tr>))}
                </tbody>
              </table>
            </>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btn} onClick={onDone}>{t('p_imp_done_btn')}</button>
            </div>
          </div>
        ) : <>
          <label style={lbl}>{t('p_imp_file_label')}</label>
          <input type="file" accept=".xlsx,.xlsm" style={{ ...inp, padding: 7 }}
                 onChange={e => { const f = e.target.files?.[0] || null; setFile(f); setPrev(null); setDone(null); setMap([]); setModes({}); setSheet(''); setMapOpen(false); if (f) runPreview(f, '', {}); }} />

          {prev && <>
            <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={lbl}>{t('p_imp_sheet')}</label>
                <select style={inp} value={sheet} onChange={e => { setSheet(e.target.value); setMap([]); file && runPreview(file, e.target.value, {}); }}>
                  {prev.sheetNames.map((s: string) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 300px' }}>
                <label style={lbl}>{t('p_imp_create_as')}</label>
                <select style={inp} value={docType} onChange={e => setDocType(e.target.value)}>
                  <option value="tax_invoice_receipt">{t('p_imp_type_tir')}</option>
                  <option value="tax_invoice">{t('p_imp_type_ti')}</option>
                  <option value="invoice">{t('p_imp_type_inv')}</option>
                </select>
              </div>
            </div>

            {/* ── Step 2 · Column mapping ─────────────────────────────────────────────────
                The spreadsheet's headers are the merchant's own words, so they are never matched
                by name alone. The server proposes a mapping; this is where a human corrects it. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0 6px' }}>
              <div style={{ fontSize: 13.5, fontWeight: 800 }}>{t('p_imp_map_title')}</div>
              {prev.fromSavedMapping && <span style={{ fontSize: 11.5, color: '#15803D' }}>{t('p_imp_map_saved')}</span>}
              <div style={{ flex: 1 }} />
              <button style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }} onClick={() => setMapOpen(o => !o)}>
                {mapOpen ? t('p_imp_map_hide') : t('p_imp_map_edit')}
              </button>
            </div>

            {(!!missing.length || noFee) && (
              <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12.5, lineHeight: 1.6 }}>
                {missing.map(f => <div key={f}>⚠️ {t('p_imp_map_required', { field: t(`p_imp_field_${f}`) })}</div>)}
                {noFee && <div>⚠️ {t('p_imp_map_need_amount')}</div>}
              </div>
            )}

            {mapOpen ? (
              <div style={{ border: '1px solid #e3e7ea', borderRadius: 8, overflow: 'auto', maxHeight: 340 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>{t('p_imp_map_th_column')}</th>
                    <th style={th}>{t('p_imp_map_th_sample')}</th>
                    <th style={th}>{t('p_imp_map_th_field')}</th>
                    <th style={th}>{t('p_imp_map_th_vat')}</th>
                    <th style={th}>{t('p_imp_map_th_label')}</th>
                  </tr></thead>
                  <tbody>
                    {(prev.headers || []).map((h: any) => {
                      const f = fieldOf(h.col);
                      const sp = specOf(h.col);
                      return (
                        <tr key={h.col}>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            <code style={{ fontSize: 12 }}>{h.header || t('p_imp_map_no_header', { n: h.col })}</code>
                          </td>
                          <td style={{ ...td, fontSize: 11.5, color: '#7c8894', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {h.samples?.join(' · ') || '—'}
                          </td>
                          <td style={td}>
                            <select style={{ ...inp, width: 190, padding: '5px 8px' }} value={f}
                                    onChange={e => setCol(h.col, { field: e.target.value, mode: e.target.value === 'fee' ? (sp.mode || 'standard_incl') : undefined })}>
                              {['ignore', 'customer', 'docNo', 'issueDate', 'dueDate', 'address', 'description', 'remark', 'fee', 'total'].map(k => {
                                const owner = takenBy(k);
                                return <option key={k} value={k} disabled={owner !== undefined && owner !== h.col}>{t(`p_imp_field_${k}`)}</option>;
                              })}
                            </select>
                          </td>
                          <td style={td}>
                            {f === 'fee' ? (
                              <select style={{ ...inp, width: 190, padding: '5px 8px' }} value={sp.mode || 'standard_incl'}
                                      onChange={e => setCol(h.col, { mode: e.target.value })}>
                                {vatOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            ) : <span style={{ color: '#c2cad2' }}>—</span>}
                          </td>
                          {/* What the buyer reads on the invoice line — defaulted from the header
                              with its "(include VAT)" note stripped, and editable because a header
                              is written for a bookkeeper, not for a customer. */}
                          <td style={td}>
                            {f === 'fee'
                              ? <input style={{ ...inp, width: 200, padding: '5px 8px' }} value={sp.label || ''} maxLength={120}
                                       onChange={e => setMap(map.map(x => x.col === h.col ? { ...x, label: e.target.value } : x))}
                                       onBlur={() => file && runPreview(file, sheet, modes, map)} />
                              : <span style={{ color: '#c2cad2' }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ fontSize: 12.5, color: '#5b6b79', lineHeight: 1.7 }}>
                {map.filter(s => s.field !== 'ignore').map(s => {
                  const h = (prev.headers || []).find((x: any) => x.col === s.col);
                  return <span key={s.col} style={{ display: 'inline-block', marginRight: 12 }}>
                    <code style={{ fontSize: 11.5 }}>{h?.header || `#${s.col}`}</code> → <b>{t(`p_imp_field_${s.field}`)}</b>
                  </span>;
                })}
              </div>
            )}

            <div style={{ fontSize: 13.5, fontWeight: 800, margin: '16px 0 6px' }}>
              {t('p_imp_fee_q')}
              <span style={{ fontWeight: 400, fontSize: 11.5, color: '#B45309' }}> {t('p_imp_fee_warn')}</span>
            </div>
            {prev.feeColumns.map((c: any) => (
              <div key={c.key} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                <div style={{ flex: 1, fontSize: 13 }}>
                  <code style={{ fontSize: 12 }}>{c.header}</code>
                  {c.headerSaysInclusive
                    ? <span style={{ color: '#15803D', fontSize: 11.5 }}> {t('p_imp_header_incl')}</span>
                    : <span style={{ color: '#B45309', fontSize: 11.5 }}> {t('p_imp_header_unknown')}</span>}
                </div>
                {/* Writes the MAPPING, not a parallel `modes` record: two controls over one
                    figure is how a screen ends up saying "VAT-exclusive" while the documents are
                    built inclusive. */}
                <select style={{ ...inp, width: 200 }} value={c.mode}
                        onChange={e => setCol(c.col, { mode: e.target.value })}>
                  {vatOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            ))}

            {!!prev.warnings?.length && <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: 10, margin: '12px 0', fontSize: 12.5, lineHeight: 1.6 }}>
              {prev.warnings.map((w: string, i: number) => <div key={i}>⚠️ {w}</div>)}
            </div>}

            {!!mismatched.length && <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: 10, margin: '12px 0', fontSize: 12.5, lineHeight: 1.6 }}>
              <b>{t('p_imp_mismatch_bold', { n: mismatched.length })}</b> {t('p_imp_mismatch_body')}
              <div style={{ marginTop: 5, fontFamily: 'ui-monospace, monospace', fontSize: 11.5 }}>
                {mismatched.slice(0, 4).map((r: any) => <div key={r.rowNo}>{t('p_imp_mismatch_line', { n: r.rowNo, docNo: r.docNo, a: money(r.gross, 2, mco()), b: money(r.sheetTotal, 2, mco()) })}</div>)}
              </div>
            </div>}

            {!!prev.alreadyIssued?.length && <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: 10, margin: '12px 0', fontSize: 12.5 }}>
              <b>{t('p_imp_already_bold', { n: prev.alreadyIssued.length })}</b> {t('p_imp_already_rest', { list: prev.alreadyIssued.slice(0, 6).join(', ') })}
            </div>}

            <div style={{ fontSize: 13, margin: '14px 0 6px' }}>
              {t('p_imp_summary', { rows: prev.rows.length, customers: prev.newCustomers?.length || 0, amount: money(totals, 2, mco()) })} · <span style={{ color: '#B45309' }}>{t('p_imp_summary_skipped', { skipped: prev.skipped.length })}</span>
            </div>

            <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid #e3e7ea', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>{t('p_imp_th_row')}</th><th style={th}>{t('p_imp_th_docno')}</th><th style={th}>{t('p_imp_prev_th_date')}</th><th style={th}>{t('p_imp_prev_th_customer')}</th><th style={th}>{t('p_imp_prev_th_lines')}</th><th style={{ ...th, textAlign: 'right' }}>{t('p_imp_prev_th_total')}</th></tr></thead>
                <tbody>{prev.rows.slice(0, 60).map((r: any) => (
                  <tr key={r.rowNo}>
                    <td style={td}>{r.rowNo}</td><td style={{ ...td, fontFamily: 'ui-monospace, monospace' }}>{r.docNo}</td>
                    <td style={td}>{r.issueDate}</td><td style={td}>{r.ownerName}</td>
                    <td style={{ ...td, fontSize: 12 }}>{r.lines.map((l: any) => l.name).join(' · ')}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(r.gross, 2, mco())}</td>
                  </tr>))}
                </tbody>
              </table>
              {prev.rows.length > 60 && <div style={{ padding: 8, fontSize: 12, color: '#7c8894' }}>{t('p_imp_more', { n: prev.rows.length - 60 })}</div>}
            </div>

            {!!prev.skipped.length && <>
              <div style={{ fontSize: 13, fontWeight: 700, margin: '14px 0 4px', color: '#B45309' }}>{t('p_imp_skipped_head')}</div>
              <div style={{ maxHeight: 150, overflow: 'auto', border: '1px solid #e3e7ea', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>{prev.skipped.map((s: any) => (
                    <tr key={s.rowNo}><td style={td}>{t('p_imp_skipped_row', { n: s.rowNo })}</td><td style={td}>{s.docNo || '—'}</td><td style={td}>{s.ownerName || '—'}</td><td style={td}>{s.property || '—'}</td><td style={{ ...td, color: '#B45309' }}>{s.why}</td></tr>))}
                  </tbody>
                </table>
              </div>
            </>}
          </>}

          {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button style={btnGhost} onClick={onClose}>{t('p_btn_cancel')}</button>
            <button style={{ ...btn, opacity: busy || !prev || !prev.rows?.length || !!missing.length || noFee ? .5 : 1 }}
                    disabled={busy || !prev || !prev.rows?.length || !!missing.length || noFee} onClick={commit}>
              {busy ? t('p_imp_working') : prev ? t('p_imp_btn_n', { n: prev.rows.length }) : t('p_imp_btn')}
            </button>
          </div>
        </>}
      </div>
    </div>
  );
}

// ── Emailing a document to the buyer ─────────────────────────────────────────
// The merchant has no mail server of their own, so this goes out through the workspace's noreply@ mailbox
// carrying the MERCHANT's name, with replies pointed back at them. The envelope is shown but not
// editable — a free-text reply address would let anyone borrow SLF's sending reputation.
function SendDocModal({ kind, doc, onClose, onSent }:
  { kind: string; doc: any; onClose: () => void; onSent: () => void }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [to, setTo] = useState(''); const [subject, setSubject] = useState(''); const [body, setBody] = useState('');
  const [save, setSave] = useState(true);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(''); const [done, setDone] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    api.portalSendDraft(kind, doc.id).then((r: any) => {
      setD(r); setTo(r.to || ''); setSubject(r.subject || ''); setBody(r.body || '');
    }).catch((e: any) => setErr(e?.message || t('p_send_err_prepare')));
  }, [kind, doc.id]); // eslint-disable-line

  const send = async () => {
    setBusy(true); setErr('');
    try {
      const r: any = await api.portalSendDoc(kind, doc.id, { to: to.trim(), subject, body, saveToCustomer: save });
      setDone(r.to); onSent();
    } catch (e: any) { setErr(e?.message || t('p_send_err_send')); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex',
                  alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: '4vh 12px' }}
         onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...card, width: 780, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto' }}>
        <div style={{ fontSize: 17, fontWeight: 800 }}>{t('p_send_title', { docNo: doc.doc_no })}</div>
        <div style={{ fontSize: 12.5, color: '#7c8894', marginTop: 2, marginBottom: 12 }}>
          {t('p_send_subtitle')}
        </div>

        {!d && !err && <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_send_preparing')}</div>}

        {done ? (
          <div>
            <div style={{ background: '#EAF6EF', border: '1px solid #A7D8BC', borderRadius: 9, padding: '12px 14px', fontSize: 14 }}>
              {t('p_send_sent_ok', { email: done })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button style={btn} onClick={onClose}>{t('p_send_done')}</button>
            </div>
          </div>
        ) : d && (<>
          {/* Fixed envelope, shown so the merchant knows what the customer will see. */}
          <div style={{ background: '#f6f8f9', border: '1px solid #e3e7ea', borderRadius: 9, padding: '11px 14px',
                        fontSize: 13, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px', marginBottom: 14 }}>
            <b style={{ color: '#5b6b79' }}>{t('p_send_from')}</b>
            <span>{d.fromName} &lt;{d.fromEmail}&gt;</span>
            <b style={{ color: '#5b6b79' }}>{t('p_send_replyto')}</b>
            <span>{d.replyTo || <span style={{ color: '#D0021B' }}>{t('p_send_no_replyto')}</span>}</span>
            <b style={{ color: '#5b6b79' }}>{t('p_send_bcc')}</b>
            <span>{d.bcc}</span>
          </div>

          <label style={lbl}>{t('p_send_to_label')}</label>
          <input style={inp} value={to} placeholder={t('p_send_to_ph')} onChange={e => setTo(e.target.value)} />
          {!d.hasCustomerEmail && (
            <label style={{ display: 'flex', gap: 7, alignItems: 'center', marginTop: 7, fontSize: 12.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={save} onChange={e => setSave(e.target.checked)} />
              {t('p_send_save_addr', { customerName: d.customerName })}
            </label>
          )}

          <label style={lbl}>{t('p_send_subject')}</label>
          <input style={inp} value={subject} onChange={e => setSubject(e.target.value)} />

          <label style={lbl}>{t('p_send_message')}</label>
          <textarea style={{ ...inp, minHeight: 300, fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical' }}
                    value={body} onChange={e => setBody(e.target.value)} />

          <label style={lbl}>{t('p_send_attachment')}</label>
          {/* Clickable: the merchant should read the actual PDF before it goes to their customer,
              not just see its filename. Server-side render, so it gets the spinner every slow
              button here gets. */}
          <button disabled={pdfBusy}
                  onClick={async () => { setPdfBusy(true); try { await openPdf(kind, doc.id, t); } finally { setPdfBusy(false); } }}
                  style={{ ...inp, display: 'flex', alignItems: 'center', gap: 9, textAlign: 'left',
                           cursor: pdfBusy ? 'progress' : 'pointer', background: '#f6f8f9',
                           borderStyle: 'dashed', color: '#27303B', fontWeight: 600 }}>
            {pdfBusy
              ? <span style={{ width: 13, height: 13, border: '2px solid #cfd8e0', borderTopColor: ACCENT,
                               borderRadius: '50%', display: 'inline-block', animation: 'mfspin .7s linear infinite', flex: 'none' }} />
              : <span aria-hidden style={{ fontSize: 15 }}>📄</span>}
            <span style={{ flex: 1 }}>{d.docNo}.pdf</span>
            <span style={{ fontSize: 12, color: ACCENT, fontWeight: 700 }}>
              {pdfBusy ? t('p_send_preparing') : t('p_send_preview')}
            </span>
          </button>
          <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 5 }}>
            {t('p_send_preview_hint')}
          </div>

          {!!d.sends?.length && (
            <div style={{ marginTop: 14, fontSize: 12.5, color: '#5b6b79' }}>
              <b>{t('p_send_history')}</b>
              {d.sends.map((s: any, i: number) => (
                <div key={i} style={{ marginTop: 3 }}>
                  {s.ok ? '✓' : <span style={{ color: '#D0021B' }}>✕</span>} {s.to_email} · {fmtDateTime(s.sent_at)}
                </div>
              ))}
            </div>
          )}

          {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button style={btnGhost} onClick={onClose}>{t('p_btn_cancel')}</button>
            <button style={{ ...btn, opacity: busy || !to.trim() || !d.replyTo ? .5 : 1 }}
                    disabled={busy || !to.trim() || !d.replyTo} onClick={send}>
              {busy ? t('p_send_sending') : t('p_send_btn')}
            </button>
          </div>
        </>)}
        {err && !d && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
      </div>
    </div>
  );
}

// ── Month / date filter (owner 2026-09-08) ──────────────────────────────────────────────────────
// "本月,上月,以及按照日期过滤" — on the invoice, quotation, receipt AND credit/debit note lists.
// Months are computed in the browser's own calendar and sent as plain dates, so the server filters
// on the very column each list is ordered and displayed by.
const monthRange = (back: number): [string, string] => {
  const n = new Date(); const d = new Date(n.getFullYear(), n.getMonth() - back, 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const iso = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return [iso(d), iso(end)];
};
function DateFilter({ from, to, onChange }: { from: string; to: string; onChange: (f: string, t: string) => void }) {
  const { t } = useTranslation();
  const [f0, t0] = monthRange(0), [f1, t1] = monthRange(1);
  const on = (a: string, b: string) => from === a && to === b;
  // A range that is neither preset is the operator's own. It used to be the only thing the two
  // always-visible date inputs could express, and they cost ~296px of the row to say nothing most
  // of the time — the three presets answer almost every question. Now the range lives behind a
  // chip that, when set, states itself: "1 Sep – 16 Sep ✕". Before this you could not tell from the
  // toolbar whether a date filter was even active.
  const custom = !!(from || to) && !on(f0, t0) && !on(f1, t1);
  const [open, setOpen] = useState(false);
  const boxRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const chip = (active: boolean): React.CSSProperties => ({
    padding: '6px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    border: `1px solid ${active ? ACCENT : '#d3dbe2'}`, background: active ? ACCENT : '#fff', color: active ? '#fff' : '#5b6b79',
  });
  // Day + short month in the region's date locale — "16 Sep" reads at a glance where 2026-09-16 does not,
  // and the year is noise inside a range that is almost always inside one.
  const short = (iso: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || ''); if (!m) return '';
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))
      .toLocaleDateString(localeDate(), { day: 'numeric', month: 'short', timeZone: 'UTC' });
  };
  const rangeLabel = from && to ? `${short(from)} – ${short(to)}`
    : from ? `${t('p_flt_from')} ${short(from)}` : `${t('p_flt_to')} ${short(to)}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <button type="button" style={chip(!from && !to)} onClick={() => { setOpen(false); onChange('', ''); }}>{t('p_flt_all')}</button>
      <button type="button" style={chip(on(f0, t0))} onClick={() => { setOpen(false); onChange(f0, t0); }}>{t('p_flt_this_month')}</button>
      <button type="button" style={chip(on(f1, t1))} onClick={() => { setOpen(false); onChange(f1, t1); }}>{t('p_flt_last_month')}</button>
      <div ref={boxRef} style={{ position: 'relative' }}>
        <button type="button" onClick={() => setOpen(v => !v)}
          style={{ ...chip(false), display: 'inline-flex', alignItems: 'center', gap: 7,
                   ...(custom ? { borderColor: ACCENT, color: ACCENT } : null) }}>
          {custom ? rangeLabel : t('p_flt_custom')}
          {custom
            ? <span role="button" aria-label={t('p_flt_clear')} title={t('p_flt_clear')}
                onClick={e => { e.stopPropagation(); setOpen(false); onChange('', ''); }}
                style={{ color: '#98a4b0', fontWeight: 800 }}>✕</span>
            : <span style={{ color: '#98a4b0', fontSize: 9 }}>▼</span>}
        </button>
        {open && (
          <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 60, background: '#fff',
                        border: '1px solid #d3dbe2', borderRadius: 11, boxShadow: '0 10px 30px rgba(20,32,45,.17)',
                        padding: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="date" value={from} onChange={e => onChange(e.target.value, to)} title={t('p_flt_from')}
              style={{ ...inp, width: 148, padding: '5px 8px', fontSize: 12.5 }} />
            <span style={{ color: '#94a3b8', fontSize: 12 }}>–</span>
            <input type="date" value={to} onChange={e => onChange(from, e.target.value)} title={t('p_flt_to')}
              style={{ ...inp, width: 148, padding: '5px 8px', fontSize: 12.5 }} />
          </div>
        )}
      </div>
    </div>
  );
}

function DocsPage({ kind, profile, toExpenses }: { kind: 'invoices' | 'quotations' | 'receipts' | 'notes'; profile: any; toExpenses: () => void }) {
  const { t } = useTranslation();
  const K = kind === 'invoices' ? 'invoice' : kind === 'quotations' ? 'quotation' : kind === 'notes' ? 'note' : 'receipt';
  const vatReg = !!profile.vat_registered;
  const co: string | null = profile?.id || null;   // the merchant company — every document here is its own
  const plain = !thaiModeOf(profile);               // outside Thailand: no withholding, no 50 ทวิ, no import (merchantMode.ts)
  const [rows, setRows] = useState<any[]>([]);
  // The withholding column follows the DOCUMENTS (their stored mode): a merchant that has left Thailand
  // still sees it beside the Thai receipts it issued before; a plain row shows a dash.
  const showWht = !plain || rows.some(r => !docPlainOf(r));
  const [create, setCreate] = useState(false);
  const [imp, setImp] = useState(false);            // spreadsheet import (invoices only)
  // Voided documents are history. They keep their number forever, so they can never be deleted from
  // the sequence — but they do not belong in the daily view, where a greyed row reads as something
  // still owing. Off by default; the toggle is next to the period filter because that is the other
  // control that decides which rows exist.
  const [voided, setVoided] = useState(false);
  const [editDoc, setEditDoc] = useState<any>(null);
  const [busyEdit, setBusyEdit] = useState('');

  // The server decides whether a document may be edited — the same rules that guard the PATCH — so
  // the button can never offer something the law (or a linked receipt) forbids.
  const openEdit = async (r: any) => {
    setBusyEdit(r.id);
    try {
      const v: any = await api.portalDocEditable(K, r.id);
      if (!v.editable) { alert(v.reason || t('p_doc_not_editable')); return; }
      // The buyer may already hold this sheet, and its PDF (and the QR they can scan) re-renders
      // from the live record — so an edit rewrites what they can verify against paper in their hand.
      if (v.warnHandedOver && !window.confirm(t('p_doc_confirm_handed_over', { docNo: v.docNo }))) return;
      const full: any = await api.portalDoc(K, r.id);
      setEditDoc({ ...full, __needsReason: !!v.needsReason });
    } catch (e: any) { alert(e?.message || t('p_doc_err_open')); }
    finally { setBusyEdit(''); }
  };
  // One hidden file input per receipt row — the menu item triggers it by id.
  const whtInputs = React.useRef<Record<string, HTMLInputElement | null>>({});
  const [sendDoc, setSendDoc] = useState<any>(null);
  const [convDoc, setConvDoc] = useState<any>(null);
  // Outstanding / overdue for the whole company. Fetched once per company and per tab, not per
  // filter — see the band below for why it is deliberately not scoped to the visible rows.
  const [sum, setSum] = useState<any>(null);
  useEffect(() => {
    if (K !== 'invoice') { setSum(null); return; }
    let live = true;
    api.portalInvoiceSummary().then((d: any) => { if (live) setSum(d); }).catch(() => {});
    return () => { live = false; };
  }, [K]);
  const [page, setPage] = useState(1); const [per, setPerRaw] = useState(50); const [total, setTotal] = useState(0);
  const [q, setQRaw] = useState('');
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [sort, setSort] = useState(''); const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const setPer = (n: number) => { setPerRaw(n); setPage(1); };
  // A new search starts at page 1 — staying on page 4 of the previous result set would look empty.
  const setQ = (v: string) => { setQRaw(v); setPage(1); };
  // Clicking the active column flips direction; a new column starts descending (newest / largest
  // first, which is what people look for).
  const onSort = (id: string) => { if (sort === id) setDir(d => (d === 'asc' ? 'desc' : 'asc')); else { setSort(id); setDir('desc'); } setPage(1); };
  // A load that failed because the server was restarting (apiDown) is made again once the API answers (slf:api-back): the
  // same GET, once (review round 2: after "Back online — no need to refresh" the list stayed as the failed load left it).
  const loadDown = useRef(false);
  const load = (p = page, n = per, term = q, so = sort, di = dir, df = from, dt = to, vd = voided) => api.portalDocs(K, p, n, term, so, di, df, dt, vd)
    .then((d: any) => { loadDown.current = false; setRows(d.rows || []); setTotal(Number(d.total) || 0); })
    .catch((e: any) => { loadDown.current = !!e?.apiDown; });
  // One DocsPage serves all four kinds, so a new kind starts with no rows: a load that fails must not leave the invoices
  // on the Receipts tab, every one of them labelled "Paid" (review round 2).
  useEffect(() => { setRows([]); setTotal(0); setPage(1); setQRaw(''); setSort(''); setFrom(''); setTo(''); setCreate(false); load(1, per, '', '', 'desc', '', ''); }, [kind]); // eslint-disable-line
  useEffect(() => { load(page, per, q, sort, dir, from, to); }, [page, per, q, sort, dir, from, to]); // eslint-disable-line
  const reloadAfterDown = useRef(() => {});
  reloadAfterDown.current = () => { if (loadDown.current) load(page, per, q, sort, dir, from, to); };
  useEffect(() => {
    const onBack = () => reloadAfterDown.current();
    window.addEventListener('slf:api-back', onBack);
    return () => window.removeEventListener('slf:api-back', onBack);
  }, []);
  // A narrowed range can leave the reader on a page that no longer exists — start over at page 1.
  const setRange = (f: string, t2: string) => { setFrom(f); setTo(t2); setPage(1); };
  // Deleting the last row of a page can leave us past the end — step back rather than show an
  // empty table while rows still exist.
  useEffect(() => { const pages = Math.max(1, Math.ceil(total / per)); if (page > pages) setPage(pages); }, [total, per, page]);
  const stat = (r: any) => r.status === 'void' ? 'VOID' : K === 'receipt' ? 'Paid' : r.status === 'paid' ? 'Paid' : r.status === 'partial' ? 'Partial' : K === 'invoice' ? 'Unpaid' : 'Open';
  // Colour runs the other way round now (2026-09-25). It used to shout the money that had already
  // ARRIVED — Paid was the loudest green on the page, Unpaid the faintest grey — while 175 of the
  // 194 live merchant invoices are cash sales that are paid at issue. The table was therefore a
  // field of green in which the ten rows that actually owe money were the least visible things on
  // it. Paid now recedes; what is still owed carries full ink.
  //
  // Red appears in ONE place in this table: an overdue invoice (the +Nd pill and its Outstanding
  // figure). If red is ever used for anything else here, it stops meaning "the money is late".
  const statColor: Record<string, string> = { VOID: '#98a4b0', Paid: '#6B7C78', Partial: '#8A5A00', Unpaid: '#27303B', Open: '#5b6b79' };
  // ── What is still owed, and whether it is late ─────────────────────────
  // Both numbers were already on every row — the list is `SELECT *`, so net_to_pay, paid_amount and
  // due_date have always reached the browser. They were simply never printed, which is why a
  // partly-paid invoice showed its FACE VALUE next to the word "Partial": INV-202609-014 reads
  // 9,212.50 when what is actually owed on it is 450.00.
  //
  // net_to_pay, not total_amount: it is what the buyer transfers after withholding, and it is what
  // paid_amount is compared against everywhere else in the system (recomputeInvoicePaid).
  const owing = (r: any): number => ir2(num2(r.net_to_pay) - num2(r.paid_amount));

  // A missing due date means NOT OVERDUE, never a guess. 83 of the 194 live merchant invoices have
  // no due date; today every invoice that actually owes money has one, so this rule costs nothing,
  // but it will not stay free and a wrong red flag costs more trust than a missing one.
  const daysLate = (r: any): number => {
    const due = String(r.due_date || '');
    if (!due || owing(r) <= 0) return 0;
    const d = Math.floor((Date.now() - Date.parse(due + 'T00:00:00Z')) / 86400000);
    return d > 0 ? d : 0;
  };

  // The paired document's number, served by the list as `linked_no`.
  //
  // An empty cell is not the same thing in every case, so it is not always a dash. A cash sale
  // (Tax Invoice / Receipt) IS its own receipt — it books the payment at issue and by design never
  // gets a separate receipts row, and 175 of the 188 merchant invoices in the system are one. A
  // dash there would read as "nothing received"; the document says so itself instead.
  const linkedCell = (r: any) => {
    if (r.linked_no) return r.linked_no;
    if (K === 'invoice' && (r.doc_type === 'tax_invoice_receipt' || r.doc_type === 'receipt')) {
      return <span style={{ color: '#98a4b0' }}>{t('p_doc_link_self')}</span>;
    }
    return <span style={{ color: '#c8d2da' }}>—</span>;
  };
  // stat() keeps returning the code word — it is the key into statColor — and only the label is translated.
  const statLabel: Record<string, string> = { VOID: t('p_doc_status_void'), Paid: t('p_doc_status_paid'), Partial: t('p_doc_status_partial'), Unpaid: t('p_doc_status_unpaid'), Open: t('p_doc_status_open') };
  return (
    <div style={card}>
      {/* ── Where this company stands ─────────────────────────────────────
          This list has never printed a total, so the first question anyone brings to it — how much
          am I owed, and is any of it late — could only be answered by adding the rows up by hand.
          And the rows could not answer it anyway: a partly-paid invoice shows its FACE value, not
          its balance.

          Anchored to THIS COMPANY, EVERY PERIOD, AS OF TODAY. It deliberately does not follow the
          period or the search below it, because a figure that moved when you changed a filter could
          not be read as "what I am owed" — and the caption says so, since it will otherwise
          disagree with the rows in view. Hidden entirely when nothing is owed, which for a merchant
          whose invoices are all cash sales is most of the time. */}
      {/* ONE BLOCK PER CURRENCY. This band used to print `sum.outstanding` — a sum of net_to_pay
          across every currency on the merchant's books — under a hardcoded ฿. One merchant's whole
          receivable is a USD document, so its owner was reading "฿68,000" for USD 68,000. There is
          no rate feed to convert with, so the band shows each currency on its own and never a
          combined total. The oldest-overdue line stays on the LAST block only: it names one
          document, so repeating it under every currency would imply one per currency. */}
      {K === 'invoice' && sum && !!(sum.byCurrency || []).length && (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start',
                      padding: '13px 15px', marginBottom: 14, background: '#F8FAFA',
                      border: '1px solid #e3e7ea', borderRadius: 10 }}>
          {(sum.byCurrency as any[]).map((c, i) => (
            <React.Fragment key={c.currency}>
              {i > 0 && <div style={{ width: 1, alignSelf: 'stretch', background: '#e3e7ea' }} />}
              <div>
                <div style={{ fontSize: 11.5, color: '#7c8894' }}>
                  {t('p_sum_outstanding')}{(sum.byCurrency as any[]).length > 1 ? ` · ${c.currency}` : ''}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.01em', fontVariantNumeric: 'tabular-nums' }}>
                  {curAmount(c.currency, c.outstanding, co)}
                </div>
                <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 1 }}>{t('p_sum_n_inv', { count: c.outstandingCount })}</div>
              </div>
              {c.overdueCount > 0 && <>
                <div style={{ width: 1, alignSelf: 'stretch', background: '#e3e7ea' }} />
                <div>
                  <div style={{ fontSize: 11.5, color: '#7c8894' }}>{t('p_sum_overdue')}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.01em', color: '#B3261E', fontVariantNumeric: 'tabular-nums' }}>
                    {curAmount(c.currency, c.overdue, co)}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 1 }}>
                    {t('p_sum_n_inv', { count: c.overdueCount })}
                    {i === (sum.byCurrency as any[]).length - 1 && sum.oldestDocNo
                      ? ' · ' + t('p_sum_oldest', { days: sum.oldestDays, docNo: sum.oldestDocNo }) : ''}
                  </div>
                </div>
              </>}
            </React.Fragment>
          ))}
          <div style={{ flex: 1, minWidth: 8 }} />
          <div style={{ fontSize: 11, color: '#b6c0c8', alignSelf: 'flex-end', textAlign: 'right' }}>{t('p_sum_scope')}</div>
        </div>
      )}
      {/* Two rows, by role. They used to be one: title, three chips, two date inputs, search and two
          buttons, every pair separated by the same gap: 10 — about 1,136px of controls with no
          grouping, so nothing read as belonging together and the wrap point moved with the window.
          Row one is "what this is, and what I can do"; row two is "which period am I looking at",
          set lighter and ruled off. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 800, textTransform: 'capitalize' }}>{kind === 'invoices' ? t('p_doc_head_invoices') : kind === 'quotations' ? t('p_doc_head_quotations') : kind === 'receipts' ? t('p_doc_head_receipts') : t('p_doc_head_notes')}</div>
        <div style={{ flex: 1 }} />
        <SearchBox value={q} onChange={setQ} placeholder={t('p_doc_search_ph')} />
        {/* The importer only ever creates invoices / tax invoices, so it is offered on that tab
            alone. Smaller than the primary action: it is used about once a month. */}
        {K === 'invoice' && !plain && (
          <button style={{ ...btnGhost, padding: '6px 12px', fontSize: 12.5, fontWeight: 600 }}
            title={t('p_imp_open')} onClick={() => setImp(true)}>{t('p_imp_open_short')}</button>
        )}
        <button style={btn} onClick={() => setCreate(true)}>{K === 'receipt' ? t('p_doc_add_receipt') : K === 'note' ? t('p_doc_add_note') : t('p_doc_add_new', { docKind: K === 'invoice' ? t('p_doc_kind_invoice') : t('p_doc_kind_quotation') })}</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                    paddingTop: 11, borderTop: '1px solid #eef1f4', marginBottom: 14 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase',
                       color: '#98a4b0', marginRight: 4 }}>{t('p_flt_period')}</span>
        <DateFilter from={from} to={to} onChange={setRange} />
        <div style={{ flex: 1 }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                        color: '#5b6b79', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={voided}
            onChange={e => { const v = e.target.checked; setVoided(v); setPage(1); load(1, per, q, sort, dir, from, to, v); }} />
          {t('p_flt_show_voided')}
        </label>
      </div>

      {/* Documents this client RECEIVED — a supplier's bill, a receipt for something they paid —
          are not created here; they go to SLF to be read and booked. The owner asked for an upload
          on these tabs (2026-09-21) and, in the same breath, that a client must not upload their
          OWN past paperwork. Both hold if the tab points at the right door instead of growing a
          second one: the upload already exists, with OCR, duplicate detection and a review step,
          and a second entry into it would be a second set of rules to keep in step. */}
      {(K === 'invoice' || K === 'receipt') && !plain && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap',
                      margin: '-4px 0 12px', fontSize: 12.5, color: '#7c8894' }}>
          <span>{K === 'invoice' ? t('p_doc_got_bill') : t('p_doc_got_receipt')}</span>
          <button onClick={toExpenses}
            style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', cursor: 'pointer',
                     color: ACCENT, fontWeight: 700 }}>
            {t('p_doc_send_to_slf')}
          </button>
        </div>
      )}
      {/* The table may legitimately be wider than the card — nine columns, and Thai headers run
          longer than English ones, so its minimum width is not a number we control. It scrolls
          inside its own box rather than painting over the page, which is what it did once the Due
          and Outstanding columns were added. */}
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
        <thead><tr>
          <SortTh id="doc_no" label={t('p_doc_th_no')} sort={sort} dir={dir} onSort={onSort} />
          <SortTh id="date" label={t('p_doc_th_date')} sort={sort} dir={dir} onSort={onSort} />
          <SortTh id="client" label={t('p_doc_th_customer')} sort={sort} dir={dir} onSort={onSort} />
          {/* The document on the other side of the pairing (owner 2026-09-22): an invoice shows the
              receipt that settled it, a quotation the invoice raised from it, a receipt the invoice
              it is for. Notes keep the buyer's address — they already carry their link in the
              `Adjusts` column right beside this one. */}
          {K === 'note'
            ? <SortTh id="property" label={t('p_doc_th_property')} sort={sort} dir={dir} onSort={onSort} />
            : <SortTh id="linked" sort={sort} dir={dir} onSort={onSort}
                      label={K === 'invoice' ? t('p_doc_th_receipt_no') : t('p_doc_th_invoice_no')} />}
          {K === 'note' && <th style={th}>{t('p_doc_th_adjusts')}</th>}
          {/* Due date, invoices only. Without it the page cannot say whether anything is late —
              three days unpaid and ninety days unpaid printed the same grey word. */}
          {K === 'invoice' && <SortTh id="due" label={t('p_doc_th_due')} sort={sort} dir={dir} onSort={onSort} />}
          <SortTh id={K === 'receipt' ? 'amount' : 'total'} align="right" sort={sort} dir={dir} onSort={onSort}
                  label={K === 'receipt' ? t('p_doc_th_received') : K === 'note' ? t('p_doc_th_difference') : t('p_doc_th_total')} />
          {/* The number the client actually came for. */}
          {K === 'invoice' && <SortTh id="owing" label={t('p_doc_th_owing')} align="right" sort={sort} dir={dir} onSort={onSort} />}
          {K === 'receipt' && showWht && <SortTh id="wht" label={t('p_doc_th_wht')} align="right" sort={sort} dir={dir} onSort={onSort} />}
          <SortTh id="status" label={t('p_doc_th_status')} sort={sort} dir={dir} onSort={onSort} />
          <th style={th}>{t('p_doc_th_sent')}</th><th style={th}></th>
        </tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id} style={r.status === 'void' ? { opacity: .45 } : undefined}>
            <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.doc_no}</td>
            <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.issue_date || r.payment_date}</td>
            {/* Never wraps. A two-line customer makes that row taller than the rest, and the
                money columns stop being a column you can read down — which is the whole job of
                this table. The full name is in the tooltip. */}
            <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap' }} title={r.client_name}>{r.client_name}</td>
            {K === 'note'
              ? <td style={{ ...td, color: '#5b6b79' }}>{r.client_address || '—'}</td>
              : <td style={{ ...td, color: '#5b6b79', whiteSpace: 'nowrap' }}>{linkedCell(r)}</td>}
            {K === 'note' && <td style={{ ...td, fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: r.doc_type === 'credit_note' ? '#B42318' : '#15803D' }}>{r.doc_type === 'credit_note' ? t('p_doc_note_credit') : t('p_doc_note_debit')}</span>
              {' · '}{r.ref_doc_no || '—'}
            </td>}
            {K === 'invoice' && <td style={{ ...td, whiteSpace: 'nowrap', color: '#5b6b79', fontVariantNumeric: 'tabular-nums' }}>
              {String(r.due_date || '') || <span style={{ color: '#c8d2da' }}>—</span>}
              {daysLate(r) > 0 && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, fontSize: 11.5,
                                                 fontWeight: 700, color: '#B3261E', background: '#FCEDEC' }}>+{daysLate(r)}d</span>}
            </td>}
            <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                         color: K === 'invoice' && owing(r) <= 0 ? '#7c8894' : undefined }}>
              {K === 'note' && r.doc_type === 'credit_note' ? '−' : ''}{rowMoney(r, K === 'receipt' ? received(r) : num2(r.total_amount))}
            </td>
            {/* Outstanding is the heaviest ink in the row, because it is the answer. Settled rows
                print an em dash rather than 0.00 — a zero invites you to read it. */}
            {K === 'invoice' && <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                             fontWeight: owing(r) > 0 ? 700 : 400,
                                             color: daysLate(r) > 0 ? '#B3261E' : owing(r) > 0 ? '#27303B' : '#c8d2da' }}>
              {owing(r) > 0 ? rowMoney(r, owing(r)) : '—'}
            </td>}
            {K === 'receipt' && showWht && <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{docPlainOf(r) ? '—' : money(r.wht_amount, 2, co)}</td>}
            <td style={{ ...td, color: statColor[stat(r)], fontWeight: stat(r) === 'Paid' ? 500 : 700 }}>{statLabel[stat(r)]}</td>
            {/* Latest send. A document may go out several times; the composer lists the full history. */}
            <td style={{ ...td, fontSize: 12.5, color: '#5b6b79', whiteSpace: 'nowrap' }}>
              {r.last_sent_at
                ? <span title={t('p_doc_send_tooltip', { count: r.send_count, email: r.last_sent_to || '' })}>
                    ✓ {fmtDate(todayISO(new Date(r.last_sent_at * 1000)), { companyId: co })}
                    {r.send_count > 1 && <span style={{ color: '#98a4b0' }}> ×{r.send_count}</span>}
                  </span>
                : <span style={{ color: '#c8d2da' }}>—</span>}
            </td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>
              {r.status !== 'void' && <>
                {K === 'receipt' && !docPlainOf(r) && <input ref={el => { whtInputs.current[r.id] = el; }} type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                  onChange={async e => { const f = e.target.files?.[0];
                    if (f) { try { await api.portalUploadWht(r.id, f); alert(t('p_doc_wht_saved')); } catch (er: any) { alert(er?.message); } }
                    if (e.target) e.target.value = ''; }} />}
                <RowMenu actions={[
                  // Quotations only, and never on a void one. First in the list because billing an
                  // accepted quote is the whole point of having quoted.
                  { label: t('p_doc_act_to_invoice'), hidden: K !== 'quotation', onClick: () => setConvDoc(r) },
                  { label: t('p_doc_act_edit'), hidden: K === 'note', onClick: () => openEdit(r) },
                  { label: t('p_doc_act_pdf'), onClick: () => openPdf(K === 'note' ? 'invoice' : K, r.id, t) },
                  // Only where this office's API can actually send it (profile.features.emailDocs).
                  // A missing features object means an older/other deployment — hide, never 404.
                  { label: t('p_doc_act_send'), title: t('p_doc_act_send_title'),
                    hidden: !profile?.features?.emailDocs,
                    onClick: () => setSendDoc(r) },
                  { label: t('p_doc_act_attach_wht'), hidden: K !== 'receipt' || docPlainOf(r), title: t('p_doc_act_attach_wht_title'),
                    onClick: () => whtInputs.current[r.id]?.click() },
                  { label: t('p_doc_act_void'), danger: true, onClick: async () => {
                      const reason = window.prompt(t('p_doc_void_prompt', { docNo: r.doc_no }));
                      if (reason === null) return;
                      if (!reason.trim()) { alert(t('p_doc_reason_required')); return; }
                      try { await api.portalVoidDoc(K === 'note' ? 'invoice' : K, r.id, reason.trim()); load(); } catch (e: any) { alert(e?.message); }
                    } },
                  // Delete = give the number back. Offered only where it can ever succeed — the server
                  // is the real guard (tail of the series, nothing depending on it, books no tax line).
                  { label: t('p_doc_act_delete'), warn: true,
                    hidden: K === 'note' || (K === 'invoice' && (r.doc_type === 'tax_invoice' || r.doc_type === 'tax_invoice_receipt')),
                    onClick: async () => {
                      const reason = window.prompt(t('p_doc_delete_prompt', { docNo: r.doc_no }));
                      if (reason === null) return;
                      if (!reason.trim()) { alert(t('p_doc_reason_required')); return; }
                      try { const d: any = await api.portalReleaseDoc(K, r.id, reason.trim()); load(); alert(t('p_doc_freed', { docNo: d.freedNo })); }
                      catch (e: any) { alert(e?.message); }
                    } },
                ]} />
              </>}
            </td>
          </tr>))}
          {!rows.length && <tr><td style={{ ...td, color: '#98a4b0' }} colSpan={10}>{q ? t('p_empty_no_match', { query: q }) : K === 'note' ? t('p_doc_empty_notes') : t('p_doc_empty')}</td></tr>}
        </tbody>
      </table>
      </div>
      <Pager page={page} per={per} total={total} onPage={setPage} onPer={setPer} />
      {create && K !== 'receipt' && <CreateDocModal kind={K as 'invoice' | 'quotation'} vatReg={vatReg} profile={profile} onClose={() => setCreate(false)} onDone={() => { setCreate(false); load(); }} />}
      {create && K === 'receipt' && <CreateReceiptModal merchantId={co} plain={plain} onClose={() => setCreate(false)} onDone={() => { setCreate(false); load(); }} />}
      {create && K === 'note' && <CreateNoteModal vatReg={vatReg} merchantId={co} onClose={() => setCreate(false)} onDone={() => { setCreate(false); load(); }} />}
      {imp && <ImportModal onClose={() => setImp(false)} onDone={() => { setImp(false); load(); }} />}
      {editDoc && K !== 'receipt' && <CreateDocModal kind={K as 'invoice' | 'quotation'} vatReg={vatReg} profile={profile} existing={editDoc} onClose={() => setEditDoc(null)} onDone={() => { setEditDoc(null); load(); }} />}
      {editDoc && K === 'receipt' && <EditReceiptModal doc={editDoc} profile={profile} onClose={() => setEditDoc(null)} onDone={() => { setEditDoc(null); load(); }} />}
      {convDoc && <ConvertQuotationModal doc={convDoc} onClose={() => setConvDoc(null)}
        onDone={docNo => { setConvDoc(null); alert(t('p_conv_done', { docNo })); load(); }} />}
      {sendDoc && <SendDocModal kind={K === 'note' ? 'invoice' : K} doc={sendDoc}
        onClose={() => setSendDoc(null)} onSent={() => load()} />}
    </div>
  );
}


function CreateDocModal({ kind, vatReg, profile, existing, onClose, onDone }: { kind: 'invoice' | 'quotation'; vatReg: boolean; profile: any; existing?: any; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const co: string | null = profile?.id || null;   // the merchant company: its amounts' format
  // The doc-kind word is a code value ('invoice' / 'quotation') that surfaces in headings and
  // buttons, so it needs a real label map before it can be interpolated into a sentence.
  const kindLabel = t(kind === 'invoice' ? 'p_doc_kind_invoice' : 'p_doc_kind_quotation');
  const editing = !!existing;
  const banks: any[] = profile.banks || [];
  // A merchant outside Thailand (merchantMode.ts): an ordinary invoice only, a typed tax rate per line,
  // no withholding, no Thai names, no FX-to-baht; its own currency and its own calendar day.
  // An EDIT stays in the world the document was issued in (its stored mode), whatever the merchant's
  // country is today — the server re-prices it the same way.
  const plain = existing ? docPlainOf(existing) : !thaiModeOf(profile);
  // Q11: the merchant's own currency is home — a Thai document of a merchant that has since left
  // Thailand keeps the Thai home (baht).
  const home = !plain && !thaiModeOf(profile) ? String(profile.thaiCurrency || currencyCode(co)) : currencyCode(co);
  const day0 = plain ? merchantToday(co) : today();
  const defWht = plain ? 0 : Number(profile.default_wht_rate) || 0;
  const [customers, setCustomers] = useState<any[]>([]); const [products, setProducts] = useState<any[]>([]);
  const [customerId, setCustomerId] = useState(existing?.client_id || '');
  // s.86/4 (final paragraph): a TAX INVOICE is made out in Thai. When a party has no Thai name on
  // file the English one still prints — a document due today must not be blocked — and the form
  // says so, because adding it is the only way the next one comes out right (owner 2026-09-17).
  const [customerNameTh, setCustomerNameTh] = useState(String(existing?.client_name_th || ''));
  const [issueDate, setIssueDate] = useState(existing?.issue_date || day0);
  const [date2, setDate2] = useState(
    existing ? (existing.due_date || existing.valid_until || (kind === 'invoice' ? addDays(existing.issue_date || day0, PAYMENT_TERM_DAYS) : ''))
             : (kind === 'invoice' ? addDays(day0, PAYMENT_TERM_DAYS) : ''));
  // Once the operator sets a due date by hand it stops following the issue date; until then it
  // tracks it, so changing the issue date does not silently leave a stale deadline behind.
  const [dueTouched, setDueTouched] = useState(!!(existing && existing.due_date));
  // Default to the first account on file so a document never goes out without payment details.
  const [bankAccountId, setBankAccountId] = useState(existing?.bank_account_id || banks[0]?.id || '');
  // Which document: a service business bills and the VAT follows at payment (Revenue Code s.78/1);
  // a goods seller owes the VAT at delivery, so the tax invoice IS the delivery document (s.78).
  const [docType, setDocType] = useState(existing?.doc_type || (plain ? 'invoice' : profile.biz_type === 'goods' ? 'tax_invoice' : 'invoice'));
  const [currency, setCurrency] = useState(existing?.currency || home);
  const [paymentMethod, setPaymentMethod] = useState(String(existing?.payment_method || ''));
  const [fxRate, setFxRate] = useState(existing?.fx_rate ? String(existing.fx_rate) : ''); const [fxDate, setFxDate] = useState(existing?.fx_date || day0);
  // A plain line's tax rate is the rate stored on it (the merchant typed it).
  const plainRate = (it: any) => (plain ? { taxRate: Number(it.vat_rate) || 0 } : {});
  const blankLine = () => (plain ? { name: '', quantity: 1, unitPrice: 0, vatCategory: 'none', whtRate: 0, taxRate: 0 }
    : { name: '', quantity: 1, unitPrice: 0, vatCategory: vatReg ? 'standard' : 'none', whtRate: defWht });
  const [items, setItems] = useState<any[]>(
    existing?.items?.length
      ? existing.items.map((it: any) => ({ name: it.name || '', description: it.description || '', quantity: Number(it.quantity) || 0,
          unitPrice: Number(it.unit_price) || 0, vatCategory: it.vat_category || (Number(it.vat_amount) ? 'standard' : 'none'), whtRate: Number(it.wht_rate) || 0, ...plainRate(it) }))
      : [blankLine()]);
  const [remark, setRemark] = useState(existing?.remark || ''); const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const { onImage: onRemarkImage, seed: seedRemark, finalise: finaliseRemark } = useRemarkImages();
  // Once, for the document this dialog was opened on: swap the stored image URLs for object URLs
  // so the editor can actually show them. The editor re-seeds itself when `value` changes.
  useEffect(() => {
    let alive = true;
    seedRemark(String(existing?.remark || '')).then(h => { if (alive) setRemark(c => (c === (existing?.remark || '') && h !== c ? h : c)); });
    return () => { alive = false; };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.portalCustomers().then((d: any) => setCustomers(d.rows || []));
    api.portalProducts().then((d: any) => setProducts(d.rows || []));
  }, []);
  const set = (i: number, p: any) => setItems(prev => prev.map((it, j) => j === i ? { ...it, ...p } : it));
  // Choosing the service sets the line's name. It must NOT reset the amount: catalogue prices are 0
  // for services billed at a different figure every month, so copying the price over would silently
  // wipe what the operator already typed. The price follows only onto a still-empty line.
  const pickService = (i: number, name: string) => {
    const p = products.find(x => x.name === name);
    const cur = items[i] || {};
    const patch: any = { name };
    if (p && plain) {
      // Plain: the catalogue's Thai VAT flag and withholding rate do not apply; only the price follows.
      if (!Number(cur.unitPrice) && Number(p.unit_price) > 0) patch.unitPrice = p.unit_price;
    } else if (p) {
      // The catalog only knows a boolean, so it must not stomp a line the operator already set to
      // inclusive — keep whichever 7% mode the line is on and only decide taxed vs not-taxed here.
      const taxed = vatReg && p.vat_applicable;
      patch.vatCategory = taxed ? (cur.vatCategory === 'standard_incl' ? 'standard_incl' : 'standard') : 'none';
      if (!Number(cur.unitPrice) && Number(p.unit_price) > 0) patch.unitPrice = p.unit_price;
      if (!Number(cur.whtRate) && Number(p.wht_rate)) patch.whtRate = Number(p.wht_rate);
    }
    set(i, patch);
  };
  // Only the standard-rated lines carry VAT: zero-rated exports (s.80/1) are taxable at 0%, and
  // exempt / non-VAT lines carry none — three different things that all add nothing here.
  const { preVat, vat, wht } = plain ? plainLineTotals(items) : lineTotals(items, vatReg);
  // What the operator typed, before any inclusive split — shown so an inclusive document can be
  // checked against the figure it was entered from.
  const grossTyped = items.reduce((s, it) => r2(s + r2((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0))), 0);
  const anyIncl = items.some(it => it.vatCategory === 'standard_incl');
  const submit = async () => {
    setBusy(true); setErr('');
    const body: any = { customerId, issueDate, items, remark: finaliseRemark(remark), bankAccountId, currency, paymentMethod };
    if (!plain && currency !== home) { body.fxRate = Number(fxRate) || 0; body.fxDate = fxDate; }
    if (kind === 'invoice') { body.dueDate = date2; body.docType = plain ? 'invoice' : docType; } else body.validUntil = date2;
    if (items.some(it => !String(it.name || '').trim())) { setErr(t('p_cdoc_err_line')); setBusy(false); return; }
    if (plain && items.some(it => !plainRateOk(it.taxRate))) { setErr(t('pi_cdoc_err_rate')); setBusy(false); return; }
    if (kind === 'invoice' && !date2) { setErr(t('p_cdoc_err_due')); setBusy(false); return; }
    if (kind === 'invoice' && date2 < issueDate) { setErr(t('p_cdoc_err_due_before')); setBusy(false); return; }
    // Correcting an already-issued tax document is a bookkeeping event: the ledger line is rewritten
    // underneath and the buyer may hold a sheet that no longer matches, so it must say why.
    if (editing && existing?.__needsReason) {
      const why = window.prompt(t('p_cdoc_correct_prompt', { docNo: existing.doc_no }));
      if (why === null) { setBusy(false); return; }
      if (!why.trim()) { setErr(t('p_cdoc_err_reason')); setBusy(false); return; }
      body.reason = why.trim();
    }
    try {
      // Editing keeps the document's own number — a new number would break the continuous series.
      if (editing) await api.portalUpdateDoc(kind, existing.id, body);
      else if (kind === 'invoice') await api.portalCreateInvoice(body);
      else await api.portalCreateQuotation(body);
      onDone();
    } catch (e: any) { setErr(e?.message || (editing ? t('p_err_save_failed') : t('p_err_create_failed'))); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: '5vh 12px' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...card, width: 980, maxWidth: '96vw' }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4, textTransform: 'capitalize' }}>{editing ? t('p_cdoc_title_edit', { docKind: kindLabel, docNo: existing.doc_no }) : t('p_cdoc_title_new', { docKind: kindLabel })}</div>
        {editing && <div style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '7px 10px', marginBottom: 8 }}>
          {t('p_cdoc_edit_note', { docNo: existing.doc_no })}
        </div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 2 }}><label style={lbl}>{t('p_cdoc_customer')}</label>
            <CustomerPicker plain={plain} valueId={customerId} onChange={(id, row) => { setCustomerId(id); if (row) setCustomerNameTh(String(row.name_th || '')); }} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>{t('p_cdoc_issue_date')}</label>
            <input style={inp} type="date" value={issueDate} onChange={e => {
              setIssueDate(e.target.value);
              if (kind === 'invoice' && !dueTouched) setDate2(addDays(e.target.value, PAYMENT_TERM_DAYS));
            }} /></div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>{kind === 'invoice' ? t('p_cdoc_due_date') : t('p_cdoc_valid_until')}
              {kind === 'invoice' && <span style={{ fontWeight: 400, color: '#7c8894' }}> {t('p_cdoc_due_hint', { days: PAYMENT_TERM_DAYS })}</span>}</label>
            <input style={{ ...inp, ...(kind === 'invoice' && !date2 ? { borderColor: '#D0021B' } : {}) }} type="date" value={date2}
                   onChange={e => { setDate2(e.target.value); setDueTouched(true); }} /></div>
        </div>
        {plain && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 2, fontSize: 12, color: '#5b6b79', paddingBottom: 8 }}>{t('pi_cdoc_plain_hint')}</div>
            <div style={{ flex: 1 }}><label style={lbl}>{t('p_cdoc_currency')}</label>
              <select style={inp} value={currency} onChange={e => setCurrency(e.target.value)}>
                {plainCurrencies(home, [...COMMON_CURRENCIES, currency], profile.packCurrencies).map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
          </div>
        )}
        {kind === 'invoice' && !plain && (
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 2 }}><label style={lbl}>{t('p_cdoc_doc_type')}{editing && <span style={{ fontWeight: 400, color: '#7c8894' }}> {t('p_cdoc_doc_type_locked')}</span>}</label>
              {/* The server ignores docType on edit: switching a bill into a tax invoice would move
                  the tax point and mint the wrong number series. Disable it rather than let the
                  merchant change it, hit Save, and see nothing happen. */}
              <select style={{ ...inp, ...(editing ? { background: '#f2f5f7', color: '#5b6b79' } : {}) }} disabled={editing}
                      value={docType} onChange={e => setDocType(e.target.value)}>
                <option value="invoice">{t('p_cdoc_type_invoice')}</option>
                <option value="tax_invoice">{t('p_cdoc_type_tax_invoice')}</option>
                <option value="tax_invoice_receipt">{t('p_cdoc_type_tir')}</option>
                <option value="proforma">{t('p_cdoc_type_proforma')}</option>
              </select></div>
            {(docType === 'tax_invoice' || docType === 'tax_invoice_receipt') && (!String(profile.name_th || '').trim() || (!!customerId && !customerNameTh.trim())) && (
              <div style={{ flexBasis: '100%', fontSize: 12, color: '#B45309', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '7px 11px', fontWeight: 600 }}>
                ⚠ {t('p_cdoc_need_th_name', {
                  who: [!String(profile.name_th || '').trim() ? t('p_cdoc_th_seller') : '', (!!customerId && !customerNameTh.trim()) ? t('p_cdoc_th_buyer') : ''].filter(Boolean).join(' + '),
                })}
              </div>
            )}
            <div style={{ flex: 1 }}><label style={lbl}>{t('p_cdoc_currency')}</label>
              <select style={inp} value={currency} onChange={e => setCurrency(e.target.value)}>
                {['THB', 'USD', 'EUR', 'GBP', 'SGD', 'AUD', 'JPY', 'CNY', 'HKD', 'CHF'].map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
          </div>
        )}
        {/* How the buyer settles. Cross-border deals are agreed on an instrument, not just a due
            date: T/T is a transfer the buyer makes, while L/C, SBLC and BG are undertakings by a
            bank, so which one applies decides when the seller is actually safe to ship. Offered on
            quotations too — it is a term being quoted, not an afterthought at invoicing time.
            Blank is the default and stays blank: no existing document states one. */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}><label style={lbl}>{t('p_cdoc_pay_method')}</label>
            <select style={inp} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
              <option value="">{t('p_cdoc_pay_method_none')}</option>
              <option value="TT">{t('p_cdoc_pm_tt')}</option>
              <option value="LC">{t('p_cdoc_pm_lc')}</option>
              <option value="SBLC">{t('p_cdoc_pm_sblc')}</option>
              <option value="BG">{t('p_cdoc_pm_bg')}</option>
            </select></div>
          <div style={{ flex: 2, fontSize: 11.5, color: '#7c8894', paddingBottom: 8 }}>
            {paymentMethod ? t('p_cdoc_pay_method_note') : t('p_cdoc_pay_method_blank')}
          </div>
        </div>
        {!plain && currency !== home && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}><label style={lbl}>{t('p_cdoc_fx_rate', currencyArgs(currencyCode(co)))}</label><input style={inp} type="number" value={fxRate} onChange={e => setFxRate(e.target.value)} placeholder={t('p_cdoc_fx_ph')} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>{t('p_cdoc_fx_date')}</label><input style={inp} type="date" value={fxDate} onChange={e => setFxDate(e.target.value)} /></div>
            {/* Revenue Code s.86/4 is Thai law: the Thai-baht note is for Thai merchants only (owner Q18). */}
            {isThaiCountry(profile?.docRegion || profile?.region) && <div style={{ flex: 2, fontSize: 11.5, color: '#B45309', paddingBottom: 8 }}>
              {t('p_cdoc_fx_note')}
            </div>}
          </div>
        )}
        <label style={lbl}>{t('p_cdoc_bank_label', { docKind: kindLabel })}</label>
        <select style={inp} value={bankAccountId} onChange={e => setBankAccountId(e.target.value)}>
          {!banks.length && <option value="">{t('p_cdoc_bank_none')}</option>}
          {banks.map(b => <option key={b.id} value={b.id}>{b.currency} · {b.bank_address} · {b.account_number}</option>)}
          <option value="cash">{t('p_cdoc_bank_cash')}</option>
        </select>
        <label style={lbl}>{t('p_cdoc_items')}</label>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ width: 20, textAlign: 'right', fontSize: 13, color: '#5b6b79', flex: 'none' }}>{i + 1}</span>
            {/* The service IS the line. A line whose stored name is not in the catalog (older
                documents carry "Service — Property") keeps its own entry at the top, so opening an
                issued invoice can never silently rewrite what it says. */}
            <select style={{ ...inp, flex: '2 1 260px', minWidth: 220 }} value={it.name || ''} onChange={e => pickService(i, e.target.value)}>
              <option value="">{t('p_cdoc_choose_service')}</option>
              {it.name && !products.some(p => p.name === it.name) && <option value={it.name}>{it.name}</option>}
              {products.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
            <input style={{ ...inp, width: 70 }} type="number" title={t('p_cdoc_qty_title')} value={it.quantity} onChange={e => set(i, { quantity: e.target.value })} />
            <input style={{ ...inp, width: 110 }} type="number" title={t('p_cdoc_unitprice_title')} value={it.unitPrice} onChange={e => set(i, { unitPrice: e.target.value })} />
            {vatReg && !plain && (
              <select style={{ ...inp, width: 168 }} value={it.vatCategory || 'standard'} onChange={e => set(i, { vatCategory: e.target.value })}
                      title={vatOptions(t).find(o => o.value === (it.vatCategory || 'standard'))?.hint || t('p_cdoc_vat_title')}>
                {vatOptions(t).map(o => <option key={o.value} value={o.value} title={o.hint}>{o.label}</option>)}
              </select>
            )}
            {plain && (
              <input style={{ ...inp, width: 96, ...(plainRateOk(it.taxRate) ? {} : { borderColor: '#D0021B' }) }} type="number" min={0} max={100} step="any"
                     title={t('pi_cdoc_tax_rate_title')} placeholder={t('pi_cdoc_tax_rate')} aria-label={t('pi_cdoc_tax_rate')}
                     value={it.taxRate ?? ''} onChange={e => set(i, { taxRate: e.target.value })} />
            )}
            {!plain && whtSelect(it.whtRate, v => set(i, { whtRate: v }), { ...inp, width: 108 }, t)}
            <button style={{ ...btnGhost, padding: '5px 9px', color: '#D0021B' }} onClick={() => setItems(prev => prev.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
        <button style={{ ...btnGhost, padding: '6px 12px' }} onClick={() => setItems(prev => [...prev, blankLine()])}>{t('p_cdoc_add_line')}</button>
        <div style={{ textAlign: 'right', fontSize: 13.5, marginTop: 10, lineHeight: 1.7 }}>
          {anyIncl && <div style={{ color: '#5b6b79', fontSize: 12.5 }}>{t('p_cdoc_entered_incl', { amount: money(grossTyped, 2, co) })}</div>}
          {t('p_cdoc_subtotal')} {money(preVat, 2, co)}{plain ? <> · {t('pi_cdoc_tax')} {money(vat, 2, co)}</> : vatReg && <> · {t('p_cdoc_vat')} {money(vat, 2, co)}</>} · <b>{t('p_cdoc_total')} {currency} {money(r2(preVat + vat), 2, co)}</b>
          {wht > 0 && <div style={{ color: '#B45309' }}>{t('p_cdoc_less_wht', { amount: money(wht, 2, co) })} · <b>{t('p_cdoc_net')} {currency} {money(preVat + vat - wht, 2, co)}</b></div>}
        </div>
        <label style={lbl}>{t('p_cdoc_remark')}</label>
        <RichTextEditor value={remark} onChange={setRemark} onImage={onRemarkImage} extras minHeight={72}
                        placeholder={t('p_cdoc_remark_ph')} />
        {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
          <button style={btnGhost} onClick={onClose}>{t('p_btn_cancel')}</button>
          <button style={{ ...btn, opacity: busy || !customerId ? .5 : 1 }} disabled={busy || !customerId} onClick={submit}>{busy ? (editing ? t('p_cdoc_saving') : t('p_cdoc_issuing')) : (editing ? t('p_cdoc_save_changes') : t('p_cdoc_issue_btn', { docKind: kindLabel }))}</button>
        </div>
      </div>
    </div>
  );
}


// ── How we keep your books ──────────────────────────────────────────────────────────────────────
// Owner 2026-10-02: 其实我是想让终端客户能看到我们公司一些记账规则 ... 这些展示都必须是只读的.
//
// Every sentence on this page was checked against the production code before it was written, and
// several candidates were CUT because the code could not support them — most of what survived is
// what the system REFUSES to do, which turned out to be the most defensible material there is.
// Two rules for anyone editing this page:
//   · state behaviour, not law. Almost every statutory reference in the codebase is a developer's
//     paraphrase in a comment, not a sourced quotation, so none of them appear here.
//   · the depreciation ceilings are NOT typed here. They are read from fixed_asset_classes, the
//     same table the calculation uses, so a rate cannot be changed in the books while this page
//     goes on quoting the old one. Those rows carried a "verify before client use" warning until
//     the owner reviewed and approved them on 2026-10-02; the approval is recorded on every row.
function BookPolicy() {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  useEffect(() => { api.portalBooksPolicy().then(setD).catch(() => setD({ assetClasses: [] })); }, []);

  const Section = ({ n, title, body, children }: { n: number; title: string; body: string; children?: React.ReactNode }) => (
    <div style={{ background: '#fff', border: '1px solid #e3e7ea', borderRadius: 12, padding: '16px 18px', marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
        <div style={{ flex: 'none', width: 22, height: 22, borderRadius: 6, background: '#EEF3F2', color: ACCENT,
                      fontSize: 11.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</div>
        <div style={{ fontSize: 14.5, fontWeight: 800 }}>{title}</div>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.72, color: '#3d4a55', marginTop: 7, marginLeft: 32 }}>{body}</div>
      {children && <div style={{ marginLeft: 32, marginTop: 11 }}>{children}</div>}
    </div>
  );

  const S = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  return (
    <div>
      <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 3 }}>{t('p_pol_title')}</div>
      <div style={{ fontSize: 12.5, color: '#7c8894', marginBottom: 4, lineHeight: 1.65, maxWidth: 760 }}>{t('p_pol_lede')}</div>
      <div style={{ fontSize: 11.5, color: '#98a4b0', marginBottom: 16 }}>{t('p_pol_readonly')}</div>
      {S.map(n => (
        <Section key={n} n={n} title={t(`p_pol_s${n}_t`)} body={t(`p_pol_s${n}_b`)}>
          {n === 7 && d && (
            <>
              <table style={{ borderCollapse: 'collapse', fontSize: 12.5, minWidth: 340 }}>
                <tbody>
                  {(d.assetClasses || []).map((a: any) => (
                    <tr key={a.code}>
                      <td style={{ padding: '5px 16px 5px 0', color: '#3d4a55' }}>{a.nameEn}</td>
                      <td style={{ padding: '5px 0', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                   color: a.depreciable ? '#27303B' : '#98a4b0', whiteSpace: 'nowrap' }}>
                        {a.depreciable && a.maxRate != null ? `${a.maxRate}%` : t('p_pol_not_dep')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 8, lineHeight: 1.6 }}>{t('p_pol_s7_note')}</div>
            </>
          )}
        </Section>
      ))}
    </div>
  );
}

// ── The books themselves ────────────────────────────────────────────────────────────────────────
// Three read-only views over one endpoint set. The chart is a table because it IS the reference
// data; everything else is a PDF opened in its own tab, rendered by the very same functions that
// produce the firm's own copies — a client's trial balance and the one an inspector asks us for
// must be one document, not two that drifted apart.
//
// Note which of these takes a period and which does not: a chart of accounts belongs to the
// client, a trial balance belongs to a period. The server draws the same line, so the period
// selector below is deliberately absent on the chart.
function BooksPage({ kind }: { kind: 'chart' | 'ledgers' | 'statements' }) {
  const { t } = useTranslation();
  const [periods, setPeriods] = useState<any[]>([]);
  const [periodId, setPeriodId] = useState('');
  const [chart, setChart] = useState<any[]>([]);
  const [busy, setBusy] = useState('');
  // null while unknown — every card stays openable until the counts arrive, so a slow or failed
  // call degrades to the old behaviour rather than greying out books that do have entries.
  const [avail, setAvail] = useState<any[] | null>(null);

  useEffect(() => {
    if (kind === 'chart') { api.portalBooksChart().then(r => setChart(r.rows || [])).catch(() => setChart([])); return; }
    api.portalBooksPeriods().then(r => {
      const ps = r.periods || [];
      setPeriods(ps);
      // Default to the newest period that actually has posted entries — opening on an empty one
      // would look like the books are empty when an earlier period is full.
      const withEntries = ps.find((p: any) => p.entries > 0);
      setPeriodId((withEntries || ps[0] || {}).id || '');
    }).catch(() => setPeriods([]));
  }, [kind]);

  useEffect(() => {
    setAvail(null);
    if (kind === 'chart' || !periodId) return;
    let live = true;
    api.portalBooksAvailable(periodId)
      .then(r => { if (live) setAvail(r.books || []); })
      .catch(() => { if (live) setAvail(null); });
    return () => { live = false; };
  }, [kind, periodId]);

  const open = async (cardId: string, k: string, bankAccountId: string) => {
    setBusy(cardId);
    try { await api.portalOpenBook(k, periodId, bankAccountId); }
    catch (e: any) { alert(e?.message || 'failed'); }
    finally { setBusy(''); }
  };

  // This list mirrors the inspection pack (server/src/services/inspectionPack.ts) book for book, in
  // its order, under its names — the zip we hand an inspector and the list the client browses must
  // not be two different claims about which books this company keeps.
  //
  // The five journals were servable from the first day (routes/portalBooks.ts JOURNALS) and simply
  // were never listed here, so a client opened the page, counted four books and reasonably concluded
  // that was all we kept for them (owner, 2026-10-02). Adding a kind to the server is not what makes
  // it exist for the client; this array is.
  //
  // The Thai name is the book's own legal name, not a caption, so it is printed as-is in every UI
  // language and never translated (the same rule the statutory registers follow).
  const GROUPS: Record<string, { head: string; docs: { k: string; label: string }[] }[]> = {
    ledgers: [
      {
        head: 'บัญชีรายวัน · Journals',
        docs: [
          { k: 'cash', label: 'บัญชีเงินสด · Cash book' },
          { k: 'bank', label: 'บัญชีธนาคาร · Bank book' },
          { k: 'purchase', label: 'บัญชีรายวันซื้อ · Purchase journal' },
          { k: 'sale', label: 'บัญชีรายวันขาย · Sales journal' },
          { k: 'general', label: 'บัญชีรายวันทั่วไป · General journal' },
        ],
      },
      {
        head: 'บัญชีแยกประเภท · Ledgers',
        docs: [
          { k: 'general-ledger', label: 'บัญชีแยกประเภท · General ledger' },
          { k: 'ar', label: 'บัญชีย่อยลูกหนี้ · Debtors' },
          { k: 'ap', label: 'บัญชีย่อยเจ้าหนี้ · Creditors' },
        ],
      },
      // Not a statutory book — a working summary drawn off the ledger — so it carries no category
      // heading rather than being filed under one it does not belong to.
      { head: '', docs: [{ k: 'trial-balance', label: 'งบทดลอง · Trial balance' }] },
    ],
    statements: [
      {
        head: '',
        docs: [
          { k: 'sfp', label: 'Statement of financial position' },
          { k: 'pl', label: 'Profit or loss' },
          { k: 'sce', label: 'Changes in equity' },
        ],
      },
    ],
  };

  if (kind === 'chart') {
    const KL: Record<string, string> = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity', income: 'Income', expense: 'Expenses' };
    const order = ['asset', 'liability', 'equity', 'income', 'expense'];
    return (
      <div>
        <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 3 }}>{t('p_menu_chart')}</div>
        <div style={{ fontSize: 11.5, color: '#98a4b0', marginBottom: 16 }}>{t('p_pol_readonly')}</div>
        {!chart.length && <EmptyBooks />}
        {order.filter(k => chart.some(c => c.klass === k)).map(k => (
          <div key={k} style={{ background: '#fff', border: '1px solid #e3e7ea', borderRadius: 12, padding: '14px 17px', marginBottom: 10 }}>
            <div style={{ fontSize: 11.5, color: '#7c8894', fontWeight: 700, letterSpacing: '.02em', marginBottom: 8 }}>{KL[k] || k}</div>
            <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
              <tbody>
                {chart.filter(c => c.klass === k).map(c => (
                  <tr key={c.code} style={{ opacity: c.active ? 1 : .45 }}>
                    <td style={{ padding: '4px 14px 4px 0', fontVariantNumeric: 'tabular-nums', color: '#7c8894', width: 60 }}>{c.code}</td>
                    <td style={{ padding: '4px 14px 4px 0' }}>{c.nameEn}</td>
                    <td style={{ padding: '4px 0', color: '#98a4b0' }}>{c.nameTh}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  }

  const groups = GROUPS[kind] || [];

  // A journal with nothing posted in it STAYS on the page and says so, rather than disappearing.
  // The staff side drops empty books (server/src/routes/ledgerBooks.ts:66) and that is right for an
  // accountant, who wants the books they can print. It is wrong here: a client whose sales journal
  // vanished from the page cannot tell "they keep no sales journal for us" apart from "we made no
  // sales", and the first reading is the damaging one (owner, 2026-10-02).
  //
  // Only the five journals are gated. The ledgers, the trial balance and the statements are drawn
  // from every book at once, so "which book" does not apply to them, and the period selector
  // already shows the period's total.
  const JOURNAL_KINDS = new Set(['cash', 'bank', 'purchase', 'sale', 'general']);
  const countOf = (k: string, acct = ''): number | null => {
    if (!avail || !JOURNAL_KINDS.has(k)) return null;      // null = unknown, leave the card alone
    return avail
      .filter((b: any) => String(b.kind) === k && (!acct || String(b.bankAccountId) === acct))
      .reduce((n: number, b: any) => n + (Number(b.entries) || 0), 0);
  };
  // A bank book is kept PER ACCOUNT NUMBER. One card per account, each carrying its own id — that
  // id is what stops services/ledgerBooks.ts:116-119 reading a blank account as "no filter" and
  // merging every account into one volume whose cover names none of them. With no bank entries yet
  // there is nothing to split, so the plain card stands in and reports itself empty.
  const cardsOf = (docs: { k: string; label: string }[]) => docs.flatMap(doc => {
    const accts: string[] = doc.k === 'bank' && avail
      ? avail.filter((b: any) => String(b.kind) === 'bank' && String(b.bankAccountId))
             .map((b: any) => String(b.bankAccountId))
      : [];
    if (accts.length) {
      return accts.map(a => ({ id: `bank:${a}`, k: 'bank', acct: a, label: `${doc.label} · ${a}`, entries: countOf('bank', a) }));
    }
    return [{ id: doc.k, k: doc.k, acct: '', label: doc.label, entries: countOf(doc.k) }];
  });
  return (
    <div>
      <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 3 }}>{t(kind === 'ledgers' ? 'p_menu_ledgers' : 'p_menu_statements')}</div>
      <div style={{ fontSize: 11.5, color: '#98a4b0', marginBottom: 14 }}>{t('p_pol_readonly')}</div>
      {!periods.length && <EmptyBooks />}
      {!!periods.length && (
        <>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>{t('p_books_period')}</label>
            <select style={{ ...inp, maxWidth: 360 }} value={periodId} onChange={e => setPeriodId(e.target.value)}>
              {periods.map(p => (
                <option key={p.id} value={p.id}>{p.start} – {p.end} · {p.entries} · {p.status}</option>
              ))}
            </select>
          </div>
          {groups.map((g, gi) => (
            <div key={g.head || `g${gi}`} style={{ marginBottom: gi === groups.length - 1 ? 0 : 18 }}>
              {!!g.head && (
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#5b6b79', marginBottom: 8 }}>
                  {g.head}
                </div>
              )}
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                {cardsOf(g.docs).map(card => {
                  const empty = card.entries === 0;
                  return (
                    <button key={card.id} onClick={() => open(card.id, card.k, card.acct)}
                      disabled={!periodId || !!busy || empty}
                      style={{ textAlign: 'left', background: empty ? '#fafbfc' : '#fff',
                               border: '1px solid #e3e7ea', borderRadius: 12, padding: '15px 17px',
                               cursor: periodId && !empty ? 'pointer' : 'default',
                               fontSize: 13.5, fontWeight: 600, color: empty ? '#98a4b0' : '#27303B' }}>
                      {card.label}
                      <div style={{ fontSize: 11.5, color: '#98a4b0', fontWeight: 400, marginTop: 3 }}>
                        {empty ? t('p_books_no_entries') : busy === card.id ? '…' : 'PDF'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function EmptyBooks() {
  const { t } = useTranslation();
  return (
    <div style={{ background: '#fff', border: '1px solid #e3e7ea', borderRadius: 12, padding: '22px 20px' }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 5 }}>{t('p_books_none_t')}</div>
      <div style={{ fontSize: 13, color: '#5b6b79', lineHeight: 1.65 }}>{t('p_books_none_b')}</div>
    </div>
  );
}

// ── The Owner Console ───────────────────────────────────────────────────────────────────────────
// Owner 2026-10-02. The other nine tabs are organised by document type, which is how an accountant
// thinks; this one answers the four questions a business owner actually opens the portal to ask —
// who owes me, what do I owe, what is due next, what is waiting on me.
//
// It adds no new arithmetic of its own: every figure comes from GET /portal/owner/console, where
// the SQL and its caveats live. What this file is responsible for is NOT LYING WITH LAYOUT:
//   · a panel with no data is ABSENT, never a zero. "฿0 owed to you" and "we have never recorded a
//     sale for you" look identical and mean opposite things.
//   · receivables are listed PER CURRENCY and never totalled. The existing invoice summary card
//     sums across currencies under a hardcoded ฿, which today prints one merchant's USD 68,000 as
//     "฿68,000". There is no rate feed to convert with, so this does not pretend there is.
//   · a proforma is shown as part of what is owed but SAID so: it is a document the customer was
//     never formally billed on.
// A money cell that knows what currency its ROW is in. The document lists print one amount column
// for every document the merchant has, and those documents do not all share a currency — one live
// proforma is USD. Printing "68,000.00" under a column headed "Total (THB)" is not a cosmetic slip:
// it states the wrong figure. The home currency stays bare (it is the overwhelming majority and the symbol is in
// the header's place); anything else carries its code.
// A merchant document's amount in ITS company's region (the row's company_id = the merchant).
const rowMoney = (r: any, n: number) => {
  const co = r?.company_id || null;
  const c = String(r?.currency || currencyCode(co)).toUpperCase();
  return c === currencyCode(co) ? money(n, 2, co) : `${money(n, 2, co)} ${c}`;
};
const curAmount = (cur: string, n: number, companyId?: string | null) =>
  cur === currencyCode(companyId) ? moneyTight(n, 2, companyId) : `${money(n, 2, companyId)} ${cur}`;

function OwnerCard({ title, tone, children }: { title: string; tone?: 'warn'; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid ' + (tone === 'warn' ? '#f0d9b5' : '#e3e7ea'),
                  borderRadius: 12, padding: '15px 17px', minWidth: 248, flex: '1 1 280px' }}>
      <div style={{ fontSize: 11.5, color: '#7c8894', fontWeight: 700, letterSpacing: '.02em', marginBottom: 9 }}>{title}</div>
      {children}
    </div>
  );
}

function OwnerConsole({ merchantId }: { merchantId?: string | null }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.portalOwnerConsole().then(setD).catch((e: any) => setErr(e?.message || 'failed')); }, []);

  if (err) return <div style={{ color: '#D0021B', fontSize: 13 }}>{err}</div>;
  if (!d) return <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_loading')}</div>;

  const big = { fontSize: 25, fontWeight: 700, letterSpacing: '-.01em', fontVariantNumeric: 'tabular-nums' as const };
  const sub = { fontSize: 11.5, color: '#98a4b0', marginTop: 2 };
  const ar: any[] = d.receivables?.byCurrency || [];
  // Deadline kinds are printed from a fixed map, not from the raw column: 'pp30' on a page a client
  // reads is an internal code, and the client would have to know that it means VAT.
  const KIND: Record<string, string> = { pp30: 'VAT (ภ.พ.30)', pnd1: 'PND.1', pnd3: 'PND.3', pnd53: 'PND.53', sso: 'Social Security', pnd51: 'PND.51', pnd50: 'PND.50', audit: 'Audit', annual_fs: 'Annual accounts', bookkeeping: 'Bookkeeping', invoice_data_upload: t('p_own_task_upload'), tax_payment: t('p_own_task_tax_pay'), service_payment: t('p_own_task_svc_pay'), lead_collect_info: t('p_own_task_info') };
  const label = (k: string) => KIND[k] || k;

  const nothing = !ar.length && !d.owedToSlf && !d.upcoming?.length && !d.waiting?.length && !d.booked;

  return (
    <div>
      <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 3 }}>{t('p_own_title')}</div>
      <div style={{ fontSize: 12.5, color: '#7c8894', marginBottom: 16 }}>{t('p_own_lede')}</div>

      {nothing && (
        <div style={{ background: '#fff', border: '1px solid #e3e7ea', borderRadius: 12, padding: '22px 20px', fontSize: 13, color: '#5b6b79', lineHeight: 1.65 }}>
          {t('p_own_empty')}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {/* ── Owed to you. One block per currency; no grand total, ever. ── */}
        {ar.map(c => (
          <OwnerCard key={c.currency} title={t('p_own_owed_you')}>
            <div style={big}>{curAmount(c.currency, c.amount, merchantId)}</div>
            <div style={sub}>{t('p_sum_n_inv', { count: c.count })}</div>
            {c.overdue > 0 && (
              <div style={{ marginTop: 9, paddingTop: 9, borderTop: '1px dashed #e8ecef' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#B3261E', fontVariantNumeric: 'tabular-nums' }}>
                  {curAmount(c.currency, c.overdue, merchantId)} {t('p_own_overdue_suffix')}
                </div>
              </div>
            )}
          </OwnerCard>
        ))}

        {/* ── The oldest unpaid one, by name. A number alone is a grievance; a number with a
             document and a customer on it is a phone call someone can make this morning. ── */}
        {d.receivables?.oldest && (
          <OwnerCard title={t('p_own_chase')} tone="warn">
            <div style={{ fontSize: 15, fontWeight: 800 }}>{d.receivables.oldest.client}</div>
            <div style={{ fontSize: 13, color: '#5b6b79', marginTop: 3 }}>
              {d.receivables.oldest.docNo} · {curAmount(d.receivables.oldest.currency, d.receivables.oldest.amount, merchantId)}
            </div>
            <div style={{ fontSize: 12.5, color: '#B3261E', fontWeight: 700, marginTop: 5 }}>
              {t('p_own_days_late', { days: d.receivables.oldest.days })}
            </div>
          </OwnerCard>
        )}

        {/* ── What you owe SLF. Never the word "draft": in this table it means "nothing paid yet". ── */}
        {d.owedToSlf && (
          <OwnerCard title={t('p_own_owed_slf')}>
            <div style={big}>{moneyTight(d.owedToSlf.amount, 2, d.owedToSlf.companyId || null)}</div>
            <div style={sub}>{t('p_sum_n_inv', { count: d.owedToSlf.count })}</div>
            {d.owedToSlf.nextDue && <div style={{ ...sub, marginTop: 5 }}>{t('p_own_next_due', { date: d.owedToSlf.nextDue })}</div>}
          </OwnerCard>
        )}

        {/* ── Waiting on you. Past-due only: most client tasks are future-dated, and a count that
             includes them says "45 things" to someone who owes nothing today. ── */}
        {!!d.waiting?.length && (
          <OwnerCard title={t('p_own_waiting')} tone="warn">
            <div style={{ ...big, color: '#8A5A00' }}>{d.waitingCount ?? d.waiting.length}</div>
            <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {d.waiting.slice(0, 4).map((w: any, i: number) => (
                <div key={i} style={{ fontSize: 12.5, color: '#5b6b79' }}>
                  {label(w.kind)}{w.period ? ` · ${w.period}` : ''}
                </div>
              ))}
              {(d.waitingCount ?? d.waiting.length) > 4 && <div style={sub}>{t('p_own_and_more', { n: (d.waitingCount ?? d.waiting.length) - 4 })}</div>}
            </div>
          </OwnerCard>
        )}
      </div>

      {/* ── What is due next. These are the Revenue Department E-FILING dates, which are not the
           paper counter dates — the caption says so, because an owner who diaries the wrong one
           files late. An amount is shown only where one has actually been worked out. ── */}
      {!!d.upcoming?.length && (
        <div style={{ background: '#fff', border: '1px solid #e3e7ea', borderRadius: 12, padding: '15px 17px', marginTop: 12 }}>
          <div style={{ fontSize: 11.5, color: '#7c8894', fontWeight: 700, letterSpacing: '.02em', marginBottom: 10 }}>{t('p_own_upcoming')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {d.upcoming.map((u: any, i: number) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ width: 54, flex: 'none', fontSize: 12.5, fontWeight: 800, color: u.days <= 7 ? '#B3261E' : '#5b6b79', fontVariantNumeric: 'tabular-nums' }}>
                  {u.days <= 0 ? t('p_own_today') : t('p_own_in_days', { days: u.days })}
                </div>
                <div style={{ flex: 1, minWidth: 140, fontSize: 13.5, fontWeight: 600 }}>{label(u.kind)}</div>
                <div style={{ fontSize: 12.5, color: '#7c8894', fontVariantNumeric: 'tabular-nums' }}>{u.period} · {u.dueDate}</div>
                {/* A NEGATIVE figure here is not a negative payment — it is input VAT exceeding
                    output VAT, i.e. a credit carried forward to the next period. Q Beverages'
                    September ภ.พ.30 is -8,767.80 right now. Printed as "฿-8,767.80" under a column
                    of things to pay, it reads as money moving the wrong way; it is labelled
                    instead, and never coloured as though something were owed. */}
                <div style={{ fontSize: 13.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 90, textAlign: 'right' }}>
                  {u.amount == null
                    ? <span style={{ color: '#b6c0c8', fontWeight: 400, fontSize: 12 }}>{t('p_own_amount_tbc')}</span>
                    : u.amount < 0
                      ? <span style={{ color: '#6B7C78', fontWeight: 600, fontSize: 12.5 }}>{t('p_own_amount_credit', { amount: moneyTight(Math.abs(u.amount)) })}</span>
                      : moneyTight(u.amount)}
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#b6c0c8', marginTop: 10 }}>{t('p_own_efiling_note')}</div>
        </div>
      )}

      {/* ── Booked this year. Revenue and purchases, and deliberately NO net: the net would subtract
           payroll that is only present once a month has been frozen, so it reads as a loss that is
           really a gap in preparation. ── */}
      {(d.booked || d.filings) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
          {d.booked && (
            <OwnerCard title={t('p_own_booked', { year: d.year })}>
              <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 11.5, color: '#7c8894' }}>{t('p_own_revenue')}</div>
                  <div style={{ fontSize: 19, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{moneyTight(d.booked.revenue)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11.5, color: '#7c8894' }}>{t('p_own_purchases')}</div>
                  <div style={{ fontSize: 19, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{moneyTight(d.booked.purchases)}</div>
                </div>
              </div>
              <div style={{ ...sub, marginTop: 8, lineHeight: 1.5 }}>{t('p_own_booked_note', { months: d.booked.months })}</div>
            </OwnerCard>
          )}
          {d.filings && (
            <OwnerCard title={t('p_own_filed', { year: d.year })}>
              <div style={big}>{d.filings.done} <span style={{ fontSize: 15, fontWeight: 400, color: '#98a4b0' }}>/ {d.filings.total}</span></div>
              <div style={sub}>{t('p_own_filed_note')}</div>
            </OwnerCard>
          )}
        </div>
      )}
    </div>
  );
}

// ── Pictures in a document's Remark ───────────────────────────────────────────────────────
// A stored Remark carries `<img src="/api/portal/doc-images/…">`, and that is an authenticated GET:
// an <img> the browser loads by itself sends no Authorization header, so it would 401 and show as
// a broken icon. While the dialog is open every picture is therefore shown from an OBJECT URL —
// fetched with the token when the document is opened, made straight from the file when one is
// pasted — and the canonical URL is put back at save time.
//
// Putting it back matters: the server's sanitiser admits our own upload URLs and nothing else, so
// a blob: that reached it would not be stored as a broken image, it would be dropped entirely.
// (The same approach as Bugify's useInlineImages; see components/Bugify/index.tsx.)
const DOC_IMG_URL = /\/api\/portal\/doc-images\/[A-Za-z0-9-]{8,64}/g;
function useRemarkImages() {
  const map = useRef<Map<string, string>>(new Map());   // object URL → /api/portal/doc-images/<id>
  useEffect(() => () => { for (const u of Array.from(map.current.keys())) URL.revokeObjectURL(u); }, []);

  const onImage = useCallback(async (f: File) => {
    const r: any = await api.portalUploadDocImage(f);
    const local = URL.createObjectURL(f);              // shown instantly — no round trip to display it
    map.current.set(local, r.url);
    return local;
  }, []);

  /** Stored value → what the editor can show. Unchanged when it holds no pictures. */
  const seed = useCallback(async (html: string): Promise<string> => {
    const urls = Array.from(new Set(String(html || '').match(DOC_IMG_URL) || []));
    if (!urls.length) return html;
    let out = html;
    for (const u of urls) {
      // An image that will not load is left as it is rather than removed: dropping it here would
      // silently delete the picture from the document the moment anything else was saved.
      try { const local = await api.portalDocImageBlob(u); map.current.set(local, u); out = out.split(u).join(local); }
      catch { /* keep the canonical URL */ }
    }
    return out;
  }, []);

  /** What leaves the browser: every object URL back to the URL the server will serve. */
  const finalise = useCallback((html: string) => {
    let out = html;
    for (const [local, real] of Array.from(map.current.entries())) out = out.split(local).join(real);
    return out;
  }, []);

  return { onImage, seed, finalise };
}

// A receipt IS the tax invoice for a service business (s.78/1 + s.86/4), so its face — buyer, date,
// values, VAT — is not rewritable while the buyer holds a copy. Only the internal remark and which
// bank account is printed may be corrected; anything else is void-and-reissue.
function EditReceiptModal({ doc, profile, onClose, onDone }: { doc: any; profile: any; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const banks: any[] = profile.banks || [];
  const [remark, setRemark] = useState(doc.remark || '');
  const { onImage: onRemarkImage, seed: seedRemark, finalise: finaliseRemark } = useRemarkImages();
  useEffect(() => {
    let alive = true;
    seedRemark(String(doc.remark || '')).then(h => { if (alive) setRemark(c => (c === (doc.remark || '') && h !== c ? h : c)); });
    return () => { alive = false; };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const [bankAccountId, setBankAccountId] = useState(doc.bank_account_id || '');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true); setErr('');
    try { await api.portalUpdateDoc('receipt', doc.id, { remark: finaliseRemark(remark), bankAccountId }); onDone(); }
    catch (e: any) { setErr(e?.message || t('p_err_save_failed')); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: '5vh 12px' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...card, width: 560, maxWidth: '96vw' }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>{t('p_erec_title', { docNo: doc.doc_no })}</div>
        <div style={{ fontSize: 12.5, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '9px 11px', marginBottom: 12, lineHeight: 1.55 }}>
          {/* Outside Thailand a receipt is not a tax invoice (merchantMode.ts) — it only records money received. */}
          {docPlainOf(doc) ? t('pi_erec_note') : t('p_erec_warning')}
        </div>
        <div style={{ fontSize: 12.5, color: '#5b6b79', marginBottom: 10 }}>
          {doc.client_name} · {doc.payment_date || doc.issue_date} · {t('p_erec_received', { amount: money(doc.amount, 2, profile?.id) })}{Number(doc.wht_amount) ? ` ${t('p_erec_wht', { amount: money(doc.wht_amount, 2, profile?.id) })}` : ''}
        </div>
        <label style={lbl}>{t('p_erec_bank')}</label>
        <select style={inp} value={bankAccountId} onChange={e => setBankAccountId(e.target.value)}>
          <option value="">{t('p_erec_bank_none')}</option>
          {banks.map(b => <option key={b.id} value={b.id}>{b.account_name} · {b.account_number}</option>)}
        </select>
        <label style={lbl}>{t('p_erec_remark')}</label>
        <RichTextEditor value={remark} onChange={setRemark} onImage={onRemarkImage} extras minHeight={72}
                        placeholder={t('p_cdoc_remark_ph')} />
        {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
          <button style={btnGhost} onClick={onClose}>{t('p_btn_cancel')}</button>
          <button style={{ ...btn, opacity: busy ? .5 : 1 }} disabled={busy} onClick={save}>{busy ? t('p_erec_saving') : t('p_cdoc_save_changes')}</button>
        </div>
      </div>
    </div>
  );
}


// Issue a credit or debit note against a tax invoice (s.86/10 · s.86/9). It captures the three
// particulars a note must carry beyond a normal document: WHICH tax invoice, WHY, and the
// DIFFERENCE in value — never the whole supply again.
function CreateNoteModal({ vatReg, merchantId, onClose, onDone }: { vatReg: boolean; merchantId?: string | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [noteType, setNoteType] = useState<'credit_note' | 'debit_note'>('credit_note');
  const [sources, setSources] = useState<any[]>([]);
  const [refKey, setRefKey] = useState('');            // "kind:id"
  const [issueDate, setIssueDate] = useState(today());
  const [reason, setReason] = useState(''); const [reasonText, setReasonText] = useState('');
  const [items, setItems] = useState<any[]>([{ name: '', quantity: 1, unitPrice: 0, vatCategory: vatReg ? 'standard' : 'none', whtRate: 0 }]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');

  // Only documents that ARE tax invoices can be adjusted: the goods tax invoice (s.78) or, for a
  // service business, the payment receipt that carries the VAT (s.78/1).
  const loadSources = (term = '') => {
    Promise.all([api.portalDocs('invoice', 1, 50, term), api.portalDocs('receipt', 1, 50, term)]).then(([iv, rc]: any[]) => {
      const inv = (iv.rows || []).filter((r: any) => r.status !== 'void' && (r.doc_type === 'tax_invoice' || r.doc_type === 'tax_invoice_receipt'))
        .map((r: any) => ({ key: `invoice:${r.id}`, label: `${r.doc_no} · ${r.client_name} · ${money(r.total_amount, 2, merchantId)}`, date: r.issue_date }));
      const rec = (rc.rows || []).filter((r: any) => r.status !== 'void')
        .map((r: any) => ({ key: `receipt:${r.id}`, label: `${r.doc_no} · ${r.client_name} · ${money(r.amount, 2, merchantId)}`, date: r.payment_date || r.issue_date }));
      setSources([...inv, ...rec]);
    }).catch(() => {});
  };
  useEffect(() => { loadSources(''); }, []); // eslint-disable-line

  const REASONS: [string, string][] = noteType === 'credit_note'
    ? [['goods_returned', t('p_note_reason_goods_returned')], ['goods_defective', t('p_note_reason_goods_defective')],
       ['service_not_provided', t('p_note_reason_service_not_provided')], ['discount', t('p_note_reason_discount')],
       ['overcharge', t('p_note_reason_overcharge')], ['other', t('p_note_reason_other')]]
    : [['extra_goods', t('p_note_reason_extra_goods')], ['undercharge', t('p_note_reason_undercharge')], ['other', t('p_note_reason_other')]];
  useEffect(() => { setReason(''); }, [noteType]);

  const set = (i: number, patch: any) => setItems(prev => prev.map((it, j) => j === i ? { ...it, ...patch } : it));
  const { preVat, vat } = lineTotals(items, vatReg);
  const sign = noteType === 'credit_note' ? '−' : '+';
  const vatOpts = vatOptions(t);

  const submit = async () => {
    setBusy(true); setErr('');
    const [refKind, refId] = refKey.split(':');
    try {
      await api.portalCreateNote({ noteType, refKind, refId, issueDate, reason, reasonText, items });
      onDone();
    } catch (e: any) { setErr(e?.message || t('p_note_err')); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: '5vh 12px' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...card, width: 980, maxWidth: '96vw' }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>{t('p_note_title_new', { noteKind: noteType === 'credit_note' ? t('p_note_kind_credit') : t('p_note_kind_debit') })}</div>
        <div style={{ fontSize: 12.5, color: '#5b6b79', background: '#F8FAFC', border: '1px solid #e3e7ea', borderRadius: 8, padding: '9px 11px', marginBottom: 12, lineHeight: 1.55 }}>
          {t('p_note_info')}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><label style={lbl}>{t('p_note_type')}</label>
            <select style={inp} value={noteType} onChange={e => setNoteType(e.target.value as any)}>
              <option value="credit_note">{t('p_note_opt_credit')}</option>
              <option value="debit_note">{t('p_note_opt_debit')}</option>
            </select></div>
          <div style={{ flex: 1 }}><label style={lbl}>{t('p_note_issue_date')}</label><input style={inp} type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} /></div>
        </div>
        <label style={lbl}>{t('p_note_ref')}</label>
        <PickerSelect
          value={sources.find(s2 => s2.key === refKey) ? { id: refKey, label: sources.find(s2 => s2.key === refKey)!.label } : null}
          options={sources.map(s2 => ({ id: s2.key, label: s2.label, sub: s2.date }))}
          onSearch={term => loadSources(term)}
          onPick={o => setRefKey(o.id)}
          placeholder={t('p_select_placeholder')} />
        {!sources.length && <div style={{ fontSize: 12, color: '#B45309', marginTop: 4 }}>{t('p_note_no_sources')}</div>}
        <label style={lbl}>{t('p_note_reason_label')}</label>
        <select style={inp} value={reason} onChange={e => setReason(e.target.value)}>
          <option value="">{t('p_select_placeholder')}</option>
          {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {reason === 'other' && <input style={{ ...inp, marginTop: 6 }} placeholder={t('p_note_reason_other_ph')} value={reasonText} onChange={e => setReasonText(e.target.value)} />}
        <label style={{ ...lbl, marginTop: 10 }}>{t('p_note_difference_label')}</label>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ width: 20, textAlign: 'right', fontSize: 13, color: '#5b6b79', flex: 'none' }}>{i + 1}</span>
            {/* A note states a correction in free text — it is not billing a catalogue service, so
                this one keeps a description box rather than the service picker. */}
            <input style={{ ...inp, flex: '2 1 240px', minWidth: 200 }} placeholder={t('p_note_item_ph')} value={it.name} onChange={e => set(i, { name: e.target.value })} />
            <input style={{ ...inp, width: 74 }} type="number" value={it.quantity} onChange={e => set(i, { quantity: e.target.value })} />
            <input style={{ ...inp, width: 110 }} type="number" placeholder={t('p_note_amount_ph')} value={it.unitPrice} onChange={e => set(i, { unitPrice: e.target.value })} />
            {vatReg && (
              <select style={{ ...inp, width: 168 }} value={it.vatCategory || 'standard'} onChange={e => set(i, { vatCategory: e.target.value })}
                      title={vatOpts.find(o => o.value === (it.vatCategory || 'standard'))?.hint || t('p_cdoc_vat_title')}>
                {vatOpts.map(o => <option key={o.value} value={o.value} title={o.hint}>{o.label}</option>)}
              </select>
            )}
            <button style={{ ...btnGhost, padding: '5px 9px', color: '#D0021B' }} onClick={() => setItems(prev => prev.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
        <button style={{ ...btnGhost, padding: '6px 12px' }} onClick={() => setItems(prev => [...prev, { name: '', quantity: 1, unitPrice: 0, vatCategory: vatReg ? 'standard' : 'none', whtRate: 0 }])}>{t('p_cdoc_add_line')}</button>
        <div style={{ textAlign: 'right', fontSize: 13.5, marginTop: 10, lineHeight: 1.7 }}>
          {t('p_note_total_difference')} {sign}{money(preVat, 2, merchantId)}{vatReg && <> · {t('p_cdoc_vat')} {sign}{money(vat, 2, merchantId)}</>} · <b>{t('p_cdoc_total')} {sign}{money(preVat + vat, 2, merchantId)}</b>
        </div>
        {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
          <button style={btnGhost} onClick={onClose}>{t('p_btn_cancel')}</button>
          <button style={{ ...btn, opacity: busy || !refKey || !reason ? .5 : 1 }} disabled={busy || !refKey || !reason} onClick={submit}>{busy ? t('p_note_issuing') : t('p_note_issue_btn')}</button>
        </div>
      </div>
    </div>
  );
}

function CreateReceiptModal({ merchantId, plain, onClose, onDone }: { merchantId?: string | null; plain?: boolean; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<any[]>([]);
  // No "today" default (owner 2026-09-03): the receipt is dated and NUMBERED by the day the money
  // arrived, and the silent default put August collections into the September series.
  const [invoiceId, setInvoiceId] = useState(''); const [paymentDate, setPaymentDate] = useState('');
  const [cash, setCash] = useState(''); const [wht, setWht] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  useEffect(() => { api.portalUnpaidInvoices().then((d: any) => setInvoices(d.rows || [])); }, []);
  const inv = invoices.find(i => i.id === invoiceId);
  // A receipt belongs to its invoice's world (server merchantCreateReceipt): the picked invoice decides,
  // and before one is picked the merchant's own mode does.
  const plainDoc = inv ? docPlainOf(inv) : !!plain;
  const outCash = inv ? r2(Number(inv.net_to_pay || 0) - Number(inv.paid_amount || 0)) : 0;
  // WHT still to come, in proportion to what is still outstanding: a first, full payment carries
  // the whole of the invoice's WHT; a second instalment carries only its share.
  // A plain document records no withholding: the buyer pays the invoice in full.
  const outWht = !plainDoc && inv && Number(inv.net_to_pay) > 0
    ? r2(Number(inv.wht_amount || 0) * (outCash / Number(inv.net_to_pay))) : 0;

  // Picking an invoice fills both figures in — the common case is "they paid it in full". Both stay
  // editable for a part payment or a buyer who withheld a different amount than expected.
  const pick = (id: string) => {
    setInvoiceId(id);
    const i = invoices.find(x => x.id === id);
    if (!i) { setCash(''); setWht(''); return; }
    const c = r2(Number(i.net_to_pay || 0) - Number(i.paid_amount || 0));
    const w = !docPlainOf(i) && Number(i.net_to_pay) > 0 ? r2(Number(i.wht_amount || 0) * (c / Number(i.net_to_pay))) : 0;
    setCash(c > 0 ? String(c) : ''); setWht(w > 0 ? String(w) : '');
  };
  const settled = r2((Number(cash) || 0) + (Number(wht) || 0));
  // s.86/4: when this receipt will itself be the tax invoice (VAT-registered merchant, the invoice
  // was not a tax invoice) it names both parties in Thai. The server says which Thai names are
  // missing (receipt_th_gaps, the same rule the receipt prints with); the English one still prints,
  // and the form says so exactly as the invoice form does (owner 2026-10-03, Q9).
  const thGaps: string[] = Array.isArray(inv?.receipt_th_gaps) ? inv.receipt_th_gaps : [];
  const submit = async () => {
    setBusy(true); setErr('');
    try { await api.portalCreateReceipt({ invoiceId, paymentDate, cashAmount: Number(cash) || 0, whtAmount: Number(wht) || 0 }); onDone(); }
    catch (e: any) { setErr(e?.message || t('p_crec_err')); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...card, width: 760, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto' }}>
        <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 2 }}>{t('p_crec_title')}</div>
        <div style={{ fontSize: 12.5, color: '#7c8894', marginBottom: 10 }}>
          {plainDoc ? t('pi_crec_subtitle') : t('p_crec_subtitle')}
        </div>
        <label style={lbl}>{t('p_crec_invoice')}</label>
        <PickerSelect
          value={inv ? { id: inv.id, label: `${inv.doc_no} · ${inv.client_name}` } : null}
          options={invoices.map(i => ({ id: i.id, label: `${i.doc_no} · ${i.client_name}`,
            sub: t('p_crec_option_outstanding', { amount: money(Number(i.net_to_pay || 0) - Number(i.paid_amount || 0), 2, merchantId) }) }))}
          onSearch={term => { api.portalUnpaidInvoices(term).then((d: any) => setInvoices(d.rows || [])).catch(() => {}); }}
          onPick={o => pick(o.id)}
          placeholder={t('p_crec_select_invoice')} />
        {inv && (
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 8, padding: '9px 12px',
                        background: '#f2f5f7', borderRadius: 8, fontSize: 13 }}>
            <span>{t('p_crec_invoice_total')} <b>{money(inv.total_amount, 2, merchantId)}</b></span>
            {Number(inv.paid_amount) > 0 && <span>{t('p_crec_already_paid')} <b>{money(inv.paid_amount, 2, merchantId)}</b></span>}
            <span>{t('p_crec_outstanding')} <b>{money(outCash, 2, merchantId)}</b></span>
            {outWht > 0 && <span style={{ color: '#B45309' }}>{t('p_crec_expected_wht')} <b>{money(outWht, 2, merchantId)}</b></span>}
          </div>
        )}
        {thGaps.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#B45309', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '7px 11px', fontWeight: 600 }}>
            ⚠ {t('p_cdoc_need_th_name', {
              who: thGaps.map(g => t(g === 'seller' ? 'p_cdoc_th_seller' : 'p_cdoc_th_buyer')).join(' + '),
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
          <div style={{ flex: '1 1 190px', minWidth: 170 }}>
            <label style={lbl}>{t('p_crec_payment_date')}</label>
            <input style={inp} type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
            <div style={{ fontSize: 11.5, color: paymentDate ? '#5b6b79' : '#B45309', marginTop: 3 }}>
              {paymentDate ? t('p_crec_date_ok', { yyyymm: paymentDate.slice(0, 7) }) : t('p_crec_date_required')}
            </div>
          </div>
          <div style={{ flex: '1 1 190px', minWidth: 170 }}><label style={lbl}>{plainDoc ? t('pi_crec_cash') : t('p_crec_cash')}</label><input style={inp} type="number" value={cash} onChange={e => setCash(e.target.value)} /></div>
          {!plainDoc && <div style={{ flex: '1 1 190px', minWidth: 170 }}><label style={lbl}>{t('p_crec_wht')}</label><input style={inp} type="number" value={wht} onChange={e => setWht(e.target.value)} /></div>}
        </div>
        <div style={{ fontSize: 13, marginTop: 10, padding: '9px 12px', borderRadius: 8,
                      background: inv && Math.abs(settled - r2(outCash + outWht)) > 0.005 ? '#FEF3C7' : '#EAF6EF' }}>
          {plainDoc ? t('pi_crec_settling', { total: money(settled, 2, merchantId) })
            : t('p_crec_settling', { total: money(settled, 2, merchantId), cash: money(Number(cash) || 0, 2, merchantId), wht: money(Number(wht) || 0, 2, merchantId) })}
          {inv && Math.abs(settled - r2(outCash + outWht)) > 0.005 &&
            <> <span style={{ color: '#B45309' }}>{t('p_crec_differs', { amount: money(r2(outCash + outWht), 2, merchantId) })}</span></>}
        </div>
        {!plainDoc && <div style={{ fontSize: 12, color: '#5b6b79', marginTop: 6 }}>{t('p_crec_attach_hint')}</div>}
        {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
          <button style={btnGhost} onClick={onClose}>{t('p_btn_cancel')}</button>
          <button style={{ ...btn, opacity: busy || !invoiceId || !paymentDate ? .5 : 1 }} disabled={busy || !invoiceId || !paymentDate} onClick={submit}>{busy ? t('p_crec_saving') : t('p_crec_issue')}</button>
        </div>
      </div>
    </div>
  );
}

// ── "From SLF" — what the firm did FOR this client ───────────────────────────
// Two read-only tabs, deliberately document-only: they re-show paperwork the client has already
// received (their filed returns, our bills), never a newly computed figure. Everything is scoped
// server-side to req.merchant.merchant_client_id — the client id never travels in the request.

// Filing documents need the Bearer token, so they are fetched and opened as a blob, exactly like
// the portal's own PDFs.
const openAuthed = async (url: string, fallbackName: string, t: TFn) => {
  const res = await docFetch(url, 'p_filing_doc_gone', t);
  if (!res) return;
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const w = window.open(href, '_blank');
  if (!w) { const a = document.createElement('a'); a.href = href; a.download = fallbackName; a.click(); }
  setTimeout(() => URL.revokeObjectURL(href), 60000);
};

// ── Purchase / expense invoices ──────────────────────────────────────────────
// The merchant sends in the bills they PAID. Nothing here reaches their tax return by itself —
// every upload waits for SLF to check it against the scan, which is why the status column and the
// reason a rejected one came back are the most important things on the page.
const MONTH_KEYS = ['p_month_jan', 'p_month_feb', 'p_month_mar', 'p_month_apr', 'p_month_may', 'p_month_jun',
  'p_month_jul', 'p_month_aug', 'p_month_sep', 'p_month_oct', 'p_month_nov', 'p_month_dec'];
/** 1-based month → localised short name. */
const monthName = (t: TFn, m: number) => t(MONTH_KEYS[Math.min(11, Math.max(0, m - 1))]);

function openExpenseFile(id: string, page: number, t: TFn) {
  docFetch(api.portalExpenseFileUrl(id, page), 'p_exp_err_open', t)
    .then(r => r && r.blob())
    .then(b => { if (!b) return; const u = URL.createObjectURL(b); window.open(u, '_blank'); setTimeout(() => URL.revokeObjectURL(u), 60000); })
    .catch(e => alert(e?.message || t('p_exp_err_open')));
}

const EXP_STATUS: Record<string, { labelKey: string; color: string }> = {
  pending:  { labelKey: 'p_exp_status_pending', color: '#B45309' },
  approved: { labelKey: 'p_exp_status_approved', color: '#15803D' },
  rejected: { labelKey: 'p_exp_status_rejected', color: '#D0021B' },
};

function Expenses() {
  const { t } = useTranslation();
  const now = zonedParts();   // the tenant's zone (utils/region.ts)
  const [year, setYear] = useState(now.y);
  const [month, setMonth] = useState<number>(now.m);
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(1); const [per, setPerRaw] = useState(50); const [total, setTotal] = useState(0);
  const [q, setQRaw] = useState(''); const [status, setStatus] = useState('');
  const [quota, setQuota] = useState<any>(null);
  const [busy, setBusy] = useState(''); const [msg, setMsg] = useState<any>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const setPer = (n: number) => { setPerRaw(n); setPage(1); };
  const setQ = (v: string) => { setQRaw(v); setPage(1); };

  const load = () => api.portalExpenses({ year, month, status: status || undefined, page, per, q })
    .then((r: any) => { setRows(r.rows || []); setTotal(Number(r.total) || 0); }).catch(() => {});
  const loadQuota = () => api.portalExpenseQuota(year, month).then(setQuota).catch(() => {});
  useEffect(() => { load(); loadQuota(); }, [year, month, status, page, per, q]); // eslint-disable-line
  useEffect(() => { setPage(1); }, [year, month, status]);
  useEffect(() => { const pages = Math.max(1, Math.ceil(total / per)); if (page > pages) setPage(pages); }, [total, per, page]);

  const send = async (files: FileList) => {
    if (!files.length) return;
    setBusy('upload'); setMsg(null);
    try {
      const r: any = await api.portalExpenseUpload(Array.from(files), year, month);
      setMsg({ ok: true, ...r });
      load(); loadQuota();
    } catch (e: any) { setMsg({ ok: false, error: e?.message || t('p_exp_upload_failed') }); }
    finally { setBusy(''); }
  };

  const years = Array.from({ length: 5 }, (_, i) => now.y - i);
  // A cap of 0 means the firm lifted the limit for this client: the server sends remaining = null
  // rather than 0, because a remaining of zero is what "you are full" looks like. Treat it as
  // unlimited everywhere the button and the counter read `left`.
  const unlimited = !!quota && Number(quota.cap) === 0;
  const left = quota && !unlimited ? Number(quota.remaining) : null;
  // The filed-month banner keeps one phrase bold in every language, so the translated sentence is
  // split on that phrase rather than being cut into fragments the translator cannot reorder.
  const filedWarn = () => {
    const bold = t('p_exp_filed_warn_bold');
    const parts = t('p_exp_filed_warn', { month: monthName(t, month), year }).split(bold);
    return parts.length > 1 ? <>{parts[0]}<b>{bold}</b>{parts.slice(1).join(bold)}</> : <>{parts[0]}</>;
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 2 }}>{t('p_exp_title')}</div>
        <div style={{ fontSize: 12.5, color: '#7c8894', marginBottom: 12 }}>
          {t('p_exp_subtitle')}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div><label style={lbl}>{t('p_exp_year')}</label>
            <select style={{ ...inp, width: 110 }} value={year} onChange={e => setYear(Number(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select></div>
          <div><label style={lbl}>{t('p_exp_month')}</label>
            <select style={{ ...inp, width: 130 }} value={month} onChange={e => setMonth(Number(e.target.value))}>
              {MONTH_KEYS.map((k, i) => <option key={k} value={i + 1}>{monthName(t, i + 1)}</option>)}
            </select></div>
          <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" style={{ display: 'none' }}
                 onChange={e => { if (e.target.files) send(e.target.files); e.target.value = ''; }} />
          <button style={{ ...btn, opacity: busy || left === 0 ? .5 : 1 }} disabled={!!busy || left === 0}
                  onClick={() => fileRef.current?.click()}>
            {busy === 'upload' ? t('p_exp_reading') : t('p_exp_upload')}
          </button>
          {quota && (
            <div style={{ fontSize: 12.5, color: left === 0 ? '#D0021B' : '#5b6b79', paddingBottom: 9 }}>
              {unlimited
                ? t('p_exp_quota_unlimited', { used: quota.used, month: monthName(t, month), year })
                : left === 0
                  ? t('p_exp_quota_full', { cap: quota.cap, month: monthName(t, month), year, contact: quota.contact })
                  : t('p_exp_quota_left', { used: quota.used, cap: quota.cap, month: monthName(t, month), year, left })}
            </div>
          )}
        </div>

        {quota?.filedMonth && (
          <div style={{ marginTop: 10, background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '9px 12px', fontSize: 12.5 }}>
            {filedWarn()}
          </div>
        )}

        {msg && (
          <div style={{ marginTop: 10, borderRadius: 8, padding: '10px 12px', fontSize: 13,
                        background: msg.ok ? '#EAF6EF' : '#FEE2E2', border: `1px solid ${msg.ok ? '#A7D8BC' : '#FCA5A5'}` }}>
            {msg.ok ? <>
              {t('p_exp_msg_saved', { n: msg.saved })}
              {msg.docs?.some((x: any) => x.appendedPage) && <div style={{ marginTop: 6, color: '#15803D' }}>
                {msg.docs.filter((x: any) => x.appendedPage).map((x: any, i: number) =>
                  <div key={i}>{t('p_exp_msg_appended', { filename: x.filename, page: x.appendedPage, docNo: x.docNo })}</div>)}
              </div>}
              {!!msg.skipped?.length && <div style={{ marginTop: 6, color: '#B45309' }}>
                {t('p_exp_msg_skipped', { n: msg.skipped.length })}
                {msg.skipped.map((s: any, i: number) => <div key={i}>{t('p_exp_msg_skipped_line', { filename: s.filename, docNo: s.docNo ? ` (${s.docNo})` : '', why: s.why })}</div>)}
              </div>}
              {msg.docs?.some((x: any) => x.ocrError) && <div style={{ marginTop: 6, color: '#B45309' }}>
                {t('p_exp_msg_ocr_error')}
              </div>}
            </> : <span style={{ color: '#D0021B' }}>{msg.error}</span>}
          </div>
        )}
        <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 8 }}>
          {t('p_exp_file_hint')}
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{t('p_exp_list_title', { month: monthName(t, month), year })}</div>
          <div style={{ flex: 1 }} />
          <select style={{ ...inp, width: 168 }} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">{t('p_exp_filter_all')}</option>
            <option value="pending">{t('p_exp_filter_pending')}</option>
            <option value="approved">{t('p_exp_filter_approved')}</option>
            <option value="rejected">{t('p_exp_filter_rejected')}</option>
          </select>
          <SearchBox value={q} onChange={setQ} placeholder={t('p_exp_search_ph')} />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 940 }}>
            <thead><tr>
              <th style={th}>{t('p_exp_th_attachment')}</th><th style={th}>{t('p_exp_th_date')}</th><th style={th}>{t('p_exp_th_docno')}</th>
              <th style={th}>{t('p_exp_th_supplier')}</th><th style={{ ...th, textAlign: 'right' }}>{t('p_exp_th_amount')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_exp_th_vat')}</th><th style={th}>{t('p_exp_th_bucket')}</th>
              <th style={th}>{t('p_exp_th_status')}</th><th style={th}></th>
            </tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id}>
                <td style={td}>
                  <button onClick={() => openExpenseFile(r.id, 0, t)} title={r.filename}
                          style={{ ...btnGhost, padding: '4px 9px', fontSize: 12.5, maxWidth: 190, overflow: 'hidden',
                                   textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-flex', gap: 5 }}>
                    {String(r.mime).includes('pdf') ? '📄' : '🖼'} {r.filename || t('p_exp_file_fallback')}
                  </button>
                  {/* Extra photographed pages of the same invoice (owner 2026-09-04) — one chip per page. */}
                  {Number(r.page_count) > 1 && (
                    <span style={{ display: 'inline-flex', gap: 4, marginLeft: 5, verticalAlign: 'middle' }}>
                      {Array.from({ length: Number(r.page_count) - 1 }, (_, i) => (
                        <button key={i} onClick={() => openExpenseFile(r.id, i + 2, t)} title={t('p_exp_page_title', { n: i + 2 })}
                                style={{ ...btnGhost, padding: '2px 7px', fontSize: 11, fontWeight: 700 }}>{t('p_exp_page_chip', { n: i + 2 })}</button>
                      ))}
                    </span>
                  )}
                  {!!r.ocr_error && <div style={{ fontSize: 11, color: '#B45309', marginTop: 2 }}>{t('p_exp_ocr_error_row')}</div>}
                </td>
                <td style={td}>{r.line_date || '—'}</td>
                <td style={{ ...td, fontFamily: 'ui-monospace, monospace' }}>{r.doc_no || '—'}</td>
                <td style={td}>{r.party_name || '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>{money(r.amount)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{money(r.vat)}</td>
                <td style={td}>{r.has_vat ? t('p_exp_with_vat') : t('p_exp_no_vat')}</td>
                <td style={{ ...td, color: EXP_STATUS[r.status]?.color, fontWeight: 700 }}>
                  {EXP_STATUS[r.status] ? t(`p_exp_status_${r.status}`) : r.status}
                  {r.status === 'rejected' && r.reject_reason &&
                    <div style={{ fontWeight: 400, fontSize: 11.5, color: '#5b6b79', marginTop: 2 }}>{r.reject_reason}</div>}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <RowMenu actions={[
                    { label: t('p_exp_act_view'), onClick: () => openExpenseFile(r.id, 0, t) },
                    // The scan is kept, so the figures can always be produced from it again.
                    { label: t('p_exp_act_rescan'), hidden: r.status === 'approved',
                      title: t('p_exp_act_rescan_title'),
                      onClick: async () => { try { await api.portalExpenseRescan(r.id); load(); } catch (e: any) { alert(e?.message); } } },
                    { label: t('p_exp_act_remove'), danger: true, hidden: r.status === 'approved',
                      onClick: async () => { if (!window.confirm(t('p_exp_confirm_remove', { filename: r.filename }))) return;
                        try { await api.portalExpenseDelete(r.id); load(); loadQuota(); } catch (e: any) { alert(e?.message); } } },
                  ]} />
                </td>
              </tr>))}
              {!rows.length && <tr><td style={{ ...td, color: '#98a4b0' }} colSpan={9}>
                {q ? t('p_empty_no_match', { query: q }) : t('p_exp_empty', { month: monthName(t, month), year })}
              </td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} per={per} total={total} onPage={setPage} onPer={setPer} />
      </div>
    </div>
  );
}

// ── Bank statements (owner 2026-10-13) ───────────────────────────────────────
// The merchant sends in statements of THEIR OWN bank accounts — the bank's PDF (password-protected
// ones included) or a CSV export. The server reads them into the company's own statement lines for
// the firm to reconcile by hand; nothing on this page touches an invoice, a receipt or the firm's
// bank module, and a statement can be withdrawn until one of its lines has been matched. The PDF
// password rides with the upload, is used once to open the file and is never stored — the field
// says so, because that is the first thing a client asks before sending a bank document.
const BS_MAX_FILES = 5;   // the server's multer cap for 'files' on POST /portal/bank-statements
const BS_MAX_MB = 15;     // per file, same source — quoted in the hint so a refusal is never a surprise
// A failed file comes back as { error: <code>, message: <sentence> } — the same codes the staff pane
// keys on. The two refusals a client can act on are worded here in the reader's language; any other
// code falls back to the server's sentence, never to the bare code.
const bsError = (f: any, t: TFn): string => {
  const code = String(f?.error || '');
  if (code === 'account_not_registered') return t('p_bs_err_account_not_registered');
  if (code === 'statement_account_mismatch') return t('p_bs_err_statement_account_mismatch');
  return String(f?.message || '') || code || t('p_bs_upload_failed');
};
// Same shape as openExpenseFile: the endpoint needs the Bearer token, so never a bare href.
function openStatementFile(id: string, t: TFn) {
  docFetch(api.portalBankStatementFileUrl(id), 'p_bs_err_open', t)
    .then(r => r && r.blob())
    .then(b => { if (!b) return; const u = URL.createObjectURL(b); window.open(u, '_blank'); setTimeout(() => URL.revokeObjectURL(u), 60000); })
    .catch(e => alert(e?.message || t('p_bs_err_open')));
}

function BankStatementsPage({ banks }: { banks: any[] }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(1); const [per, setPerRaw] = useState(50); const [total, setTotal] = useState(0);
  const [accountId, setAccountId] = useState('');     // '' = let the server detect the account from the file
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState<any>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const setPer = (n: number) => { setPerRaw(n); setPage(1); };

  const load = () => api.portalBankStatements(page, per)
    .then((r: any) => { setRows(r.rows || []); setTotal(Number(r.total) || 0); })
    .catch((e: any) => setMsg({ ok: false, error: e?.message || t('p_bs_upload_failed') }));
  useEffect(() => { load(); }, [page, per]); // eslint-disable-line
  useEffect(() => { const pages = Math.max(1, Math.ceil(total / per)); if (page > pages) setPage(pages); }, [total, per, page]);

  const bankOf = (id: string) => banks.find(b => b.id === id);

  const send = async (files: FileList) => {
    if (!files.length) return;
    // Refuse here rather than let multer drop the sixth file silently: the client would never learn
    // which statement did not arrive.
    if (files.length > BS_MAX_FILES) { setMsg({ ok: false, error: t('p_bs_too_many', { max: BS_MAX_FILES }) }); return; }
    setBusy(true); setMsg(null);
    try {
      const r: any = await api.portalBankStatementUpload(Array.from(files), accountId, password);
      setMsg({ ok: true, saved: r.saved || [], failed: r.failed || [] });
      // New rows sort first, so show page 1. Changing the page reloads through the effect; calling
      // load() as well from page 2 would race a stale page-2 answer against it.
      if (page === 1) load(); else setPage(1);
    } catch (e: any) { setMsg({ ok: false, error: bsError({ error: e?.message, message: e?.payload?.message }, t) }); }
    finally {
      // One batch, one password. It has done its job the moment the request returns; keeping it in
      // state would only leave it on screen for the next upload to send by mistake.
      setPassword(''); setBusy(false);
    }
  };

  const withdraw = async (r: any) => {
    if (!window.confirm(t('p_bs_confirm_withdraw', { filename: r.filename || t('p_bs_file_fallback') }))) return;
    try { await api.portalBankStatementDelete(r.id); load(); }
    // 409 = the firm has already matched a line of this statement; the server refuses rather than
    // unpick the firm's work, and the client is told why instead of seeing a bare error.
    catch (e: any) { alert(e?.status === 409 ? t('p_bs_withdraw_locked') : (e?.message || t('p_bs_upload_failed'))); }
  };

  const period = (r: any) => (r.period_from || r.period_to)
    ? `${r.period_from ? fmtDate(r.period_from) : '…'} – ${r.period_to ? fmtDate(r.period_to) : '…'}` : '—';
  const sourcePill: React.CSSProperties = { fontSize: 10.5, fontWeight: 800, color: '#5b6b79', border: '1px solid #d3dbe2', borderRadius: 4, padding: '0 4px', lineHeight: '16px' };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 2 }}>{t('p_bs_title')}</div>
        <div style={{ fontSize: 12.5, color: '#7c8894', marginBottom: 12 }}>{t('p_bs_subtitle')}</div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div><label style={lbl}>{t('p_bs_account')}</label>
            <select style={{ ...inp, width: 280 }} value={accountId} onChange={e => setAccountId(e.target.value)}>
              <option value="">{t('p_bs_account_auto')}</option>
              {banks.map((b: any) => (
                <option key={b.id} value={b.id}>
                  {b.account_number}{b.account_name ? ` · ${b.account_name}` : ''}{b.bank_address ? ` · ${b.bank_address}` : ''}
                </option>
              ))}
            </select></div>
          {/* The hint lives IN the label so the field stays aligned with its neighbours; it is the
              one sentence every client reads before typing a bank password into a web page. */}
          <div><label style={lbl}>{t('p_bs_pw_label')} <span style={{ fontWeight: 400, color: '#98a4b0' }}>· {t('p_bs_pw_hint')}</span></label>
            <input style={{ ...inp, width: 250 }} type="password" autoComplete="off" value={password}
                   placeholder={t('p_bs_pw_ph')} onChange={e => setPassword(e.target.value)} /></div>
          <input ref={fileRef} type="file" multiple accept=".pdf,.csv,application/pdf,text/csv" style={{ display: 'none' }}
                 onChange={e => { if (e.target.files) send(e.target.files); e.target.value = ''; }} />
          <button style={{ ...btn, opacity: busy ? .5 : 1 }} disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? t('p_bs_uploading') : t('p_bs_upload')}
          </button>
        </div>
        {!banks.length && <div style={{ fontSize: 12.5, color: '#B45309', marginTop: 8 }}>{t('p_bs_no_accounts')}</div>}

        {msg && (
          <div style={{ marginTop: 10, borderRadius: 8, padding: '10px 12px', fontSize: 13,
                        background: msg.ok ? '#EAF6EF' : '#FEE2E2', border: `1px solid ${msg.ok ? '#A7D8BC' : '#FCA5A5'}` }}>
            {msg.ok ? <>
              {msg.saved.map((s: any, i: number) => (
                <div key={`s${i}`} style={{ color: '#15803D' }}>
                  {t('p_bs_res_saved', { filename: s.filename, account: s.accountNumber || '—', rows: Number(s.rows) || 0, added: Number(s.added) || 0, skipped: Number(s.skipped) || 0 })}
                </div>
              ))}
              {msg.failed.map((f: any, i: number) => (
                <div key={`f${i}`} style={{ color: '#D0021B', marginTop: msg.saved.length || i ? 4 : 0 }}>
                  {t('p_bs_res_failed', { filename: f.filename, error: bsError(f, t) })}
                </div>
              ))}
              {!msg.saved.length && !msg.failed.length && <span style={{ color: '#5b6b79' }}>{t('p_bs_empty')}</span>}
            </> : <span style={{ color: '#D0021B' }}>{msg.error}</span>}
          </div>
        )}
        <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 8 }}>{t('p_bs_file_hint', { max: BS_MAX_FILES, mb: BS_MAX_MB })}</div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>{t('p_bs_list_title')}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead><tr>
              <th style={th}>{t('p_bs_th_file')}</th><th style={th}>{t('p_bs_th_account')}</th><th style={th}>{t('p_bs_th_period')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_bs_th_rows')}</th><th style={th}>{t('p_bs_th_uploaded')}</th><th style={th}></th>
            </tr></thead>
            <tbody>{rows.map(r => { const b = bankOf(r.bank_account_id); return (
              <tr key={r.id}>
                <td style={td}>
                  <button onClick={() => openStatementFile(r.id, t)} title={r.filename}
                          style={{ ...btnGhost, padding: '4px 9px', fontSize: 12.5, maxWidth: 260, overflow: 'hidden',
                                   textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={sourcePill}>{String(r.source || 'pdf').toUpperCase()}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.filename || t('p_bs_file_fallback')}</span>
                  </button>
                </td>
                <td style={td}>
                  <b>{r.account_number || b?.account_number || '—'}</b>
                  {b?.account_name && <div style={{ fontSize: 11.5, color: '#5b6b79' }}>{b.account_name}</div>}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {period(r)}
                  {r.closing_balance != null && r.closing_balance !== '' &&
                    <div style={{ fontSize: 11.5, color: '#5b6b79' }}>{t('p_bs_closing', { amount: money(r.closing_balance, 2, mco()) })}</div>}
                </td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{t('p_bs_rows_cell', { added: Number(r.added_count) || 0, rows: Number(r.row_count) || 0 })}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {fmtDateTime(r.created_at) || '—'}
                  {/* Who sent it, not who by name: a staff member's name is the firm's business, as on the Expenses list. */}
                  <div style={{ fontSize: 11.5, color: '#5b6b79' }}>{r.uploaded_via === 'portal' ? t('p_bs_via_portal') : t('p_bs_via_workspace')}</div>
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <RowMenu actions={[
                    { label: t('p_bs_act_view'), onClick: () => openStatementFile(r.id, t) },
                    { label: t('p_bs_act_withdraw'), danger: true, onClick: () => withdraw(r) },
                  ]} />
                </td>
              </tr>); })}
              {!rows.length && <tr><td style={{ ...td, color: '#98a4b0' }} colSpan={6}>{t('p_bs_empty')}</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} per={per} total={total} onPage={setPage} onPer={setPer} />
      </div>
    </div>
  );
}

function TaxFilings() {
  const { t } = useTranslation();
  const monthCols = monthAbbrs(t);
  const [year, setYear] = useState<number>(zonedParts().y);
  const [years, setYears] = useState<number[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => { api.portalHubFilingYears().then((d: any) => setYears(d.years || [])).catch(() => {}); }, []);
  useEffect(() => {
    setBusy(true);
    api.portalHubFilings(year).then((d: any) => setRows(d.rows || [])).catch(() => setRows([])).finally(() => setBusy(false));
  }, [year]);

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_fil_title')}</div>
        <div style={{ flex: 1 }} />
        <select style={{ ...inp, width: 'auto', padding: '7px 10px' }} value={year} onChange={e => setYear(Number(e.target.value))}>
          {(years.length ? years : [year]).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div style={{ fontSize: 12.5, color: '#7c8894', marginBottom: 14 }}>
        {t('p_fil_subtitle')}
      </div>
      {busy ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_fil_loading')}</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead><tr>
              <th style={{ ...th, position: 'sticky', left: 0, background: '#fff' }}>{t('p_fil_th_filing')}</th>
              {monthCols.map(m => <th key={m} style={{ ...th, textAlign: 'center' }}>{m}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.key}>
                  <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#fff' }}>{r.label}</td>
                  {r.months.map((m: any) => (
                    // PND.51 is filed once for the YEAR, so its row spans all twelve columns rather
                    // than sitting in a month it was never assigned to (owner 2026-08-24).
                    <td key={m.month} colSpan={r.annual ? monthCols.length : 1} style={{ ...td, textAlign: 'center', verticalAlign: 'top', background: m.filed && m.docs.length ? '#E8F6EE' : undefined }}>
                      {r.annual && <div style={{ fontSize: 11, fontWeight: 700, color: '#7c8894', marginBottom: 3 }}>{m.name}</div>}
                      {/* Word chips, not icons: a client has to know WHICH document they are opening,
                          and emoji render differently (or not at all) across the devices they use. */}
                      {m.docs.length ? m.docs.map((d: any) => (
                        <button key={d.id} title={d.filename}
                          onClick={() => openAuthed(api.portalFilingDocUrl(d.id), d.filename, t)}
                          style={{ display: 'block', margin: '0 auto 3px', padding: '2px 8px', border: '1px solid #cfe3dc', borderRadius: 999, background: '#eef6f3', color: ACCENT, fontWeight: 700, fontSize: 11.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {d.slot === 'form' ? t('p_fil_chip_form') : d.slot === 'attached' ? t('p_fil_chip_attachment') : t('p_fil_chip_receipt')}{d.filing > 0 ? ` ${t('p_fil_chip_additional', { n: d.filing })}` : ''}
                        </button>
                      )) : <span style={{ color: m.filed ? ACCENT : '#d3dbe2', fontSize: 13 }}>{m.filed ? '✓' : '·'}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontSize: 12, color: '#98a4b0', marginTop: 12 }}>
        {t('p_fil_legend')}
      </div>
    </div>
  );
}
/** The 12 column headers of the filings grid, localised. */
const monthAbbrs = (t: TFn) => MONTH_KEYS.map(k => t(k));

function BillsFromSlf() {
  const { t } = useTranslation();
  const [data, setData] = useState<any>({ quotations: [], invoices: [], receipts: [] });
  const [kind, setKind] = useState<'invoices' | 'quotations' | 'receipts'>('invoices');
  const [busy, setBusy] = useState(true);
  useEffect(() => { api.portalHubBills().then((d: any) => setData(d || {})).catch(() => {}).finally(() => setBusy(false)); }, []);
  const rows: any[] = data[kind] || [];
  const KINDS: { k: typeof kind; label: string }[] = [
    { k: 'invoices', label: t('p_bills_tab_invoices') }, { k: 'receipts', label: t('p_bills_tab_receipts') }, { k: 'quotations', label: t('p_bills_tab_quotations') },
  ];
  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#27303B' }}>{t('p_bills_title')}</div>
      <div style={{ fontSize: 12.5, color: '#7c8894', margin: '4px 0 14px' }}>
        {t('p_bills_subtitle')}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {KINDS.map(tab => (
          <button key={tab.k} onClick={() => setKind(tab.k)}
            style={{ ...btnGhost, padding: '6px 14px', background: kind === tab.k ? ACCENT : '#eef2f5', color: kind === tab.k ? '#fff' : '#333' }}>
            {tab.label} ({(data[tab.k] || []).length})
          </button>
        ))}
      </div>
      {busy ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_bills_loading')}</div>
        : !rows.length ? <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_doc_empty')}</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>{t('p_bills_th_no')}</th><th style={th}>{t('p_bills_th_date')}</th>
              <th style={th}>{kind === 'receipts' ? t('p_bills_th_paid_on') : kind === 'quotations' ? t('p_bills_th_valid_until') : t('p_bills_th_due')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_bills_th_total', currencyArgs(currencyCode()))}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('p_bills_th_net')}</th>
              <th style={th}>{t('p_bills_th_status')}</th><th style={th}>{t('p_bills_th_issued_by')}</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td, fontWeight: 700 }}>{r.doc_no}</td>
                  <td style={td}>{r.issue_date || '—'}</td>
                  <td style={td}>{(kind === 'receipts' ? r.payment_date : r.due_date) || '—'}</td>
                  {/* A bill from the firm prints in the FIRM's region (#58), not the merchant's. */}
                  <td style={{ ...td, textAlign: 'right' }}>{money(r.total, 2, r.companyId)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(r.net, 2, r.companyId)}</td>
                  <td style={td}>{r.status || '—'}</td>
                  <td style={{ ...td, color: '#5b6b79' }}>{r.firm}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <a href={api.portalBillPdfUrl(r.token)} target="_blank" rel="noreferrer"
                      style={{ ...btnGhost, padding: '5px 12px', textDecoration: 'none', display: 'inline-block' }}>{t('p_bills_open_pdf')}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
